// frontend/src/WriterStudio.js – NEXTSCENES Writer Studio
// Server-authoritative scenes + proposal governance
// Preserves: Replies + Light Analytics + Share + Intent + Decision Log + Voting UX + Comment Highlight
//
// Contracts used (NO backend changes):
// - GET   /api/stories
// - POST  /api/stories/:id/scenes
// - PATCH /api/stories/:id/proposals   (open/close/approve/rejectOne/rejectAll)
//
// Reader Feedback Sync (Cross-device):
// - GET  /api/feedback/live?storyId=...&target=storyline&source=writerStudio&limit=...
// - POST /api/feedback  { storyId, storyTitle, forum, text, target, source, parentId, author }
//
// Build: 2026-06-22-ai-review-and-full-scene-proposal-v4
//
// Fixes (v2):
// - Hard syntax break fixed: ModeBadge import line was glued into a const declaration.
// - Writing Mode filter now actually filters (keeps your UI labels exactly; no backend expectations).
// - No backend changes. No feature removals.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { useUser } from "./UserContext";
import { api } from "./api";
import StoryCharactersPanel from "./components/StoryCharactersPanel";
import { loadStoredStories, saveStoredStories } from "./storyStorage";
import ProjectBreadcrumbTrail from "./components/ProjectBreadcrumbTrail";
import NextScenesBookBlueprintNavigator from "./components/NextScenesBookBlueprintNavigator";
import GeneralDiscussionsV1 from "./components/GeneralDiscussionsV1";
import {
  NovelStorySceneRichEditor,
  NovelStorySceneReadOnly,
} from "./components/NovelStorySceneManuscript";

import { generateText } from "./utils/simpleTextGenerator";
import { buildNovelDraftSequence } from "./utils/novelStoryDraftAssembly";
import { displayedNovelSceneNumberV1 } from "./utils/displayedNovelSceneNumberV1";

import { TEXT } from "./i18n";
import { getPublicationInfo as getKnownPublicationInfo } from "./utils/publicationStatus";




function wsLowerFirst(text) {
  const clean = String(text || "").trim();
  if (!clean) return "";
  return clean.charAt(0).toLowerCase() + clean.slice(1);
}

function wsStripProposalOpening(text, isFR) {
  let clean = String(text || "").trim();
  if (!clean) return "";

  if (isFR) {
    clean = clean
      .replace(/^voici\s+une\s+prochaine\s+sc[eè]ne\s+possible\s*(où|dans laquelle|:|-|,)?\s*/i, "")
      .replace(/^voici\s+une\s+sc[eè]ne\s+possible\s*(où|dans laquelle|:|-|,)?\s*/i, "");
  } else {
    clean = clean
      .replace(/^here\s+is\s+one\s+possible\s+next\s+scene\s*(where|in which|:|-|,)?\s*/i, "")
      .replace(/^here\s+is\s+a\s+possible\s+next\s+scene\s*(where|in which|:|-|,)?\s*/i, "")
      .replace(/^here\s+is\s+a\s+possible\s+scene\s*(where|in which|:|-|,)?\s*/i, "");
  }

  return clean.trim();
}

function wsStripDirectiveLanguage(text, isFR) {
  let clean = String(text || "").trim();
  if (!clean) return "";

  if (isFR) {
    clean = clean
      .replace(/\b(vous|tu)\s+(devez|dois|devriez|pourriez|pouvez)\b\s*/gi, "")
      .replace(/\bl[’']auteur\s+(doit|devrait|peut|pourrait)\b\s*/gi, "")
      .replace(/^\s*(écrire|rédiger)\s+(une?|la)?\s*(sc[eè]ne|partie|suite)?\s*(où|dans laquelle|qui|que)?\s*/i, "")
      .replace(/^\s*(il\s+faut|montrez|montrer|faire|faites|assurez-vous|considérez|essayez\s+de)\b\s*(que|une?|la|le|les|des)?\s*/i, "");
  } else {
    clean = clean
      .replace(/\b(you|the\s+writer|the\s+author|writer|author)\s+(should|must|need\s+to|needs\s+to|could|can|may)\b\s*/gi, "")
      .replace(/^\s*(write|draft|create)\s+(a|an|the)?\s*(scene|moment|chapter|section)?\s*(where|in which|that)?\s*/i, "")
      .replace(/^\s*(show|make\s+sure|consider|try\s+to|have)\b\s*(that|a|an|the)?\s*/i, "");
  }

  return clean.replace(/^\s*[:;,.\-–—]+\s*/, "").replace(/\s{2,}/g, " ").trim();
}

function wsEnsureProposalOpening(text, isFR) {
  const clean = String(text || "").trim();
  if (!clean) return "";
  const lower = clean.toLowerCase();
  const alreadyProposal = isFR
    ? lower.startsWith("voici une prochaine scène possible") || lower.startsWith("voici une scène possible")
    : lower.startsWith("here is a possible next scene") || lower.startsWith("here is one possible next scene") || lower.startsWith("here is a possible scene");

  if (alreadyProposal) return clean;

  const core = wsStripDirectiveLanguage(clean, isFR) || clean;
  return isFR
    ? "Voici une prochaine scène possible: " + core
    : "Here is a possible next scene: " + core;
}

function wsEnsureWhatHappensText(text, fallbackProposal, isFR) {
  let clean = String(text || "").trim() || wsStripProposalOpening(fallbackProposal, isFR);
  if (!clean) return "";

  clean = wsStripProposalOpening(clean, isFR);
  clean = wsStripDirectiveLanguage(clean, isFR) || clean;
  clean = wsStripProposalOpening(clean, isFR).replace(/^\s*[:;,.\-–—]+\s*/, "").trim();

  if (!clean) return "";
  if (isFR) {
    if (/^(dans\s+cette\s+sc[eè]ne|la\s+sc[eè]ne|ugo\b)/i.test(clean)) return clean;
    return "Dans cette scène, " + wsLowerFirst(clean);
  }

  if (/^(in\s+this\s+scene|the\s+scene|ugo\b)/i.test(clean)) return clean;
  return "In this scene, " + wsLowerFirst(clean);
}

function normalizeWriterStudioAiProposal(proposal, index, lang) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const p = proposal && typeof proposal === "object" ? proposal : {};

  const firstClean = (...values) => {
    for (const value of values) {
      if (Array.isArray(value)) {
        const joined = value
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .join(", ");
        if (joined) return joined;
      }

      const clean = String(value || "").trim();
      if (clean) return clean;
    }

    return "";
  };

  const sceneTitle = firstClean(
    p.sceneTitle,
    p.title,
    p.name,
    p.heading
  );

  const shortSceneProposal = firstClean(
    p.shortSceneProposal,
    p.shortProposal,
    p.proposal,
    p.summary,
    p.description
  );

  const whatHappens = firstClean(
    p.whatHappens,
    p.mainEvent,
    p.event,
    p.action,
    p.sceneAction
  );

  const sceneDraft = firstClean(
    p.sceneDraft,
    p.proposedSceneText,
    p.sceneText,
    p.draftScene,
    p.fullScene,
    p.actualScene
  );

  const charactersInvolvedText = firstClean(
    p.charactersInvolved,
    p.characters,
    p.cast,
    p.characterList
  );

  const charactersInvolvedList = Array.isArray(p.charactersInvolved)
    ? p.charactersInvolved.map((item) => String(item || "").trim()).filter(Boolean)
    : charactersInvolvedText
    ? charactersInvolvedText.split(",").map((item) => item.trim()).filter(Boolean)
    : [];

  const emotionalDirection = firstClean(
    p.emotionalDirection,
    p.emotion,
    p.tone,
    p.mood
  );

  const continuityNotes = firstClean(
    p.continuityNotes,
    p.continuity,
    p.canonNotes
  );

  const valueOrLesson = firstClean(
    p.valueOrLesson,
    p.lesson,
    p.moral,
    p.value
  );

  const possibleRiskToAvoid = firstClean(
    p.possibleRiskToAvoid,
    p.riskWarning,
    p.riskToAvoid,
    p.risk,
    p.warning
  );

  const fallbackTitlesEN = [
    "The Bridge of Leaves",
    "The Flower That Would Not Open",
    "The Seed Asks for a Song",
    "A Gentle Next Step",
    "The Kindness That Returned"
  ];

  const fallbackTitlesFR = [
    "Le pont de feuilles",
    "La fleur qui refusait de s’ouvrir",
    "La graine demande une chanson",
    "Un doux pas suivant",
    "La bonté qui revient"
  ];

  const fallbackTitle = isFR
    ? fallbackTitlesFR[index] || "Scène possible " + (index + 1)
    : fallbackTitlesEN[index] || "Possible Scene " + (index + 1);

  const safeShortSceneProposal = wsEnsureProposalOpening(shortSceneProposal, isFR);
  const safeWhatHappens = wsEnsureWhatHappensText(whatHappens, safeShortSceneProposal, isFR);

  return {
    ...p,
    sceneTitle: sceneTitle || fallbackTitle,
    title: sceneTitle || fallbackTitle,
    shortSceneProposal: safeShortSceneProposal,
    proposal: safeShortSceneProposal,
    summary: safeShortSceneProposal,
    sceneDraft,
    proposedSceneText: sceneDraft,
    sceneText: sceneDraft,
    draftScene: sceneDraft,
    fullScene: sceneDraft,
    whatHappens: safeWhatHappens,
    mainEvent: safeWhatHappens,
    charactersInvolved: charactersInvolvedList,
    characters: charactersInvolvedText,
    emotionalDirection,
    continuityNotes,
    valueOrLesson,
    possibleRiskToAvoid,
    riskWarning: possibleRiskToAvoid
  };
}

function normalizeWriterStudioAiProposalList(proposals, lang) {
  return Array.isArray(proposals)
    ? proposals.map((proposal, index) => normalizeWriterStudioAiProposal(proposal, index, lang))
    : [];
}


// NOTE: ModeBadge is not used in this file right now.
// Keeping it out avoids build warnings / potential lint failures.
// If you later want to show the ModeBadge component in the UI, re-add this import.
// import ModeBadge from "./components/ModeBadge";

const COMMENTS_PREFIX = "ns.comments.";
const INTENT_PREFIX = "ns.proposalIntent.";
const DECISION_LOG_PREFIX = "ns.decisionLog.";

// Feedback server sync controls
const FEEDBACK_SOURCE = "writerStudio";
const FEEDBACK_TARGET = "storyline";
const FEEDBACK_LIMIT = 100;
const FEEDBACK_POLL_MS = 8000;
const PROPOSAL_VOTE_POLL_MS = 8000;

const MIN_PROPOSAL_WORDS = 200;
const RECOMMENDED_MAX_PROPOSAL_WORDS = 500;
const MAX_PROPOSAL_WORDS = 550;

// WriterStudio UI filter options (display strings)
const WRITING_MODES = [
  "Solo Writing",
  "Collaborative Writing",
  "Group Writing (Open)",
  "Group Writing (Closed)",
];

const STORY_HISTORY_PAGE_SIZE = 40;
const STORY_HISTORY_MAX_RENDER = 400;

const NEXTSCENES_FOUNDING_WRITERS_LINKS = {
  en: "https://www.nextscenes.org/founding-writers-pilot",
  fr: "https://www.nextscenes.org/fr/pilote-auteurs-fondateurs",
};

function getNextScenesFoundingWritersLink(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr")
    ? NEXTSCENES_FOUNDING_WRITERS_LINKS.fr
    : NEXTSCENES_FOUNDING_WRITERS_LINKS.en;
}

function FoundingWritersPilotCard({ lang, title, body, linkLabel }) {
  return (
    <div className="ns-founding-pilot-card" role="note">
      <div className="ns-founding-pilot-kicker">{title}</div>
      <p className="ns-founding-pilot-body">{body}</p>
      <a
        className="ns-founding-pilot-link"
        href={getNextScenesFoundingWritersLink(lang)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkLabel} →
      </a>
    </div>
  );
}

const NEXTSCENES_POLICY_LINKS = {
  en: [
    { label: "Writer Rights", href: "https://www.nextscenes.org/writer-rights" },
    { label: "Contributor Policy", href: "https://www.nextscenes.org/contributor-policy" },
    { label: "Publication and Benefit-Sharing", href: "https://www.nextscenes.org/publication-benefit-sharing" },
    { label: "Plain-Language Terms", href: "https://www.nextscenes.org/plain-language-terms" },
  ],
  fr: [
    { label: "Droits des auteurs", href: "https://www.nextscenes.org/fr/droits-des-auteurs" },
    { label: "Politique des contributeurs", href: "https://www.nextscenes.org/fr/politique-contributeurs" },
    { label: "Publication et partage des bénéfices", href: "https://www.nextscenes.org/fr/publication-partage-benefices" },
    { label: "Conditions simples", href: "https://www.nextscenes.org/fr/conditions-simples" },
  ],
};

function getNextScenesPolicyLinks(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr")
    ? NEXTSCENES_POLICY_LINKS.fr
    : NEXTSCENES_POLICY_LINKS.en;
}

function PolicyLinksCard({ lang, title, body, compact = false }) {
  const links = getNextScenesPolicyLinks(lang);

  return (
    <div className={compact ? "ns-policy-links-card ns-policy-links-card-compact" : "ns-policy-links-card"} role="note">
      <div className="ns-policy-links-title">{title}</div>
      {body ? <p className="ns-policy-links-body">{body}</p> : null}
      <div className="ns-policy-links-row">
        {links.map((item) => (
          <a
            key={item.href}
            className="ns-policy-link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
}


const NEXTSCENES_AI_PRINCIPLES_LINKS = {
  en: "https://www.nextscenes.org/ai-principles",
  fr: "https://www.nextscenes.org/fr/principes-ia",
};

function getNextScenesAiPrinciplesLink(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr")
    ? NEXTSCENES_AI_PRINCIPLES_LINKS.fr
    : NEXTSCENES_AI_PRINCIPLES_LINKS.en;
}

function pickAiProposalPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  return payload.result || payload.data || payload;
}

function aiProposalText(value, fallback = "") {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value)) return value.filter(Boolean).map((x) => String(x).trim()).filter(Boolean).join(", ");
  return fallback;
}

function normalizeAiProposal(item, index, isFR) {
  const fallbackTitle = isFR ? `Scène possible ${index + 1}` : `Possible Scene ${index + 1}`;
  return {
    sceneTitle: aiProposalText(item?.sceneTitle || item?.title, fallbackTitle),
    shortSceneProposal: aiProposalText(
      item?.shortSceneProposal || item?.proposal || item?.summary,
      isFR
        ? "Voici une prochaine scène possible que l’auteur peut accepter, modifier, rejeter ou utiliser comme inspiration."
        : "Here is a possible next scene the writer may accept, edit, reject, or use as inspiration."
    ),
    whatHappens: aiProposalText(item?.whatHappens || item?.mainEvent || item?.action),
    charactersInvolved: aiProposalText(item?.charactersInvolved || item?.characters),
    emotionalDirection: aiProposalText(item?.emotionalDirection || item?.emotion || item?.tone),
    continuityNotes: aiProposalText(item?.continuityNotes || item?.continuity),
    valueOrLesson: aiProposalText(item?.valueOrLesson || item?.lesson || item?.moral),
    possibleRiskToAvoid: aiProposalText(item?.possibleRiskToAvoid || item?.risk || item?.riskToAvoid),
  };
}

function getAiSceneProposals(payload, isFR) {
  const body = pickAiProposalPayload(payload);
  const raw = Array.isArray(body?.proposals)
    ? body.proposals
    : Array.isArray(body?.sceneProposals)
    ? body.sceneProposals
    : Array.isArray(body)
    ? body
    : [];

  return raw
    .map((item, index) => normalizeAiProposal(item, index, isFR))
    .filter((item) => item.sceneTitle || item.shortSceneProposal || item.whatHappens);
}

function AiProposalField({ label, value }) {
  const clean = aiProposalText(value);
  if (!clean) return null;
  return (
    <div className="ws-ai-proposal-field">
      <strong>{label}</strong>
      <p>{clean}</p>
    </div>
  );
}

function pickAiReviewPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  return payload.review || payload.result?.review || payload.data?.review || payload.result || payload.data || payload;
}

function aiReviewText(value, fallback = "") {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join(" ");
    return joined || fallback;
  }
  return fallback;
}

function aiReviewList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, 5);
  }

  const clean = String(value || "").trim();
  return clean ? [clean] : [];
}

function normalizeAiReviewResult(payload, isFR) {
  const review = pickAiReviewPayload(payload) || {};
  const fallbackOverall = isFR
    ? "Relecture consultative reçue. Aucun texte n’a été modifié automatiquement."
    : "Advisory review received. No text has been changed automatically.";

  return {
    overallImpression: aiReviewText(
      review.overallImpression || review.summary || review.overall || review.message,
      fallbackOverall
    ),
    clarity: aiReviewList(review.clarity || review.clarityNotes || review.flow),
    continuity: aiReviewList(review.continuity || review.continuityNotes || review.canonNotes),
    grammarAndStyle: aiReviewList(
      review.grammarAndStyle ||
        review.grammar ||
        review.spellingGrammarSentenceStructure ||
        review.sentenceStructure ||
        review.style
    ),
    platformStandards: aiReviewList(
      review.platformStandards ||
        review.audienceFit ||
        review.nextScenesValues ||
        review.values ||
        review.standards ||
        review.safety
    ),
    suggestedManualImprovements: aiReviewList(
      review.suggestedManualImprovements ||
        review.suggestedNextSteps ||
        review.suggestions ||
        review.improvements
    ),
    note: aiReviewText(review.note || review.notice || payload?.notice || ""),
  };
}

function AiReviewSection({ label, items, fallback }) {
  const list = aiReviewList(items);
  const cleanFallback = String(fallback || "").trim();

  if (!list.length && !cleanFallback) return null;

  return (
    <section className="ws-ai-result-section">
      <strong>{label}</strong>
      {list.length ? (
        <ul>
          {list.map((item, index) => (
            <li key={`${label}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="ws-ai-result-summary">{cleanFallback}</p>
      )}
    </section>
  );
}

function WriterStudioAiAssistancePlaceholder({
  lang,
  activeStory,
  sceneTitle = "",
  sceneText = "",
  reviewContext = "canon",
  disabled = false,
  disabledReason = "",
  compact = false,
  onBackToScene = null,
}) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const storyId = String(activeStory?._id || activeStory?.id || "").trim();
  const cleanSceneText = String(sceneText || "").trim();
  const cleanSceneTitle = String(sceneTitle || "").trim();
  const canReview = Boolean(storyId && cleanSceneText && !disabled && !busy);
  const review = result ? normalizeAiReviewResult(result, isFR) : null;
  const reviewType = reviewContext === "proposal" ? "proposal_review" : "scene_review";

  async function handleReviewScene() {
    if (!storyId) {
      setError(isFR ? "Choisissez d’abord une storyline." : "Choose a storyline first.");
      return;
    }

    if (!cleanSceneText) {
      setError(isFR ? "Rédigez d’abord une scène à relire." : "Draft a scene first before review.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const payload = {
        storyId,
        draftTitle: cleanSceneTitle,
        draftText: cleanSceneText,
        language: isFR ? "fr" : "en",
        reviewType,
        advisoryOnly: true,
        source: "writerStudio",
      };

      const data = await api.post("/api/ai/review-scene", payload);
      setResult(data || {});
    } catch (err) {
      const status = err?.status ? `${err.status} ` : "";
      const msg = err?.message || (isFR ? "La relecture IA a échoué." : "AI review failed.");
      setError(`${status}${msg}`.trim());
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={compact ? "ws-ai-assistance-card ws-ai-assistance-card-compact" : "ws-ai-assistance-card"} role="note">
      <div className="ws-ai-assistance-head">
        <div>
          <div className="ws-ai-kicker">
            {isFR ? "Relecture IA" : "AI Scene Review"}
          </div>
          <h4>
            {compact
              ? (isFR ? "Relecture facultative" : "Optional Review")
              : (isFR ? "Relire cette scène" : "Review this scene")}
          </h4>
        </div>
        {!compact ? (
          <span className="ws-ai-pill ws-ai-pill-live">
            {isFR ? "Consultatif" : "Advisory"}
          </span>
        ) : null}
      </div>

      <p className="ws-ai-short-line">
        {compact
          ? (
              isFR
                ? "Facultatif. La relecture ne démarre que lorsque l’auteur la demande et ne modifie jamais la scène automatiquement."
                : "Optional. Review runs only when the author requests it and never changes the Scene automatically."
            )
          : (
              isFR
                ? "L’IA vérifie l’orthographe, la grammaire, la structure des phrases, la clarté, la continuité, le public visé et les valeurs NextScenes. Elle ne modifie rien automatiquement."
                : "AI checks spelling, grammar, sentence structure, clarity, continuity, audience fit, and NextScenes values. It changes nothing automatically."
            )}
      </p>

      {!compact ? (
        <div className="ws-ai-points" aria-label={isFR ? "Domaines de relecture IA" : "AI review areas"}>
          <span>{isFR ? "Orthographe/grammaire" : "Spelling/grammar"}</span>
          <span>{isFR ? "Structure" : "Sentence structure"}</span>
          <span>{isFR ? "Continuité" : "Continuity"}</span>
          <span>{isFR ? "Public/valeurs" : "Audience/values"}</span>
        </div>
      ) : null}

      {!compact ? (
        <div className="ws-ai-details-row">
          <details className="ws-ai-disclosure">
            <summary>{isFR ? "Ce que l’IA relit" : "What AI reviews"}</summary>
            <p>
              {isFR
                ? "La forme, la clarté, la cohérence avec le canon, l’âge du public et les valeurs de la plateforme."
                : "Language quality, clarity, canon consistency, reader age fit, and platform values."}
            </p>
          </details>

          <details className="ws-ai-disclosure">
            <summary>{isFR ? "Contrôle de l’auteur" : "Author control"}</summary>
            <p>
              {isFR
                ? "L’IA ne réécrit pas, ne publie pas, ne valide pas, ne remplace pas et ne décide jamais du canon."
                : "AI does not rewrite, publish, approve, replace, or decide canon."}
            </p>
          </details>
        </div>
      ) : null}

      <div className="ws-ai-action-row">
        <button
          type="button"
          className="ns-button ns-button-outline ws-ai-review-button"
          disabled={!canReview}
          onClick={handleReviewScene}
          title={disabledReason || (isFR ? "Demander une relecture consultative." : "Ask for advisory review.")}
        >
          {busy ? (isFR ? "Relecture…" : "Reviewing…") : isFR ? "Relire cette scène" : "Review this scene"}
        </button>

        {!compact ? (
          <a
            className="ws-ai-principles-link"
            href={getNextScenesAiPrinciplesLink(lang)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isFR ? "Principes IA" : "AI principles"} →
          </a>
        ) : null}
      </div>

      {disabledReason ? <p className="ws-ai-status-line">{disabledReason}</p> : null}
      {!disabledReason && !cleanSceneText ? (
        <p className="ws-ai-status-line">
          {isFR ? "Rédigez une scène pour activer la relecture." : "Draft a scene to enable review."}
        </p>
      ) : null}
      {error ? <p className="ws-ai-status-line ws-ai-status-error">{error}</p> : null}

      {review ? (
        <div className="ws-ai-result ws-ai-result-polished" aria-live="polite">
          <div className="ws-ai-result-topline">
            <div>
              <div className="ws-ai-result-title">
                {isFR ? "Relecture consultative" : "Advisory scene review"}
              </div>
              <p className="ws-ai-result-note">
                {isFR
                  ? "Aucune correction n’est appliquée automatiquement. L’auteur décide."
                  : "No correction is applied automatically. The writer decides."}
              </p>
            </div>
            <span className="ws-ai-result-human-pill">
              {isFR ? "Décision humaine" : "Human decision"}
            </span>
          </div>

          <div className="ws-ai-overall-card">
            <strong>{isFR ? "Impression générale" : "Overall impression"}</strong>
            <p>{review.overallImpression}</p>
          </div>

          <div className="ws-ai-result-sections ws-ai-result-sections-polished">
            <AiReviewSection
              label={isFR ? "Clarté et fluidité" : "Clarity and flow"}
              items={review.clarity}
              fallback={isFR ? "Aucune remarque de clarté particulière." : "No specific clarity note."}
            />
            <AiReviewSection
              label={isFR ? "Continuité" : "Continuity"}
              items={review.continuity}
              fallback={isFR ? "Aucune rupture de continuité signalée." : "No continuity issue flagged."}
            />
            <AiReviewSection
              label={isFR ? "Orthographe, grammaire et structure" : "Spelling, grammar, and sentence structure"}
              items={review.grammarAndStyle}
              fallback={isFR ? "Aucune remarque linguistique particulière." : "No specific language note."}
            />
            <AiReviewSection
              label={isFR ? "Public visé et valeurs NextScenes" : "Audience fit and NextScenes values"}
              items={review.platformStandards}
              fallback={isFR ? "Aucune alerte de public ou de valeurs." : "No audience or values alert."}
            />
            <AiReviewSection
              label={isFR ? "Améliorations manuelles suggérées" : "Suggested manual improvements"}
              items={review.suggestedManualImprovements}
              fallback={isFR ? "Aucune amélioration manuelle urgente." : "No urgent manual improvement."}
            />
          </div>

          {review.note ? <p className="ws-ai-result-note" style={{ marginTop: 8 }}>{review.note}</p> : null}

          {typeof onBackToScene === "function" ? (
            <div
              className="ws-ai-review-return-row"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                type="button"
                className="ns-button ns-button-primary"
                onClick={onBackToScene}
              >
                {isFR
                  ? "Terminé · Retour à la scène"
                  : "Done · Back to Scene"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function commentsKey(storyId) {
  return COMMENTS_PREFIX + String(storyId || "");
}
function intentKey(storyId) {
  return INTENT_PREFIX + String(storyId || "");
}
function decisionLogKey(storyId) {
  return DECISION_LOG_PREFIX + String(storyId || "");
}

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function toSafeInt(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getUiText() {
  try {
    const lang =
      (typeof window !== "undefined" &&
        (new URLSearchParams(window.location.search).get("lang") ||
          window.localStorage.getItem("ns.lang"))) ||
      "en";
    return TEXT[lang] || TEXT.en;
  } catch {
    return TEXT.en;
  }
}



function PublicationStatusPill({ info }) {
  if (!info?.isPublished) return null;
  return <span className="ns-publication-chip is-published">{info.publicationLabel}</span>;
}

function PreviewStatusPill({ info }) {
  if (!info?.isPublished) return null;
  return <span className="ns-publication-chip is-preview">{info.developmentLabel}</span>;
}


function getPublishedBookCoverUrl(story, publicationInfo) {
  return String(
    publicationInfo?.coverUrl ||
      story?.publicationCoverUrl ||
      story?.publishedCoverUrl ||
      story?.finalCoverImageUrl ||
      story?.coverImageUrl ||
      story?.coverUrl ||
      story?.coverImage ||
      story?.imageUrl ||
      ""
  ).trim();
}

function PublishedWorkspaceNotice({ story, publicationInfo, lang, onOpenAmazon }) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const coverUrl = getPublishedBookCoverUrl(story, publicationInfo);
  const title = String(story?.title || "").trim();

  return (
    <div className="ns-published-workspace-notice" role="note">
      <div className="ns-published-workspace-cover">
        {coverUrl ? (
          <img src={coverUrl} alt={title || (isFR ? "Couverture du livre" : "Book cover")} loading="lazy" />
        ) : (
          <div className="ns-published-workspace-cover-fallback" aria-hidden="true">
            {(title || "NS")
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join("")
              .toUpperCase()}
          </div>
        )}
      </div>

      <div className="ns-published-workspace-copy">
        <div className="ns-publication-mini-row">
          <PublicationStatusPill info={publicationInfo} />
        </div>

        <h3>
          {isFR
            ? "Cette storyline n’est plus active dans Writer Studio."
            : "This storyline is no longer active in Writer Studio."}
        </h3>

        <p>
          {isFR
            ? "Ce livre a été complété, poli et publié. L’espace d’écriture est fermé afin de préserver la version achevée."
            : "This book has been completed, polished, and published. The writing workspace is closed to protect the finished version."}
        </p>

        {publicationInfo?.amazonHref ? (
          <button
            type="button"
            className="ns-button ns-button-outline ns-amazon-action"
            onClick={onOpenAmazon}
          >
            {publicationInfo.cta}
          </button>
        ) : null}
      </div>
    </div>
  );
}


function WriterStudioBookThumb({ story, publicationInfo, large = false }) {
  const title = String(story?.title || "").trim();
  const coverUrl = getPublishedBookCoverUrl(story, publicationInfo);
  const initials = (title || "NS")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <div className={large ? "ws-book-thumb ws-book-thumb-large" : "ws-book-thumb"} aria-hidden="true">
      {coverUrl ? (
        <img src={coverUrl} alt="" loading="lazy" />
      ) : (
        <div className="ws-book-thumb-fallback">
          <span>{initials}</span>
        </div>
      )}
    </div>
  );
}

function WriterStudioCommandCenter({
  activeStory,
  lang,
  modeBadge,
  proposalsOpen,
  canonicalScenes,
  proposedScenes,
  analytics,
  isWriter,
  isSoloMode,
  canSeeProposals,
  canPropose,
  onAddCanon,
  onOpenStoryInfo,
  onSubmitProposal,
  onOpenProposalRound,
  onReviewProposals,
  onOpenReaderView,
}) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const publicationInfo = activeStory ? getKnownPublicationInfo(activeStory, lang) : null;
  const storyTitle = String(activeStory?.title || "").trim();
  const hasActiveStory = !!activeStory;
  const canonCount = Array.isArray(canonicalScenes) ? canonicalScenes.length : 0;
  const proposalCount = Math.max(
    Array.isArray(proposedScenes) ? proposedScenes.length : 0,
    toSafeInt(analytics?.proposalCount)
  );
  const voteTotal =
    toSafeInt(analytics?.votesUp) +
    toSafeInt(analytics?.votesDown) +
    toSafeInt(analytics?.votesNeutral);
  const published = !!publicationInfo?.isPublished;

  const headline = hasActiveStory
    ? storyTitle
    : isFR
    ? "Choisissez une storyline pour commencer."
    : "Choose a storyline to begin.";

  const stateLabel = published
    ? publicationInfo?.publicationLabel || (isFR ? "Publié" : "Published")
    : proposalsOpen
    ? isFR
      ? "Tour de propositions ouvert"
      : "Proposal round open"
    : isFR
    ? "Écriture canon active"
    : "Canon writing active";

  const stateBody = published
    ? isFR
      ? "Cette histoire est maintenant protégée comme œuvre achevée."
      : "This story is protected as a finished work."
    : proposalsOpen
    ? isFR
      ? "Les contributeurs peuvent proposer la prochaine scène. L’auteur garde la décision finale."
      : "Contributors can suggest the next scene. The author keeps final control."
    : isFR
    ? "L’auteur peut écrire directement. Les propositions s’ouvrent seulement quand la storyline l’autorise."
    : "The author can write directly. Proposal rounds open only when the storyline allows it.";

  const addDisabled = !hasActiveStory || published || !isWriter || (!isSoloMode && proposalsOpen);
  const proposalDisabled = !hasActiveStory || published || !canPropose || !canSeeProposals;
  const openRoundDisabled = !hasActiveStory || published || !isWriter || proposalsOpen || isSoloMode;

  return (
    <section className="ws-command-center" aria-label={isFR ? "Tableau de bord Writer Studio" : "Writer Studio dashboard"}>
      <div className="ws-command-card ws-command-primary">
        <div className="ws-command-visual" aria-hidden="true">
          <div className="ws-command-visual-glow" />
          <WriterStudioBookThumb story={activeStory || { title: "NextScenes" }} publicationInfo={publicationInfo || {}} large />
          <span className="ws-command-pen">✍️</span>
        </div>

        <div className="ws-command-copy">
          <span className="ws-command-kicker">{isFR ? "Atelier de l’auteur" : "Writer workspace"}</span>
          <h2>{headline}</h2>
          <p>{stateBody}</p>

          <WriterStudioJourneyGuide
            activeStory={activeStory}
            lang={lang}
            proposalsOpen={proposalsOpen}
            canonicalScenes={canonicalScenes}
            proposedScenes={proposedScenes}
            analytics={analytics}
            isWriter={isWriter}
            isSoloMode={isSoloMode}
            canSeeProposals={canSeeProposals}
            canAddCanon={!addDisabled}
            canSubmitProposal={!proposalDisabled}
            canOpenProposalRound={!openRoundDisabled}
            onAddCanon={onAddCanon}
            onOpenStoryInfo={onOpenStoryInfo}
            onSubmitProposal={onSubmitProposal}
            onOpenProposalRound={onOpenProposalRound}
            onReviewProposals={onReviewProposals}
            onOpenReaderView={onOpenReaderView}
          />
        </div>
      </div>

      <aside className={"ws-voting-window" + (proposalsOpen ? " is-open" : "")}>
        <div className="ws-voting-head">
          <span className="ws-command-kicker">{isFR ? "Fenêtre de collaboration" : "Collaboration window"}</span>
          <strong>{stateLabel}</strong>
        </div>
        <p>
          {proposalsOpen
            ? isFR
              ? "Les propositions, les votes et la décision finale vivent ici."
              : "Proposals, votes, and the final decision live here."
            : isFR
            ? "La salle reste calme jusqu’à l’ouverture du prochain tour."
            : "The room stays calm until the next proposal round opens."}
        </p>
        <div className="ws-vote-stat-grid" aria-label={isFR ? "Résumé des votes" : "Vote summary"}>
          <span><strong>{canonCount}</strong><small>{isFR ? "Canon" : "Canon"}</small></span>
          <span><strong>{proposalCount}</strong><small>{isFR ? "Propositions" : "Proposals"}</small></span>
          <span><strong>{voteTotal}</strong><small>{isFR ? "Votes" : "Votes"}</small></span>
        </div>
        <button type="button" className="ws-vote-review-link" onClick={onReviewProposals} disabled={!hasActiveStory}>
          {isFR ? "Voir les propositions et votes" : "Review proposals and votes"} →
        </button>
      </aside>
    </section>
  );
}



function WriterStudioJourneyGuide({
  activeStory,
  lang,
  proposalsOpen,
  canonicalScenes,
  proposedScenes,
  analytics,
  isWriter,
  isSoloMode,
  canSeeProposals,
  canAddCanon,
  canSubmitProposal,
  canOpenProposalRound,
  onAddCanon,
  onOpenStoryInfo,
  onSubmitProposal,
  onOpenProposalRound,
  onReviewProposals,
  onOpenReaderView,
}) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const hasActiveStory = !!activeStory;
  const publicationInfo = activeStory ? getKnownPublicationInfo(activeStory, lang) : null;
  const published = !!publicationInfo?.isPublished;
  const canonCount = Array.isArray(canonicalScenes) ? canonicalScenes.length : 0;
  const proposalCount = Math.max(
    Array.isArray(proposedScenes) ? proposedScenes.length : 0,
    toSafeInt(analytics?.proposalCount)
  );

  let activeStep = 0;
  let nextTitle = isFR ? "Que faire maintenant ?" : "What should I do next?";
  let nextBody = isFR
    ? "Choisissez une storyline, puis suivez le parcours éclairé. Les cartes grisées ne sont pas nécessaires maintenant."
    : "Choose a storyline, then follow the illuminated path. Dimmed cards are not needed right now.";

  if (hasActiveStory && published) {
    activeStep = 5;
    nextBody = isFR
      ? "Cette storyline est publiée ou protégée. Utilisez surtout Reader View, Story Info, et les outils de suivi."
      : "This storyline is published or protected. Use Reader View, Story Info, and tracking tools mainly.";
  } else if (hasActiveStory && !isWriter && proposalsOpen && canSeeProposals) {
    activeStep = 2;
    nextBody = isFR
      ? "Le Proposal Round est ouvert. Vous pouvez soumettre une Proposed Scene si vous avez une idée."
      : "The Proposal Round is open. You may submit a Proposed Scene if you have an idea.";
  } else if (hasActiveStory && !isWriter) {
    activeStep = 5;
    nextBody = isFR
      ? "Vous pouvez lire, suivre l’activité, et utiliser Reader Feedback lorsque c’est disponible."
      : "You can read, follow activity, and use Reader Feedback when it is available.";
  } else if (hasActiveStory && proposalsOpen && proposalCount > 0) {
    activeStep = 3;
    nextBody = isFR
      ? "Des Proposed Scenes attendent. Lisez-les, regardez les votes, puis décidez ce qui devient canon."
      : "Proposed Scenes are waiting. Read them, check the votes, then decide what becomes canon.";
  } else if (hasActiveStory && proposalsOpen) {
    activeStep = 2;
    nextBody = isFR
      ? "Le Proposal Round est ouvert. Attendez des propositions, soumettez la vôtre, ou préparez la décision."
      : "The Proposal Round is open. Wait for proposals, submit your own, or prepare the decision.";
  } else if (hasActiveStory && canonCount === 0) {
    activeStep = 1;
    nextBody = isFR
      ? "Commencez par Add Canon Scene. Les propositions, l’IA et les autres outils peuvent attendre."
      : "Start with Add Canon Scene. Proposals, AI, and other tools can wait.";
  } else if (hasActiveStory && !isSoloMode) {
    activeStep = 2;
    nextBody = isFR
      ? "La dernière scène canon est en place. Continuez avec Add Canon Scene, ou ouvrez un Proposal Round si vous voulez des contributions."
      : "The latest canon scene is in place. Continue with Add Canon Scene, or open a Proposal Round if you want contributions.";
  } else if (hasActiveStory) {
    activeStep = 1;
    nextBody = isFR
      ? "Continuez avec Add Canon Scene. Review this scene, Character Bible et Reader Feedback restent des supports."
      : "Continue with Add Canon Scene. Review this scene, Character Bible, and Reader Feedback remain support tools.";
  }

  const proposalAction = proposalsOpen
    ? {
        label: isFR ? "Soumettre une Proposed Scene" : "Submit Proposed Scene",
        handler: onSubmitProposal,
        enabled: canSubmitProposal,
      }
    : {
        label: isFR ? "Ouvrir le Proposal Round" : "Open Proposal Round",
        handler: onOpenProposalRound,
        enabled: canOpenProposalRound,
      };

  const flowSteps = isFR
    ? [
        {
          number: 1,
          title: "Canon Writing",
          body: canonCount > 0 ? "Scène canon déjà en place." : "Ajouter la partie officielle de l’histoire.",
          tag: canonCount > 0 ? "Fait" : "Principal",
          icon: "✍️",
          actionLabel: "Add Canon Scene",
          handler: onAddCanon,
          enabled: canAddCanon,
          requiredNow: activeStep === 1 || (canonCount === 0 && !proposalsOpen),
          complete: canonCount > 0,
        },
        {
          number: 2,
          title: "Proposal Round",
          body: proposalsOpen ? "Le tour est ouvert aux contributions." : "Optionnel : inviter des Proposed Scenes.",
          tag: isSoloMode ? "Optionnel" : proposalsOpen ? "Ouvert" : "Optionnel",
          icon: "💡",
          actionLabel: proposalAction.label,
          handler: proposalAction.handler,
          enabled: proposalAction.enabled,
          requiredNow: activeStep === 2 || (!isSoloMode && canonCount > 0),
          complete: proposalsOpen,
        },
        {
          number: 3,
          title: "Review and Decide",
          body: proposalCount > 0 ? "Lire les propositions et choisir le canon." : "Attend les Proposed Scenes.",
          tag: proposalCount > 0 ? "Décision" : "En attente",
          icon: "⚖️",
          actionLabel: "Review proposals and votes",
          handler: onReviewProposals,
          enabled: hasActiveStory && canSeeProposals && (proposalCount > 0 || proposalsOpen),
          requiredNow: activeStep === 3,
          complete: false,
        },
        {
          number: 4,
          title: "Story Support",
          body: "Story Info, Character Bible, continuité et guidance IA.",
          tag: "Support",
          icon: "📜",
          actionLabel: "Story Info",
          handler: onOpenStoryInfo,
          enabled: hasActiveStory,
          requiredNow: false,
          complete: false,
        },
        {
          number: 5,
          title: "Reader Feedback",
          body: canonCount > 0 ? "Lire la storyline et suivre les réactions." : "Disponible après la première scène canon.",
          tag: canonCount > 0 ? "Lecture" : "Plus tard",
          icon: "💬",
          actionLabel: "Reader View",
          handler: onOpenReaderView,
          enabled: hasActiveStory && canonCount > 0,
          requiredNow: activeStep === 5,
          complete: false,
        },
      ]
    : [
        {
          number: 1,
          title: "Canon Writing",
          body: canonCount > 0 ? "Canon scene already in place." : "Add the official part of the story.",
          tag: canonCount > 0 ? "Done" : "Main",
          icon: "✍️",
          actionLabel: "Add Canon Scene",
          handler: onAddCanon,
          enabled: canAddCanon,
          requiredNow: activeStep === 1 || (canonCount === 0 && !proposalsOpen),
          complete: canonCount > 0,
        },
        {
          number: 2,
          title: "Proposal Round",
          body: proposalsOpen ? "Round is open for contributions." : "Optional: invite Proposed Scenes.",
          tag: isSoloMode ? "Optional" : proposalsOpen ? "Open" : "Optional",
          icon: "💡",
          actionLabel: proposalAction.label,
          handler: proposalAction.handler,
          enabled: proposalAction.enabled,
          requiredNow: activeStep === 2 || (!isSoloMode && canonCount > 0),
          complete: proposalsOpen,
        },
        {
          number: 3,
          title: "Review and Decide",
          body: proposalCount > 0 ? "Read proposals and choose what becomes canon." : "Waiting for Proposed Scenes.",
          tag: proposalCount > 0 ? "Decision" : "Waiting",
          icon: "⚖️",
          actionLabel: "Review proposals and votes",
          handler: onReviewProposals,
          enabled: hasActiveStory && canSeeProposals && (proposalCount > 0 || proposalsOpen),
          requiredNow: activeStep === 3,
          complete: false,
        },
        {
          number: 4,
          title: "Story Support",
          body: "Story Info, Character Bible, continuity, and AI guidance.",
          tag: "Support",
          icon: "📜",
          actionLabel: "Story Info",
          handler: onOpenStoryInfo,
          enabled: hasActiveStory,
          requiredNow: false,
          complete: false,
        },
        {
          number: 5,
          title: "Reader Feedback",
          body: canonCount > 0 ? "Read the storyline and follow reactions." : "Available after the first canon scene.",
          tag: canonCount > 0 ? "Reading" : "Later",
          icon: "💬",
          actionLabel: "Reader View",
          handler: onOpenReaderView,
          enabled: hasActiveStory && canonCount > 0,
          requiredNow: activeStep === 5,
          complete: false,
        },
      ];

  return (
    <div className="ws-journey-guide ws-flow-guide" role="note">
      <div className="ws-next-step-card ws-flow-next-card">
        <span className="ws-guide-kicker">{isFR ? "Parcours Writer Studio" : "Writer Studio path"}</span>
        <strong>{nextTitle}</strong>
        <p>{nextBody}</p>
      </div>

      <div className="ws-flow-scroll-hint" aria-hidden="true">
        <span>⇦</span>
        <strong>{isFR ? "Faites défiler le parcours" : "Swipe the path"}</strong>
        <span>⇨</span>
      </div>

      <ol className="ws-flow-map" aria-label={isFR ? "Parcours visuel Writer Studio" : "Visual Writer Studio path"}>
        {flowSteps.map((step, index) => {
          const isCurrent = step.number === activeStep;
          const isMuted = !step.requiredNow && !isCurrent && !step.complete;
          return (
            <li key={step.number} className="ws-flow-item">
              <button
                type="button"
                className={
                  "ws-flow-card" +
                  (isCurrent ? " is-current" : "") +
                  (step.complete ? " is-complete" : "") +
                  (isMuted ? " is-muted" : "") +
                  (!step.enabled ? " is-disabled" : "")
                }
                onClick={step.handler}
                disabled={!step.enabled}
              >
                <span className="ws-flow-icon" aria-hidden="true">{step.icon}</span>
                <span className="ws-flow-copy">
                  <span className="ws-flow-topline">
                    <strong>{step.title}</strong>
                    <em>{step.tag}</em>
                  </span>
                  <span className="ws-flow-body">{step.body}</span>
                  <span className="ws-flow-action">{step.actionLabel}</span>
                </span>
              </button>
              {index < flowSteps.length - 1 ? <span className="ws-flow-arrow" aria-hidden="true">⇨</span> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function getStoryAuthorDisplay(story, fallback = "Unknown") {
  const profile = story?.authorProfile && typeof story.authorProfile === "object" ? story.authorProfile : {};
  const username =
    String(profile?.username || story?.authorUsername || story?.createdBy || story?.createdByUsername || story?.author || "").trim();
  const name = String(profile?.name || story?.authorName || story?.ownerName || "").trim();
  const avatarUrl =
    String(profile?.avatarUrl || story?.authorAvatarUrl || story?.authorAvatar || story?.ownerAvatarUrl || story?.ownerAvatar || "").trim();
  const displayName = name || username || fallback;
  return { username, name, avatarUrl, displayName };
}

function WriterStudioAuthorAvatar({ story, label }) {
  const author = getStoryAuthorDisplay(story, label || "Unknown");
  if (author.avatarUrl) {
    return (
      <img
        src={author.avatarUrl}
        alt={author.displayName}
        className="ns-author-mini-avatar"
        loading="lazy"
        onError={(e) => {
          try {
            e.currentTarget.style.display = "none";
          } catch {}
        }}
      />
    );
  }
  return (
    <span className="ns-author-mini-avatar ns-author-mini-avatar-fallback" aria-hidden="true">
      {(author.displayName || "?").slice(0, 1).toUpperCase()}
    </span>
  );
}

function safeSceneIllustration(scene) {
  const url = String(scene?.illustrationUrl || "").trim();
  if (!url) return null;
  return {
    url,
    caption: String(scene?.illustrationCaption || "").trim(),
    credit: String(scene?.illustrationCredit || "").trim(),
    alt: String(scene?.illustrationAltText || scene?.illustrationCaption || scene?.title || "Scene illustration").trim(),
    status: String(scene?.illustrationStatus || "").trim().toLowerCase(),
    type: String(scene?.illustrationType || "").trim(),
    reviewReason: String(scene?.illustrationReviewReason || "").trim(),
    reviewNote: String(scene?.illustrationReviewNote || "").trim(),
    reviewedBy: String(scene?.illustrationReviewedByName || scene?.illustrationApprovedByName || "").trim(),
  };
}

function illustrationRejectionReasonLabel(reason, t) {
  const r = String(reason || "").trim().toLowerCase();
  if (r === "does_not_match_scene") return t.sceneIllustrationReasonDoesNotMatch || "Does not match the scene";
  if (r === "image_quality") return t.sceneIllustrationReasonImageQuality || "Image quality is not suitable";
  if (r === "consent_or_copyright") return t.sceneIllustrationReasonConsentCopyright || "Consent or copyright concern";
  if (r === "safety_or_policy") return t.sceneIllustrationReasonSafetyPolicy || "Safety or policy concern";
  if (r === "other") return t.sceneIllustrationReasonOther || "Other";
  return String(reason || "").trim();
}

function SceneIllustrationPreview({ scene, t, compact = false }) {
  const art = safeSceneIllustration(scene);
  if (!art) return null;

  const statusLabel =
    art.status === "approved"
      ? t.sceneIllustrationApproved
      : art.status === "rejected"
      ? t.sceneIllustrationRejected
      : t.sceneIllustrationPending;

  return (
    <div className="ns-scene-illustration-box" style={{ marginTop: compact ? 8 : 12 }}>
      <div className="ns-scene-illustration-head">
        <strong>{t.sceneIllustrationLabel}</strong>
        <span className={`ns-scene-illustration-status ns-scene-illustration-status-${art.status || "pending"}`}>
          {statusLabel}
        </span>
      </div>
      <img
        src={art.url}
        alt={art.alt || t.sceneIllustrationLabel}
        className="ns-scene-illustration-img"
        loading="lazy"
        onError={(e) => {
          try {
            e.currentTarget.style.display = "none";
          } catch {}
        }}
      />
      {(art.caption || art.credit) ? (
        <p className="ns-card-meta" style={{ marginTop: 6 }}>
          {art.caption ? art.caption : ""}
          {art.credit ? `${art.caption ? " · " : ""}${t.sceneIllustrationCredit}: ${art.credit}` : ""}
        </p>
      ) : null}
      {art.status === "rejected" && (art.reviewReason || art.reviewNote) ? (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            borderRadius: 12,
            background: "rgba(254,242,242,0.72)",
            border: "1px solid rgba(239,68,68,0.20)",
            color: "#7f1d1d",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {art.reviewReason ? (
            <div><strong>{t.sceneIllustrationReviewReason || "Reason"}:</strong> {illustrationRejectionReasonLabel(art.reviewReason, t)}</div>
          ) : null}
          {art.reviewNote ? (
            <div style={{ marginTop: art.reviewReason ? 4 : 0 }}><strong>{t.sceneIllustrationReviewNote || "Note"}:</strong> {art.reviewNote}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


function FullDraftSceneIllustration({ scene, t }) {
  const art = safeSceneIllustration(scene);
  if (!art || art.status !== "approved") return null;

  return (
    <figure className="ns-full-draft-illustration">
      <img
        src={art.url}
        alt={art.alt || t.sceneIllustrationLabel || "Scene illustration"}
        loading="lazy"
        onError={(e) => {
          try {
            e.currentTarget.style.display = "none";
          } catch {}
        }}
      />
      {(art.caption || art.credit) ? (
        <figcaption>
          {art.caption ? <span>{art.caption}</span> : null}
          {art.credit ? (
            <span>{art.caption ? " · " : ""}{t.sceneIllustrationCredit || "Credit"}: {art.credit}</span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

/**
 * COMMENTS MODEL (compat + future-proof)
 * We store flat comments in localStorage cache:
 *   { id, author, text, createdAt, parentId? }
 *
 * Backward compat:
 * - If older saved data has nested replies: { replies: [...] }
 *   we flatten it on load.
 */
function normalizeComments(list) {
  if (!Array.isArray(list)) return [];

  const flat = [];
  const seen = new Set();

  const makeId = (prefix) =>
    `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  function pushUnique(item) {
    const id = String(item?.id || "").trim();
    if (!id) return;
    if (seen.has(id)) return;
    seen.add(id);
    flat.push(item);
  }

  list
    .filter(Boolean)
    .forEach((c) => {
      const cid = String(c?.id || "").trim() || makeId("c");
      const createdAt = c?.createdAt
        ? String(c.createdAt)
        : new Date().toISOString();
      const author = c?.author ? String(c.author) : getUiText().anonymous;
      const text = c?.text ? String(c.text) : "";

      // Parent (top-level)
      pushUnique({
        id: cid,
        author,
        text,
        createdAt,
        parentId: c?.parentId ? String(c.parentId) : "",
      });

      // Backward compat: nested replies
      const replies = Array.isArray(c?.replies) ? c.replies : [];
      replies.filter(Boolean).forEach((r) => {
        const rid = String(r?.id || "").trim() || makeId("r");
        const rCreatedAt = r?.createdAt
          ? String(r.createdAt)
          : new Date().toISOString();
        const rAuthor = r?.author ? String(r.author) : getUiText().anonymous;
        const rText = r?.text ? String(r.text) : "";

        pushUnique({
          id: rid,
          author: rAuthor,
          text: rText,
          createdAt: rCreatedAt,
          parentId: cid,
        });
      });
    });

  // Ensure consistent fields + strings
  const cleaned = flat
    .filter(Boolean)
    .map((x) => ({
      id: String(x.id || "").trim() || makeId("c"),
      author: String(x.author || getUiText().anonymous),
      text: String(x.text || ""),
      createdAt: x.createdAt ? String(x.createdAt) : new Date().toISOString(),
      parentId: x.parentId ? String(x.parentId) : "",
    }));

  // De-dupe again defensively
  const final = [];
  const ids = new Set();
  cleaned.forEach((x) => {
    if (!x.id) return;
    if (ids.has(x.id)) return;
    ids.add(x.id);
    final.push(x);
  });

  return final;
}

// Local cache (not authority)
function loadComments(storyId) {
  if (!storyId) return [];
  try {
    const raw = window.localStorage.getItem(commentsKey(storyId));
    if (!raw) return [];
    const parsed = safeJsonParse(raw, []);
    return normalizeComments(parsed);
  } catch {
    return [];
  }
}
function saveComments(storyId, comments) {
  if (!storyId) return;
  try {
    window.localStorage.setItem(
      commentsKey(storyId),
      JSON.stringify(normalizeComments(comments))
    );
  } catch {}
}

// Server authoritative (cross-device)
function mapFeedbackItemsToComments(items) {
  const rows = Array.isArray(items) ? items : [];
  const mapped = rows
    .filter(Boolean)
    .map((it) => {
      const id = String(it?.id || it?._id || "").trim();
      const author = String(it?.author || getUiText().anonymous);
      const text = String(it?.text || "");
      const createdAt = it?.createdAt
        ? String(it.createdAt)
        : new Date().toISOString();
      const parentId = it?.parentId ? String(it.parentId) : "";
      if (!id || !text.trim()) return null;
      return { id, author, text, createdAt, parentId };
    })
    .filter(Boolean);

  return normalizeComments(mapped);
}

function readIntentFromStorage(storyId) {
  if (!storyId) return "";
  try {
    const raw = window.localStorage.getItem(intentKey(storyId));
    return raw ? String(raw) : "";
  } catch {
    return "";
  }
}
function writeIntentToStorage(storyId, value) {
  if (!storyId) return;
  try {
    const v = (value || "").toString().trim();
    if (!v) {
      window.localStorage.removeItem(intentKey(storyId));
      return;
    }
    window.localStorage.setItem(intentKey(storyId), v);
  } catch {}
}

function loadDecisionLog(storyId) {
  if (!storyId) return [];
  try {
    const raw = window.localStorage.getItem(decisionLogKey(storyId));
    if (!raw) return [];
    const parsed = safeJsonParse(raw, []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveDecisionLog(storyId, log) {
  if (!storyId) return;
  try {
    window.localStorage.setItem(
      decisionLogKey(storyId),
      JSON.stringify(log || [])
    );
  } catch {}
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function countWords(text) {
  const t = (text || "").trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}
function hasParagraphBreaks(text) {
  const t = (text || "").trim();
  if (!t) return false;
  const lines = t.split("\n").map((s) => s.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  return t.includes("\n");
}


function getApiErrorMessage(err, fallback) {
  const fb = String(fallback || getUiText().genericError);
  if (!err) return fb;

  const status = err?.status || err?.statusCode || err?.response?.status;
  const direct =
    (typeof err === "string" ? err : "") ||
    (err?.response?.data?.error ? String(err.response.data.error) : "") ||
    (err?.response?.data?.message ? String(err.response.data.message) : "") ||
    (err?.data?.error ? String(err.data.error) : "") ||
    (err?.data?.message ? String(err.data.message) : "") ||
    (err?.error ? String(err.error) : "") ||
    (err?.message ? String(err.message) : "");

  const msg = String(direct || "").trim();
  if (msg) return status ? `${msg} (HTTP ${status})` : msg;

  return status ? `${fb} (HTTP ${status})` : fb;
}


function FormattedText({ text }) {
  const raw = String(text || "");
  const blocks = raw
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/g)
    .map((s) => s.trim())
    .filter(Boolean);

  if (blocks.length === 0) return null;

  return (
    <div style={{ textAlign: "justify" }}>
      {blocks.map((b, i) => (
        <p
          key={i}
          style={{
            margin: i === blocks.length - 1 ? 0 : "0 0 10px 0",
            textIndent: "24px",
            lineHeight: 1.65,
            fontFamily: '"Garamond", "Georgia", serif',
            fontSize: "16px",
          }}
        >
          {b.replace(/\n/g, " ").trim()}
        </p>
      ))}
    </div>
  );
}

function extractCanonTokensFromText(text) {
  const tokens = new Set();
  const t = (text || "").replace(/[“”"]/g, " ");
  const matches = t.match(/\b[A-Z][a-z]{2,}\b/g) || [];
  matches.forEach((w) => {
    const clean = String(w).trim();
    if (clean.length >= 3 && clean.length <= 20) tokens.add(clean);
  });
  return tokens;
}

function getSceneId(scene) {
  return String(scene?._id || scene?.id || "").trim();
}

function normalizeProposalSelectionTime(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value).trim()
    : d.toISOString();
}

function proposalTextFingerprintForAdoption(value) {
  const text = String(value == null ? "" : value);
  let hash = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (
    String(text.length) +
    ":" +
    (hash >>> 0).toString(16).padStart(8, "0")
  );
}

function buildProposalAdoptionSelection(scene) {
  return {
    sceneId: getSceneId(scene),
    title: String(
      scene?.title ||
        scene?.proposalTitle ||
        scene?.proposedTitle ||
        ""
    ).trim(),
    authorId: String(
      scene?.authorId ||
        scene?.authorProfile?.id ||
        scene?.createdById ||
        scene?.userId ||
        ""
    ).trim(),
    authorUsername: String(
      scene?.authorUsername ||
        scene?.createdBy ||
        scene?.author ||
        ""
    )
      .trim()
      .toLowerCase(),
    authorName: String(
      scene?.authorName ||
        scene?.authorProfile?.name ||
        scene?.author ||
        ""
    ).trim(),
    createdAt: normalizeProposalSelectionTime(
      scene?.createdAt
    ),
    textFingerprint: proposalTextFingerprintForAdoption(
      scene?.text ||
        scene?.proposalText ||
        scene?.proposedText ||
        ""
    ),
  };
}

function proposalSelectionMatchesScene(scene, selection) {
  if (!scene || !selection) return false;

  const actual = buildProposalAdoptionSelection(scene);
  const expected = {
    ...selection,
    authorUsername: String(
      selection?.authorUsername || ""
    )
      .trim()
      .toLowerCase(),
    createdAt: normalizeProposalSelectionTime(
      selection?.createdAt
    ),
  };

  if (
    !actual.sceneId ||
    actual.sceneId !== String(expected.sceneId || "").trim()
  ) {
    return false;
  }

  if (
    actual.title !== String(expected.title || "").trim() ||
    actual.createdAt !== expected.createdAt
  ) {
    return false;
  }

  if (
    expected.authorId &&
    actual.authorId !== expected.authorId
  ) {
    return false;
  }

  if (
    !expected.authorId &&
    expected.authorUsername &&
    actual.authorUsername !== expected.authorUsername
  ) {
    return false;
  }

  if (
    !expected.authorId &&
    !expected.authorUsername &&
    expected.authorName &&
    actual.authorName !== expected.authorName
  ) {
    return false;
  }

  return (
    actual.textFingerprint ===
    String(expected.textFingerprint || "").trim()
  );
}

function isCanonical(scene) {
  if (!scene) return false;

  const t = String(scene?.type || "").trim().toLowerCase();
  const st = String(scene?.status || "").trim().toLowerCase();

  // Rejected proposals and recoverably removed Canon scenes carry
  // decision metadata, but they are not active Canon and must never
  // participate in Current Canon ordering/focus.
  const explicitlyNotCanon =
    st === "rejected" ||
    st === "removed" ||
    st === "deleted";

  if (explicitlyNotCanon) return false;

  // Preserve older Canon records that may predate explicit approved status.
  if (t === "canon") {
    return !st || st === "approved" || st === "canon";
  }

  const approvedFlag =
    scene?.approved === true ||
    scene?.isApproved === true ||
    scene?.validated === true ||
    scene?.isValidated === true ||
    scene?.promoted === true ||
    scene?.isCanon === true;

  const approvedStatus =
    st === "approved" ||
    st === "canon" ||
    st === "validated" ||
    st === "accepted" ||
    st === "promoted" ||
    st === "approved_proposal" ||
    st === "approvedproposal" ||
    st === "approved-next" ||
    st === "approved_next" ||
    st === "approvednext";

  if (approvedFlag || approvedStatus) return true;

  // Legacy promoted proposals may carry a decision without having been
  // normalized to type=canon/status=approved. Only accept this fallback
  // when there is no explicit non-Canon status (guarded above).
  const hasDecision =
    (scene?.decisionNote && String(scene.decisionNote).trim()) ||
    (scene?.decisionBy && String(scene.decisionBy).trim()) ||
    (scene?.decisionAt && String(scene.decisionAt).trim());

  if (hasDecision && (t === "proposal" || t === "proposed")) return true;

  return false;
}

function isPendingProposal(scene) {
  const t = String(scene?.type || "").toLowerCase();
  const st = String(scene?.status || "").toLowerCase();
  const isProposalType = t === "proposal" || t === "proposed";
  const pending = st === "pending" || !st;
  return !!scene && isProposalType && pending;
}

function readThreadTargetFromUrlOrStorage() {
  let fromUrl = "";
  try {
    const params = new URLSearchParams(window.location.search || "");
    fromUrl =
      params.get("storyId") ||
      params.get("threadId") ||
      params.get("focusStoryId") ||
      "";
  } catch {}
  if (fromUrl) return String(fromUrl);

  const keys = [
    "ns.writerStudio.focusStoryId",
    "ns.writerStudio.storyId",
    "ns.threadTargetStoryId",
    "ns.selectedStoryId",
    "ns.reader.focusStoryId",
    "ns.focusStoryId",
    "focusStoryId",
    "storyId",
  ];

  for (let i = 0; i < keys.length; i += 1) {
    try {
      const v = window.localStorage.getItem(keys[i]);
      if (v && String(v).trim()) return String(v).trim();
    } catch {}
  }
  return "";
}

function readWriterStudioSceneContext() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return {
      storyId: String(params.get("storyId") || "").trim(),
      chapterId: String(params.get("chapterId") || "").trim(),
      sceneId: String(params.get("sceneId") || "").trim(),
      newScene: String(params.get("newScene") || "").trim() === "1",
      demoPreview:
        String(params.get("demoPreview") || "").trim() === "1",
      previewSceneTitle:
        String(params.get("previewSceneTitle") || "").trim(),
      previewSceneText:
        String(params.get("previewSceneText") || ""),
      previewChapterTitle:
        String(params.get("previewChapterTitle") || "").trim(),
    };
  } catch {
    return {
      storyId: "",
      chapterId: "",
      sceneId: "",
      newScene: false,
      demoPreview: false,
      previewSceneTitle: "",
      previewSceneText: "",
      previewChapterTitle: "",
    };
  }
}

function readCommentTargetFromUrlOrStorage() {
  let fromUrl = "";
  try {
    const params = new URLSearchParams(window.location.search || "");
    fromUrl = params.get("commentId") || params.get("focusCommentId") || "";
  } catch {}
  if (fromUrl && String(fromUrl).trim()) return String(fromUrl).trim();

  const keys = [
    "ns.threadTargetCommentId",
    "ns.writerStudio.focusCommentId",
    "ns.reader.focusCommentId",
    "ns.focusCommentId",
    "focusCommentId",
  ];
  for (let i = 0; i < keys.length; i += 1) {
    try {
      const v = window.localStorage.getItem(keys[i]);
      if (v && String(v).trim()) return String(v).trim();
    } catch {}
  }
  return "";
}

function clearCommentTargetInStorage() {
  const keys = [
    "ns.threadTargetCommentId",
    "ns.writerStudio.focusCommentId",
    "ns.reader.focusCommentId",
    "ns.focusCommentId",
    "focusCommentId",
  ];
  keys.forEach((k) => {
    try {
      window.localStorage.removeItem(k);
    } catch {}
  });
}

const INPUT_STYLE = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
  fontSize: "15px",
  lineHeight: 1.45,
  background: "#ffffff",
};

const FILTER_PANEL_STYLE = {
  marginTop: "10px",
  padding: "12px",
  borderRadius: "14px",
  background: "#f6f2ea",
  border: "1px solid rgba(15,23,42,0.10)",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const FILTER_PILL_STYLE = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "999px",
  border: "1px solid rgba(15,23,42,0.14)",
  background: "#ffffff",
  fontSize: "14px",
  lineHeight: 1.3,
};

const TEXTAREA_STYLE = {
  width: "100%",
  padding: "12px",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  fontFamily: '"Garamond", "Georgia", serif',
  fontSize: "16px",
  lineHeight: 1.65,
  background: "#ffffff",
  minHeight: "260px",
  resize: "vertical",
};

function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  maxWidth = 980,
  zIndex = 1600,
  closeLabel = getUiText().close,
  tone = ""
}) {
  useEffect(() => {
    if (
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const body = document.body;
    const root = document.documentElement;

    const previous = {
      bodyOverflow: body.style.overflow,
      bodyOverscroll:
        body.style.overscrollBehavior,
      rootOverflow: root.style.overflow,
      rootOverscroll:
        root.style.overscrollBehavior,
      rootScrollbarGutter:
        root.style.scrollbarGutter,
    };

    // Keep the page geometry stable while the modal itself owns scrolling.
    // The backdrop already provides the fixed viewport; the modal window
    // is positioned relatively inside that grid to avoid double-fixed jumps.
    root.style.scrollbarGutter = "stable";
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow =
        previous.bodyOverflow;
      body.style.overscrollBehavior =
        previous.bodyOverscroll;
      root.style.overflow =
        previous.rootOverflow;
      root.style.overscrollBehavior =
        previous.rootOverscroll;
      root.style.scrollbarGutter =
        previous.rootScrollbarGutter;
    };
  }, []);

  const modal = (
    <div
      className="ws-textbook-modal-backdrop"
      data-writer-studio-textbook-modal-backdrop="true"
      data-writer-studio-stable-scroll-modal="true"
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(15,23,42,0.52)",
        overflow: "hidden",
      }}
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget
        ) {
          onClose?.();
        }
      }}
    >
      <section
        className={`ns-modal ws-textbook-modal-window${tone ? ` ns-modal-${tone}` : ""}`}
        data-writer-studio-textbook-modal="true"
        role="dialog"
        aria-modal="true"
        aria-label={String(title || "Dialog")}
        style={{
          position: "relative",
          zIndex: zIndex + 1,
          width:
            `min(${maxWidth}px, 94vw)`,
          maxHeight: "86vh",
          overflowY: "auto",
          overflowX: "hidden",
          padding: 0,
          borderRadius: 18,
          border:
            "1px solid rgba(5,150,105,0.28)",
          background: "#ffffff",
          boxShadow:
            "0 28px 80px rgba(15,23,42,0.28)",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
      >
        <div
          className="ns-modal-header"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            padding: "14px 18px",
            borderBottom:
              "1px solid rgba(148,163,184,0.22)",
            background: "#f7fdf9",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2
              className="ns-section-title"
              style={{ margin: 0 }}
            >
              {title}
            </h2>

            {subtitle ? (
              <p
                className="ns-card-meta"
                style={{
                  marginTop: 6,
                  marginBottom: 0,
                }}
              >
                {subtitle}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={onClose}
          >
            {closeLabel}
          </button>
        </div>

        <div
          className="ns-modal-body ns-writer-studio-modal-body"
          style={{
            padding: 18,
            overflow: "visible",
          }}
        >
          {children}
        </div>
      </section>
    </div>
  );

  if (
    typeof document === "undefined" ||
    !document.body
  ) {
    return modal;
  }

  return createPortal(
    modal,
    document.body
  );
}

/** Robust ownership detection (fixes your exact issue) */
function norm(v) {
  return String(v || "").trim().toLowerCase();
}

function identityVariants(v) {
  const s = norm(v);
  if (!s) return [];
  const local = s.includes("@") ? s.split("@")[0] : "";
  const out = [s];
  if (local && local !== s) out.push(local);
  return out;
}

function identitiesMatch(a, b) {
  const av = identityVariants(a);
  const bv = identityVariants(b);
  if (!av.length || !bv.length) return false;
  return av.some((x) => bv.includes(x));
}

function getStoryOwners(story) {
  if (!story) return [];
  const owners = [
    story.authorUsername,
    story.createdBy,
    story.author,
    story.createdByUsername,
    story.authorProfile?.username,
    story.authorName,
    story.authorProfile?.name,
  ];
  return owners.map(norm).filter(Boolean);
}

function getSceneAuthors(scene) {
  if (!scene) return [];
  const candidates = [
    scene.authorUsername,
    scene.author,
    scene.authorName,
    scene.createdBy,
    scene.createdByUsername,
    scene.proposedBy,
    scene.proposedByUsername,
    scene.username,
    scene.user,
    scene.by,
    scene.writer,
    scene.authorProfile?.username,
    scene.authorProfile?.name,
    scene.meta?.authorUsername,
    scene.meta?.author,
    scene.meta?.createdBy,
    scene.meta?.username,
  ];
  const out = candidates.map(norm).filter(Boolean);
  return Array.from(new Set(out));
}

// Comment signature (for cheap "did anything change" checks)

function extractApiError(err, fallback) {
  const fb = String(fallback || getUiText().genericRequestFailed);
  if (!err) return fb;

  // common shapes
  const direct = err?.error || err?.message;
  const dataErr = err?.data?.error || err?.data?.message;
  const respErr = err?.response?.data?.error || err?.response?.data?.message;
  const status = err?.status || err?.statusCode || err?.response?.status;

  const msg = String(respErr || dataErr || direct || "").trim();
  if (msg) {
    if (status) return `${msg} (HTTP ${status})`;
    return msg;
  }
  if (status) return `${fb} (HTTP ${status})`;
  return fb;
}

function commentsSignature(list) {
  const arr = Array.isArray(list) ? list : [];
  if (arr.length === 0) return "0||";
  let bestTs = -1;
  let bestId = "";
  for (const c of arr) {
    const ts = new Date(c?.createdAt || 0).getTime();
    const safeTs = Number.isFinite(ts) ? ts : 0;
    if (safeTs > bestTs) {
      bestTs = safeTs;
      bestId = String(c?.id || "");
    }
  }
  return `${arr.length}|${bestId}|${bestTs}`;
}

/** Avatar rendering (initials-based, neutral, no backend calls) */
function initialsFromName(nameRaw) {
  const s = String(nameRaw || "").trim();
  if (!s) return "A";
  const cleaned = s.replace(/[@._-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return "A";

  const parts = cleaned.split(" ").filter(Boolean);
  const first = parts[0] ? parts[0][0] : "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  const two = (first + last).toUpperCase();
  if (two.trim()) return two.slice(0, 2);

  return cleaned.slice(0, 2).toUpperCase();
}

function AvatarBubble({ name, size = 28, title }) {
  const label = initialsFromName(name);
  return (
    <div
      title={title || String(name || "")}
      aria-label={title || String(name || "")}
      style={{
        width: size,
        height: size,
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size <= 28 ? "12px" : "13px",
        fontWeight: 800,
        letterSpacing: "0.02em",
        color: "#0f172a",
        background: "#f1f5f9",
        border: "1px solid #e5e7eb",
        userSelect: "none",
        flex: "0 0 auto",
      }}
    >
      {label}
    </div>
  );
}

/** Writing mode label helpers (UI-only; no backend expectations) */
function getWritingModeRaw(story) {
  const raw = String(story?.writingMode || story?.mode || "").trim();
  return raw || "Solo";
}

function deriveModeForDisplay(story) {
  const raw = getWritingModeRaw(story);
  const lower = raw.toLowerCase();

  const groupModeFromStory = String(
    story?.groupMode || story?.group?.mode || ""
  )
    .trim()
    .toLowerCase();
  const groupModeLabel =
    groupModeFromStory ||
    (lower.includes("closed") ? "closed" : lower.includes("open") ? "open" : "");

  if (lower.includes("group")) {
    if (groupModeLabel === "closed") return "Group Writing (Closed)";
    return "Group Writing (Open)";
  }
  if (lower.includes("collab") || lower.includes("collaborative"))
    return "Collaborative Writing";
  return "Solo Writing";
}

function getModePillTheme(modeLabel) {
  const m = String(modeLabel || "").toLowerCase();

  // Mild differentiation: same NextScenes calm palette, different “personalities”.
  if (m.includes("solo")) {
    return {
      bg: "#f0f9ff",
      border: "#bae6fd",
      text: "#075985",
      dot: "#0284c7",
    };
  }
  if (m.includes("collaborative")) {
    return {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      text: "#166534",
      dot: "#16a34a",
    };
  }
  if (m.includes("open")) {
    return {
      bg: "#fff7ed",
      border: "#fed7aa",
      text: "#9a3412",
      dot: "#f97316",
    };
  }
  if (m.includes("closed")) {
    return {
      bg: "#fef2f2",
      border: "#fecaca",
      text: "#991b1b",
      dot: "#ef4444",
    };
  }
  return { bg: "#f3f4f6", border: "#e5e7eb", text: "#374151", dot: "#6b7280" };
}

function ModePill({ label }) {
  const t = getModePillTheme(label);
  const pill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${t.border}`,
    background: t.bg,
    color: t.text,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.2,
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  const dot = {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: t.dot,
    boxShadow: `0 0 0 3px ${t.bg}`,
  };

  return (
    <span style={pill} title={`${getUiText().writingModeTooltip}: ${label}`}>
      <span style={dot} />
      {label}
    </span>
  );
}

function getStoryOwnerLabel(story) {
  if (!story) return getUiText().unknown;
  return (
    String(story?.authorName || "").trim() ||
    String(story?.authorUsername || "").trim() ||
    String(story?.author || "").trim() ||
    String(story?.createdBy || "").trim() ||
    getUiText().unknown
  );
}

function getSceneAuthorLabel(scene) {
  if (!scene) return getUiText().unknown;
  return (
    String(scene?.authorName || "").trim() ||
    String(scene?.authorUsername || "").trim() ||
    String(scene?.author || "").trim() ||
    String(scene?.createdBy || "").trim() ||
    String(scene?.decisionBy || "").trim() ||
    getUiText().unknown
  );
}

function toTimeValue(value) {
  const t = new Date(value || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function storyHistoryIcon(entry) {
  const type = String(entry?.type || "history").toLowerCase();
  const actionKey = String(entry?.actionKey || "").toLowerCase();

  if (actionKey === "story_created") return "📜";
  if (actionKey === "proposal_round_opened" || actionKey === "proposal_round_currently_open") return "🔓";
  if (actionKey === "proposal_round_closed") return "🔒";
  if (actionKey === "proposal_approved_into_canon") return "✔";
  if (actionKey === "proposal_rejected_one" || actionKey === "proposal_rejected_all") return "❌";
  if (type === "canon") return "🟢";
  if (type === "proposal") return "💡";
  if (type === "decision") return "✔";
  return "•";
}

function buildStoryHistoryEntries({
  t,
  story,
  canonicalScenes,
  decisionLog,
  proposalsOpen,
  proposedScenes,
}) {
  if (!story) return [];

  const ownerLabel = getStoryOwnerLabel(story);
  const serverHistory = Array.isArray(story?.storyHistory) ? story.storyHistory : [];

  const entries = serverHistory
    .filter(Boolean)
    .map((row, index) => ({
      id: String(row?.id || `server_${index}`),
      actionKey: String(row?.actionKey || row?.action || "").trim().toLowerCase().replace(/\s+/g, "_"),
      action: String(row?.actionLabel || row?.action || "").trim(),
      detail:
        String(row?.note || "").trim() ||
        (String(row?.by || "").trim() ? `${t.by} ${String(row.by).trim()}` : ""),
      at: String(row?.at || "").trim(),
      type: String(row?.type || "history").trim().toLowerCase() || "history",
      by: String(row?.by || "").trim(),
      sceneId: String(row?.sceneId || "").trim(),
      sceneNumber:
        Number.isFinite(Number(row?.sceneNumber)) && Number(row?.sceneNumber) > 0
          ? Number(row.sceneNumber)
          : null,
      title: String(row?.title || "").trim(),
    }))
    .filter((entry) => entry.action);

  if (entries.length === 0) {
    if (story?.createdAt) {
      entries.push({
        id: `created_${story._id || story.id || "story"}`,
        actionKey: "story_created",
        action: t.storyCreated,
        detail: ownerLabel ? `${t.by} ${ownerLabel}` : "",
        at: String(story.createdAt),
        type: "history",
        by: ownerLabel,
      });
    }

    (Array.isArray(canonicalScenes) ? canonicalScenes : []).forEach((scene, index) => {
      const sceneNumber = Number(scene?.sceneNumber) || index + 1;
      const title = String(scene?.title || t.card_untitled_story).trim();
      const sceneAuthor = getSceneAuthorLabel(scene);
      const approvedFromProposal = !!String(scene?.decisionAt || "").trim();
      const action = approvedFromProposal
        ? `${t.proposalApprovedIntoCanon} (${t.sceneLabel} ${sceneNumber})`
        : `${t.canonSceneLabel} ${sceneNumber} ${t.addedLabel}`;
      const detail = approvedFromProposal
        ? `${title} · ${t.approvedByLabel} ${String(scene?.decisionBy || sceneAuthor).trim() || t.writerRoleLabel}`
        : `${title} · ${t.by} ${sceneAuthor}`;

      entries.push({
        id: `canon_${getSceneId(scene) || sceneNumber}`,
        actionKey: approvedFromProposal ? "proposal_approved_into_canon" : "canon_scene_added",
        action,
        detail,
        at: String(scene?.decisionAt || scene?.createdAt || story?.updatedAt || ""),
        type: approvedFromProposal ? "decision" : "canon",
        by: approvedFromProposal ? String(scene?.decisionBy || sceneAuthor).trim() : sceneAuthor,
        sceneId: getSceneId(scene),
        sceneNumber,
        title,
      });
    });

    (Array.isArray(decisionLog) ? decisionLog : []).forEach((item, index) => {
      const actionKey = String(item?.action || "").trim().toLowerCase();
      if (!actionKey || actionKey === "approve") return;

      let mappedActionKey = "";
      let action = "";
      let type = "decision";

      if (actionKey === "open_round") {
        mappedActionKey = "proposal_round_opened";
        action = t.proposalRoundOpened;
        type = "proposal";
      } else if (actionKey === "reject_one") {
        mappedActionKey = "proposal_rejected_one";
        action = t.oneProposalRejected;
      } else if (actionKey === "reject_all") {
        mappedActionKey = "proposal_rejected_all";
        action = t.allProposalsRejectedLabel;
      } else {
        return;
      }

      const by = String(item?.by || ownerLabel).trim() || ownerLabel;
      const note = String(item?.note || item?.sceneTitle || "").trim();

      entries.push({
        id: `log_${item?.id || index}`,
        actionKey: mappedActionKey,
        action,
        detail: note ? `${note} · ${t.by} ${by}` : `${t.by} ${by}`,
        at: String(item?.at || story?.updatedAt || ""),
        type,
        by,
      });
    });
  }

  if (proposalsOpen) {
    const pendingCount = Array.isArray(proposedScenes) ? proposedScenes.length : 0;
    entries.unshift({
      id: `open_now_${story._id || story.id || "story"}`,
      actionKey: "proposal_round_currently_open",
      action: t.proposalRoundCurrentlyOpen,
      detail:
        pendingCount > 0
          ? `${pendingCount} ${pendingCount === 1 ? t.pendingProposalSingle : t.pendingProposalPlural}`
          : t.waitingForProposals,
      at: String(story?.updatedAt || story?.canonUpdatedAt || story?.createdAt || ""),
      type: "proposal",
      isCurrent: true,
      by: ownerLabel,
    });
  }

  entries.sort((a, b) => {
    const diff = toTimeValue(b?.at) - toTimeValue(a?.at);
    if (diff !== 0) return diff;
    return String(b?.id || "").localeCompare(String(a?.id || ""));
  });

  const seen = new Set();
  return entries.filter((entry) => {
    const key = `${entry.actionKey}|${entry.action}|${entry.detail}|${entry.at}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}


/**
 * ✅ FIXED: modeMatchesFilter
 * Your filter dropdown uses the *display strings* in WRITING_MODES.
 * So the filter must compare against those strings, not internal keys.
 */
function modeMatchesFilter(modeDisplay, filterValue) {
  const m = String(modeDisplay || "").trim().toLowerCase();
  const f = String(filterValue || "").trim().toLowerCase();

  if (!f || f === "all") return true;

  // If user selected exact display string (recommended path)
  // e.g. "Solo Writing", "Group Writing (Open)"...
  if (WRITING_MODES.map((x) => String(x).toLowerCase()).includes(f)) {
    return m === f;
  }

  // Back-compat: if something ever passes internal keys
  if (f === "solo") return m.includes("solo");
  if (f === "collaborative") return m.includes("collaborative");
  if (f === "group_open") return m.includes("group") && m.includes("open");
  if (f === "group_closed") return m.includes("group") && m.includes("closed");

  // Unknown filter value: don’t accidentally hide everything
  return true;
}


function AiSceneProposalCard({ story, lang, disabled = false, disabledReason = "", onAdoptProposal = null }) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const [proposalCount, setProposalCount] = useState("3");
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [editableTitle, setEditableTitle] = useState("");
  const [editableDraft, setEditableDraft] = useState("");
  const [showAiDraftModal, setShowAiDraftModal] = useState(false);
  const [aiDraftStatus, setAiDraftStatus] = useState("");

  const storyId = String(story?.id || story?._id || "").trim();
  const aiDraftStorageKey = storyId ? `ns.aiSceneProposalDraft.${storyId}` : "";
  const proposals = normalizeWriterStudioAiProposalList(
    Array.isArray(result?.proposals) ? result.proposals : [],
    lang
  );
  const selectedProposal =
    selectedIndex >= 0 && selectedIndex < proposals.length ? proposals[selectedIndex] : null;
  const canRequest = Boolean(storyId && !disabled && !busy);
  const canAdopt = Boolean(selectedProposal && String(editableDraft || "").trim());

  function proposalToDraftText(proposal) {
    const p = proposal && typeof proposal === "object" ? proposal : {};

    // The editable AI draft must contain only the proposed scene prose.
    // Supporting notes such as direction, action summary, characters,
    // continuity, value, and risk warning stay visible on the proposal card,
    // but must not be copied into the AI draft modal or the human draft.
    const sceneDraft = String(
      p.sceneDraft || p.proposedSceneText || p.sceneText || p.draftScene || p.fullScene || p.actualScene || ""
    ).trim();

    if (sceneDraft) return sceneDraft;

    // Last-resort fallback for older saved proposals that may not have a
    // sceneDraft field yet. Keep this prose-only and do not append advisory notes.
    return String(p.shortSceneProposal || p.summary || p.proposal || p.whatHappens || p.mainEvent || "").trim();
  }

  function handleClearProposals() {
    setResult(null);
    setSelectedIndex(-1);
    setEditableTitle("");
    setEditableDraft("");
    setShowAiDraftModal(false);
    setAiDraftStatus("");
    setError("");
  }

  function handleSelectProposal(proposal, index) {
    if (selectedIndex === index && (editableTitle || editableDraft)) {
      setShowAiDraftModal(true);
      return;
    }

    setSelectedIndex(index);
    setEditableTitle(String(proposal?.title || "").trim());
    setEditableDraft(proposalToDraftText(proposal));
    setAiDraftStatus("");
    setError("");
    setShowAiDraftModal(true);
  }

  function handleAdoptSelectedProposal() {
    const title = String(editableTitle || selectedProposal?.title || "").trim();
    const draftText = String(editableDraft || "").trim();

    if (!draftText) {
      setAiDraftStatus(
        isFR
          ? "Le brouillon IA est vide."
          : "The AI draft is empty."
      );
      return;
    }

    if (typeof onAdoptProposal !== "function") {
      setAiDraftStatus(
        isFR
          ? "Impossible de copier dans le brouillon humain pour le moment."
          : "Could not copy into the human draft right now."
      );
      return;
    }

    onAdoptProposal({
      title,
      text: draftText,
      source: "ai_scene_proposal",
      proposal: selectedProposal || null,
    });

    setAiDraftStatus(
      isFR
        ? "Copié dans le brouillon humain."
        : "Copied into the human draft."
    );

    setShowAiDraftModal(false);
  }

  function handleSaveAiDraft() {
    const title = String(editableTitle || selectedProposal?.title || "").trim();
    const draftText = String(editableDraft || "").trim();

    if (!aiDraftStorageKey) {
      setAiDraftStatus(
        isFR
          ? "Aucune histoire active pour sauvegarder ce brouillon IA."
          : "No active story available for saving this AI draft."
      );
      return;
    }

    if (!draftText) {
      setAiDraftStatus(
        isFR
          ? "Le brouillon IA est vide."
          : "The AI draft is empty."
      );
      return;
    }

    try {
      window.localStorage.setItem(
        aiDraftStorageKey,
        JSON.stringify({
          title,
          text: draftText,
          source: "ai_scene_proposal",
          savedAt: new Date().toISOString(),
        })
      );

      setAiDraftStatus(
        isFR
          ? "Brouillon IA sauvegardé localement."
          : "AI draft saved locally."
      );
    } catch {
      setAiDraftStatus(
        isFR
          ? "Impossible de sauvegarder le brouillon IA."
          : "Could not save the AI draft."
      );
    }
  }

  function handleClearAiDraft() {
    if (aiDraftStorageKey) {
      try {
        window.localStorage.removeItem(aiDraftStorageKey);
      } catch {
        // ignore localStorage errors
      }
    }

    setEditableTitle("");
    setEditableDraft("");
    setAiDraftStatus(isFR ? "Brouillon IA effacé." : "AI draft cleared.");
  }

  async function handleRequestSceneProposals() {
    if (!storyId) {
      setError(isFR ? "Aucune histoire active." : "No active story.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const data = await api.post("/api/ai/propose-scenes", {
        storyId,
        storyTitle: String(story?.title || story?.name || "").trim(),
        synopsis: String(story?.synopsis || story?.description || story?.summary || "").trim(),
        language: isFR ? "fr" : "en",
        numberOfProposals: Number(proposalCount) || 3,
        proposalCount: Number(proposalCount) || 3,
        requestNote:
          String(instruction || "").trim() ||
          (/ugo/i.test(String(story?.title || "")) &&
          /magic seed|graine magique/i.test(String(story?.title || ""))
            ? isFR
              ? "Continuer Ugo et la graine magique après la scène du petit arbre assoiffé. Proposer de vraies scènes suivantes éditables d’environ 400 à 500 mots chacune, douces, morales, simples et adaptées aux enfants de 4 à 10 ans."
              : "Continue Ugo and the Magic Seed after the thirsty little tree scene. Propose actual editable next scenes of about 400 to 500 words each, gentle, moral, simple, and suitable for ages 4 to 10."
            : isFR
            ? "Proposer de vraies prochaines scènes éditables d’environ 400 à 500 mots chacune. Ne pas donner des consignes."
            : "Propose actual editable next scenes of about 400 to 500 words each. Do not give instructions."),
        advisoryOnly: true,
        source: "writerStudio",
      });

      setResult(data);
      setSelectedIndex(-1);
      setEditableTitle("");
      setEditableDraft("");
      setShowAiDraftModal(false);
      setAiDraftStatus("");
    } catch (err) {
      setError(
        err?.message ||
          (isFR
            ? "La proposition IA n’a pas pu être générée."
            : "AI scene proposal could not be generated.")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="ws-optional-tool ws-optional-tool-ai">
      <summary>
        <span className="ws-optional-tool-copy">
          <strong>{isFR ? "Assistant IA fondé sur le canon" : "Canon-grounded AI Assistant"}</strong>
          <small>{isFR ? "Ouvrir seulement lorsque vous avez besoin de propositions assistées." : "Open only when you need assisted scene proposals."}</small>
        </span>
        <span className="ws-optional-tool-state" aria-hidden="true">{isFR ? "Afficher" : "Show"}</span>
      </summary>
      <div className="ws-optional-tool-body">
        <section
      className="ns-card ns-card-soft"
      style={{
        marginTop: 22,
        marginBottom: 18,
        border: "1px solid #bfdbfe",
        borderLeft: "8px solid #1d4ed8",
        borderRadius: 24,
        background:
          "linear-gradient(135deg, #eff6ff 0%, #ffffff 48%, #ecfdf5 100%)",
        boxShadow: "0 18px 40px rgba(30, 64, 175, 0.10)",
      }}
      aria-label={isFR ? "Assistant de proposition de scène IA" : "AI Scene Proposal Assistant"}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 18,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: "1 1 560px", minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 999,
              background: "#1e3a8a",
              color: "#ffffff",
              fontWeight: 800,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <span aria-hidden="true">✍️</span>
            <span>{isFR ? "Assistant fondé sur le canon" : "Canon-grounded Assistant"}</span>
          </div>

          <h3
            style={{
              margin: "0 0 8px",
              color: "#1e3a8a",
              fontSize: "clamp(1.35rem, 2vw, 1.85rem)",
              lineHeight: 1.12,
            }}
          >
            {isFR ? "Proposition IA · Prochaines scènes possibles" : "AI Scene Proposal · Possible Next Scenes"}
          </h3>

          <p
            style={{
              margin: 0,
              color: "#334155",
              lineHeight: 1.55,
              maxWidth: 820,
            }}
          >
            {isFR
              ? "Demandez de vrais brouillons éditables de prochaines scènes, d’environ 400 à 500 mots chacun, fondés sur le canon, les scènes validées, la mémoire de l’histoire et les personnages. Ce ne sont pas des consignes. Rien n’est enregistré, approuvé ou publié automatiquement. L’auteur garde la décision finale."
              : "Request actual editable next-scene drafts, about 400 to 500 words each, grounded in the latest validated scenes, story memory, and character guidance. These are proposals, not instructions. Nothing is saved, approved, or published automatically. The author keeps final control."}
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 14,
            }}
          >
            {[
              isFR ? "Scènes éditables" : "Editable scene drafts",
              isFR ? "Fondé sur le canon" : "Canon grounded",
              isFR ? "Aucun canon automatique" : "No automatic canon",
              isFR ? "Décision humaine" : "Human decision",
            ].map((item) => (
              <span
                key={item}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  border: "1px solid #bfdbfe",
                  borderRadius: 999,
                  padding: "6px 10px",
                  color: "#1d4ed8",
                  background: "rgba(255,255,255,0.86)",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        <div style={{ flex: "0 1 330px", width: "100%", maxWidth: 380 }}>
          <label
            style={{
              display: "block",
              color: "#0f172a",
              fontWeight: 800,
              marginBottom: 6,
            }}
          >
            {isFR ? "Nombre de propositions" : "Number of proposals"}
          </label>
          <select
            value={proposalCount}
            onChange={(e) => setProposalCount(e.target.value)}
            disabled={busy}
            style={{
              width: "100%",
              border: "1px solid #bfdbfe",
              borderRadius: 14,
              padding: "10px 12px",
              background: "#ffffff",
              color: "#0f172a",
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            <option value="1">1</option>
            <option value="3">3</option>
            <option value="5">5</option>
          </select>

          <label
            style={{
              display: "block",
              color: "#0f172a",
              fontWeight: 800,
              marginBottom: 6,
            }}
          >
            {isFR ? "Orientation optionnelle" : "Optional direction"}
          </label>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            disabled={busy}
            rows={3}
            maxLength={1200}
            placeholder={
              isFR
                ? "Exemple : proposer une scène douce avec Ugo et une leçon sur la patience."
                : "Example: suggest a gentle scene with Ugo and a lesson about patience."
            }
            style={{
              width: "100%",
              border: "1px solid #bfdbfe",
              borderRadius: 14,
              padding: "10px 12px",
              background: "#ffffff",
              color: "#0f172a",
              resize: "vertical",
              marginBottom: 12,
            }}
          />

          <button
            type="button"
            className="ns-button ws-ai-major-action"
            onClick={handleRequestSceneProposals}
            disabled={!canRequest}
            style={{
              width: "100%",
              justifyContent: "center",
              background: "#facc15",
              borderColor: "#eab308",
              color: "#0f172a",
              fontWeight: 900,
              letterSpacing: "0.01em",
              boxShadow: "0 12px 28px rgba(234, 179, 8, 0.28)",
            }}
          >
            {busy
              ? isFR
                ? "Préparation…"
                : "Preparing…"
              : isFR
              ? "Demander une proposition IA"
              : "Request AI Scene Proposal"}
          </button>

          {proposals.length ? (
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={handleClearProposals}
              disabled={busy}
              style={{
                width: "100%",
                justifyContent: "center",
                marginTop: 10,
                borderColor: "#bfdbfe",
                color: "#1d4ed8",
                fontWeight: 800,
              }}
            >
              {isFR ? "Effacer les propositions IA" : "Clear AI proposals"}
            </button>
          ) : null}

          {disabledReason ? (
            <p style={{ margin: "10px 0 0", color: "#64748b", fontSize: 13 }}>
              {disabledReason}
            </p>
          ) : null}

          {error ? (
            <p style={{ margin: "10px 0 0", color: "#b91c1c", fontWeight: 700, fontSize: 13 }}>
              {error}
            </p>
          ) : null}
        </div>
      </div>

      {proposals.length ? (
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
          }}
          aria-live="polite"
        >
          {proposals.map((proposal, index) => (
            <article
              key={`${proposal?.title || "proposal"}-${index}`}
              style={{
                border:
                  selectedIndex === index
                    ? "2px solid #1d4ed8"
                    : "1px solid #dbeafe",
                borderRadius: 18,
                background: selectedIndex === index ? "#eff6ff" : "#ffffff",
                padding: 16,
                boxShadow:
                  selectedIndex === index
                    ? "0 14px 30px rgba(30, 64, 175, 0.14)"
                    : "0 10px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  borderRadius: 999,
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  fontWeight: 800,
                  fontSize: 12,
                  padding: "5px 9px",
                  marginBottom: 8,
                }}
              >
                {isFR ? `Proposition ${index + 1}` : `Proposal ${index + 1}`}
              </div>

              <h4 style={{ margin: "0 0 8px", color: "#0f172a" }}>
                {proposal?.title || (isFR ? "Proposition sans titre" : "Possible Scene")}
              </h4>

              {proposal?.sceneDraft ? (
                <div style={{ margin: "0 0 12px" }}>
                  <p style={{ margin: "0 0 6px", color: "#1e3a8a", fontWeight: 800, fontSize: 13 }}>
                    {isFR ? "Scène proposée" : "Proposed scene"}
                  </p>
                  <p style={{ margin: 0, color: "#334155", lineHeight: 1.58, whiteSpace: "pre-line" }}>
                    {proposal.sceneDraft}
                  </p>
                </div>
              ) : (
                <p style={{ margin: "0 0 10px", color: "#334155", lineHeight: 1.5 }}>
                  {proposal?.summary || ""}
                </p>
              )}

              {[
                [isFR ? "Orientation brève" : "Brief direction", proposal?.summary],
                [isFR ? "Résumé de l’action" : "Action summary", proposal?.mainEvent],
                [isFR ? "Personnages" : "Characters", Array.isArray(proposal?.charactersInvolved) ? proposal.charactersInvolved.join(", ") : ""],
                [isFR ? "Direction émotionnelle" : "Emotional direction", proposal?.emotionalDirection],
                [isFR ? "Continuité" : "Continuity notes", proposal?.continuityNotes],
                [isFR ? "Valeur ou leçon" : "Value or lesson", proposal?.valueOrLesson],
                [isFR ? "Risque à surveiller" : "Risk warning", proposal?.riskWarning],
              ]
                .filter(([, value]) => String(value || "").trim())
                .map(([label, value]) => (
                  <p
                    key={label}
                    style={{
                      margin: "8px 0 0",
                      color: "#475569",
                      fontSize: 13,
                      lineHeight: 1.45,
                    }}
                  >
                    <strong style={{ color: "#1e3a8a" }}>{label}:</strong> {value}
                  </p>
                ))}
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => handleSelectProposal(proposal, index)}
                style={{
                  marginTop: 12,
                  width: "100%",
                  justifyContent: "center",
                  borderColor: selectedIndex === index ? "#1d4ed8" : "#bfdbfe",
                  color: "#1d4ed8",
                  fontWeight: 800,
                }}
              >
                {selectedIndex === index
                  ? isFR
                    ? "Proposition sélectionnée"
                    : "Selected proposal"
                  : isFR
                  ? "Sélectionner, modifier ou utiliser"
                  : "Select, edit, or use"}
              </button>
            </article>
          ))}
        </div>
      ) : null}

      {showAiDraftModal ? (
        <ModalShell
          title={isFR ? "Brouillon de proposition IA" : "AI Proposal Draft"}
          subtitle={
            isFR
              ? "Modifier, sauvegarder ou copier dans un brouillon humain. Rien ne devient canon automatiquement."
              : "Edit, save, or copy into a human draft. Nothing becomes canon automatically."
          }
          onClose={() => setShowAiDraftModal(false)}
          maxWidth={920}
          tone="ai"
        >
          <div className="ns-card ns-card-soft" style={{ marginBottom: 12 }}>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {isFR
                ? "Ce texte est une proposition consultative, pas une consigne. L’auteur garde la décision finale."
                : "This text is an advisory proposal, not an instruction. The author keeps final control."}
            </p>
            {aiDraftStatus ? (
              <p className="ns-card-meta" style={{ marginTop: 8, color: "#1d4ed8", fontWeight: 800 }}>
                {aiDraftStatus}
              </p>
            ) : null}
          </div>

          <div className="ns-field">
            <label>{isFR ? "Titre du brouillon IA" : "AI draft title"}</label>
            <input
              style={INPUT_STYLE}
              value={editableTitle}
              onChange={(e) => setEditableTitle(e.target.value)}
              maxLength={160}
              placeholder={isFR ? "Titre de la proposition" : "Proposal title"}
            />
          </div>

          <div className="ns-field" style={{ marginTop: 12 }}>
            <label>{isFR ? "Texte du brouillon IA" : "AI draft text"}</label>
            <textarea
              style={{ ...TEXTAREA_STYLE, minHeight: 260 }}
              value={editableDraft}
              onChange={(e) => setEditableDraft(e.target.value)}
              maxLength={5000}
              placeholder={
                isFR
                  ? "Modifiez cette proposition avant de l’utiliser comme inspiration ou brouillon humain."
                  : "Edit this proposal before using it as inspiration or a human draft."
              }
            />
          </div>

          <div className="ns-card ns-card-soft" style={{ marginTop: 12, background: "#f8fafc" }}>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {isFR
                ? "Sauvegarder ici conserve seulement un brouillon IA local. Copier dans le brouillon humain ne soumet rien automatiquement et ne décide pas le canon."
                : "Saving here keeps only a local AI draft. Copying into the human draft does not submit anything automatically and does not decide canon."}
            </p>
          </div>

          {aiDraftStatus ? (
            <p
              className="ns-card-meta"
              style={{
                marginTop: 12,
                marginBottom: 0,
                color: "#1d4ed8",
                fontWeight: 800,
              }}
            >
              {aiDraftStatus}
            </p>
          ) : null}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={handleClearAiDraft}
            >
              {isFR ? "Effacer le brouillon IA" : "Clear AI draft"}
            </button>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => setShowAiDraftModal(false)}
              >
                {isFR ? "Fermer" : "Close"}
              </button>

              <button
                type="button"
                className="ns-button ns-button-outline ws-ai-secondary-action"
                onClick={handleSaveAiDraft}
                disabled={!String(editableDraft || "").trim()}
              >
                {isFR ? "Sauvegarder comme brouillon IA" : "Save as AI draft"}
              </button>

              <button
                type="button"
                className="ns-button ws-ai-major-action"
                onClick={handleAdoptSelectedProposal}
                disabled={!String(editableDraft || "").trim()}
                style={{
                  background: "#facc15",
                  borderColor: "#eab308",
                  color: "#0f172a",
                  fontWeight: 900,
                }}
              >
                {isFR ? "Utiliser comme brouillon humain" : "Use as human draft"}
              </button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {result?.groundingSummary ? (
        <p style={{ margin: proposals.length ? "14px 0 0" : "12px 0 0", color: "#475569", fontSize: 13, fontWeight: 700 }}>
          {result.groundingSummary}
        </p>
      ) : null}

      {result?.notice ? (
        <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 13 }}>
          {result.notice}
        </p>
      ) : null}
    </section>
      </div>
    </details>
  );
}


export default function WriterStudio({
  onNavigatePage,
  projectHubContext = {},
}) {
  const { user, username, isAuthenticated, role, booting } = useUser();

  const lang =
  (typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("lang") ||
      window.localStorage.getItem("ns.lang"))) ||
  "en";
const t = TEXT[lang] || TEXT.en;
  const illustrationPolicyHref = String(lang || "en").toLowerCase().startsWith("fr")
    ? "https://www.nextscenes.org/fr/politique-illustrations-televersements"
    : "https://www.nextscenes.org/illustration-upload-policy";

  const navigate = useNavigate();
  const initialSceneContext =
    readWriterStudioSceneContext();

  // HARD STOP: No auth, no studio.
  if (!isAuthenticated) {
    return (
      <div className="ns-page">
        <div className="ns-card">
          <h1 className="ns-page-title">{t.writerStudioTitle}</h1>
          <p className="ns-page-subtitle">{t.writerStudioLocked}</p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className="ns-button ns-button-primary"
              onClick={() => onNavigatePage && onNavigatePage("login")}
            >
              {t.goToLogin}
            </button>
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() => onNavigatePage && onNavigatePage("storylines")}
            >
              {t.backToStorylines}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (initialSceneContext.demoPreview) {
    const isFR =
      String(lang || "en")
        .toLowerCase()
        .startsWith("fr");

    const chapterTitle =
      initialSceneContext.previewChapterTitle ||
      (isFR ? "Chapitre aperçu" : "Preview Chapter");

    const sceneTitle =
      initialSceneContext.previewSceneTitle ||
      (isFR ? "Scène aperçu" : "Preview Scene");

    return (
      <div
        className="ns-page"
        data-writer-studio-preview-editor="true"
        style={{
          maxWidth: 1120,
          margin: "0 auto",
          paddingTop: 24,
        }}
      >
        <section
          className="ns-card"
          style={{
            padding: 22,
            borderRadius: 20,
            border: "1px solid rgba(37,99,235,0.25)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#2563eb",
            }}
          >
            {isFR
              ? "WRITER STUDIO · APERÇU"
              : "WRITER STUDIO · PREVIEW"}
          </div>

          <h1
            className="ns-page-title"
            style={{ margin: "8px 0 4px" }}
          >
            {sceneTitle}
          </h1>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              fontWeight: 800,
            }}
          >
            {chapterTitle}
          </p>

          <div
            role="status"
            style={{
              marginTop: 16,
              padding: "10px 12px",
              borderRadius: 12,
              background: "#fff7d6",
              border: "1px solid #eab308",
              color: "#713f12",
              fontWeight: 850,
            }}
          >
            {isFR
              ? "Exemple en lecture seule · créez ou ouvrez un vrai projet Roman / Histoire pour écrire et enregistrer."
              : "Read-only sample · create or open a real Novel / Story project to write and save."}
          </div>

          <div
            style={{
              display: "grid",
              gap: 14,
              marginTop: 20,
            }}
          >
            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 800,
              }}
            >
              <span>
                {isFR ? "Titre de la scène" : "Scene title"}
              </span>
              <input
                className="ns-input"
                defaultValue={sceneTitle}
                disabled
                aria-label={
                  isFR ? "Titre de la scène" : "Scene title"
                }
              />
            </label>

            <div>
              <div
                style={{
                  marginBottom: 6,
                  fontWeight: 800,
                }}
              >
                {isFR ? "Texte de la scène" : "Scene text"}
              </div>

              <NovelStorySceneRichEditor
                id={
                  initialSceneContext.sceneId ||
                  "novel-story-preview-scene"
                }
                title={sceneTitle}
                text={
                  initialSceneContext.previewSceneText
                }
                manuscriptContent={null}
                disabled={true}
                lang={lang}
                saveActionLabel={
                  isFR
                    ? "Exemple en lecture seule"
                    : "Read-only Sample"
                }
                onChange={() => {}}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 18,
            }}
          >
            <button
              type="button"
              className="ns-button ns-button-primary"
              disabled
              title={
                isFR
                  ? "L’enregistrement est désactivé dans l’aperçu."
                  : "Saving is disabled in preview mode."
              }
            >
              {isFR
                ? "Exemple en lecture seule"
                : "Read-only Sample"}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() =>
                onNavigatePage?.(
                  "projectHub",
                  {
                    projectCategory: "story",
                  }
                )
              }
            >
              {isFR
                ? "← Retour à Roman / Histoire"
                : "← Back to Novel / Story"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const canParticipate = true;

  const writerStudioSceneContext = useMemo(
    () => initialSceneContext,
    // The Writer Studio page is mounted from a canonical URL.
    // Capture that navigation context once for this mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialWriterStudioStories = useMemo(
    () => loadStoredStories() || [],
    []
  );

  const initialRequestedStoryId =
    String(
      writerStudioSceneContext.storyId || ""
    ).trim();

  const initialRequestedStoryIsCached =
    Boolean(
      initialRequestedStoryId &&
      initialWriterStudioStories.some(
        (story) =>
          String(
            story?._id ||
            story?.id ||
            ""
          ) === initialRequestedStoryId
      )
    );

  const initialSelectedStoryId = (() => {
    if (initialRequestedStoryIsCached) {
      return initialRequestedStoryId;
    }

    let storedId = "";
    try {
      storedId =
        String(
          window.localStorage.getItem(
            "ns.selectedStoryId"
          ) || ""
        ).trim();
    } catch {}

    if (
      storedId &&
      initialWriterStudioStories.some(
        (story) =>
          String(
            story?._id ||
            story?.id ||
            ""
          ) === storedId
      )
    ) {
      return storedId;
    }

    const first =
      initialWriterStudioStories[0];

    return first
      ? String(
          first?._id ||
          first?.id ||
          ""
        )
      : "";
  })();

  const [stories, setStories] = useState(
    () => initialWriterStudioStories
  );
  const [loadingStories, setLoadingStories] =
    useState(
      () =>
        initialWriterStudioStories.length === 0 ||
        (
          Boolean(initialRequestedStoryId) &&
          !initialRequestedStoryIsCached
        )
    );
  const [storiesError, setStoriesError] = useState("");
  const [selectedStoryId, setSelectedStoryId] =
    useState(
      () => initialSelectedStoryId || null
    );
  const [editingCanonSceneId, setEditingCanonSceneId] =
    useState("");
  const [targetChapterId, setTargetChapterId] =
    useState(writerStudioSceneContext.chapterId || "");
  const [sceneContextHandled, setSceneContextHandled] =
    useState(false);
  const [writerStudioStage, setWriterStudioStage] =
    useState("write");
  const [focusedSceneId, setFocusedSceneId] =
    useState(writerStudioSceneContext.sceneId || "");
  const [focusedChapterId, setFocusedChapterId] =
    useState(writerStudioSceneContext.chapterId || "");

  // Search + Filters (UI-only)
  const [storySearch, setStorySearch] = useState("");
  const [filterForum, setFilterForum] = useState("All");
  const [filterMode, setFilterMode] = useState("All");
  const [showCharactersPanel, setShowCharactersPanel] = useState(false);
  const [showStoryInfo, setShowStoryInfo] = useState(false);
  const [showStoryHistory, setShowStoryHistory] = useState(false);
  const [storyInfoSearch, setStoryInfoSearch] = useState("");
  const [storyInfoScope, setStoryInfoScope] = useState("all");
  const [storyHistoryVisibleCount, setStoryHistoryVisibleCount] = useState(STORY_HISTORY_PAGE_SIZE);
  const storyHistoryGatewayRef = useRef(null);
  const storyHistorySectionRef = useRef(null);
  const storyHistoryScrollIntentRef = useRef("");
  const latestCanonFocusAppliedRef = useRef("");

  useEffect(() => {
    if (!showStoryInfo) return;

    const intent = storyHistoryScrollIntentRef.current;
    if (!intent) return;

    storyHistoryScrollIntentRef.current = "";

    const node =
      intent === "open"
        ? storyHistorySectionRef.current
        : storyHistoryGatewayRef.current;

    if (!node || typeof node.scrollIntoView !== "function") return;

    const reveal = () => {
      try {
        node.scrollIntoView({
          behavior: "smooth",
          block: intent === "open" ? "start" : "nearest",
        });
      } catch {
        node.scrollIntoView();
      }
    };

    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(reveal)
      );
    } else if (typeof window !== "undefined") {
      window.setTimeout(reveal, 0);
    }
  }, [showStoryHistory, showStoryInfo]);

  const [canonTitle, setCanonTitle] = useState("");
  const [canonText, setCanonText] = useState("");
  const [canonManuscriptContent, setCanonManuscriptContent] =
    useState(null);
  const [canonIllustrationUrl, setCanonIllustrationUrl] = useState("");
  const [canonIllustrationCaption, setCanonIllustrationCaption] = useState("");
  const [canonIllustrationCredit, setCanonIllustrationCredit] = useState("");
  const [canonIllustrationAltText, setCanonIllustrationAltText] = useState("");
  const [creatingCanon, setCreatingCanon] = useState(false);
  const [canonMessage, setCanonMessage] = useState("");

  const [proposalTitle, setProposalTitle] = useState("");
  const [proposalText, setProposalText] = useState("");
  const [proposalManuscriptContent, setProposalManuscriptContent] =
    useState(null);
  const [proposalIllustrationUrl, setProposalIllustrationUrl] = useState("");
  const [proposalIllustrationCaption, setProposalIllustrationCaption] = useState("");
  const [proposalIllustrationCredit, setProposalIllustrationCredit] = useState("");
  const [proposalIllustrationAltText, setProposalIllustrationAltText] = useState("");
  const [canonIllustrationUploading, setCanonIllustrationUploading] = useState(false);
  const [proposalIllustrationUploading, setProposalIllustrationUploading] = useState(false);
  const [canonIllustrationConfirmed, setCanonIllustrationConfirmed] = useState(false);
  const [proposalIllustrationConfirmed, setProposalIllustrationConfirmed] = useState(false);
  const [illustrationReviewBusy, setIllustrationReviewBusy] = useState({});
  const [illustrationReviewDrafts, setIllustrationReviewDrafts] = useState({});
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [proposalMessage, setProposalMessage] = useState("");

  const [proposalIntent, setProposalIntent] = useState("");
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [showCancelEmptyRoundModal, setShowCancelEmptyRoundModal] = useState(false);
  const [cancelEmptyRoundBusy, setCancelEmptyRoundBusy] = useState(false);
  const [intentDraft, setIntentDraft] = useState("");

  const [comments, setComments] = useState([]);
  const [showAllReaderDiscussions, setShowAllReaderDiscussions] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentMessage, setCommentMessage] = useState("");

  const [highlightCommentId, setHighlightCommentId] = useState("");
  const commentItemRefs = useRef({});
  const highlightTimerRef = useRef(null);

  const [replyOpenFor, setReplyOpenFor] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});

  const [showCanonWindow, setShowCanonWindow] = useState(false);
  const [showDraftWindow, setShowDraftWindow] = useState(false);
  const [fullProposalScene, setFullProposalScene] = useState(null);

  const [showAddCanonModal, setShowAddCanonModal] = useState(
    Boolean(
      writerStudioSceneContext.newScene &&
      writerStudioSceneContext.chapterId
    )
  );
  const [showAddProposalModal, setShowAddProposalModal] = useState(false);

  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decisionDraft, setDecisionDraft] = useState("");
  const [decisionError, setDecisionError] = useState("");

  /* ===============================
     Draft Auto-Save (localStorage)
     - Presentation only (no backend)
     - Per storyId, per window
     =============================== */

  function wsKey(kind, storyId, scope = "") {
    const sid = storyId || selectedStoryId || "none";
    const suffix = String(scope || "").trim();

    return suffix
      ? `nextscenes:writerstudio:draft:${kind}:${sid}:${suffix}`
      : `nextscenes:writerstudio:draft:${kind}:${sid}`;
  }

  function canonDraftScope() {
    const editingId =
      String(editingCanonSceneId || "").trim();

    if (editingId) {
      return `scene:${editingId}`;
    }

    const chapterId =
      String(
        targetChapterId ||
        focusedChapterId ||
        writerStudioSceneContext.chapterId ||
        ""
      ).trim();

    return chapterId
      ? `chapter:${chapterId}:new`
      : "unassigned:new";
  }

  function canonDraftKey(storyId) {
    return wsKey(
      "canon",
      storyId,
      canonDraftScope()
    );
  }

  function lsGet(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function lsSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function lsDel(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {}
  }

  const [canonAutoStatus, setCanonAutoStatus] = useState("");
  const [proposalAutoStatus, setProposalAutoStatus] = useState("");
  const [proposalDraftPresent, setProposalDraftPresent] = useState(false);
  const [proposalDraftJustSaved, setProposalDraftJustSaved] = useState(false);
  const [intentAutoStatus, setIntentAutoStatus] = useState("");
  const [decisionAutoStatus, setDecisionAutoStatus] = useState("");

  const canonAutoTimer = useRef(null);
  const proposalAutoTimer = useRef(null);
  const proposalSavedTimer = useRef(null);
  const intentAutoTimer = useRef(null);
  const decisionAutoTimer = useRef(null);
  // Prevent duplicate canon saves (same title+text) per story
  const lastCanonSavedRef = useRef({});

  // Restore drafts on open (only if fields are empty)
  useEffect(() => {
    if (
      !showAddCanonModal ||
      !selectedStoryId
    ) {
      return;
    }
    const k = canonDraftKey(selectedStoryId);
    const saved = lsGet(k);
    if (saved && (!canonTitle && !canonText && !canonIllustrationUrl)) {
      setCanonTitle(String(saved.title || ""));
      setCanonText(String(saved.text || ""));
      setCanonManuscriptContent(
        saved.manuscriptContent &&
        typeof saved.manuscriptContent === "object" &&
        !Array.isArray(saved.manuscriptContent)
          ? saved.manuscriptContent
          : null
      );
      setCanonIllustrationUrl(String(saved.illustrationUrl || ""));
      setCanonIllustrationCaption(String(saved.illustrationCaption || ""));
      setCanonIllustrationCredit(String(saved.illustrationCredit || ""));
      setCanonIllustrationAltText(String(saved.illustrationAltText || ""));
      setCanonIllustrationConfirmed(false);
      setCanonAutoStatus(t.draftRestored);
      setTimeout(() => setCanonAutoStatus(""), 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddCanonModal, selectedStoryId]);

  useEffect(() => {
    if (!showAddProposalModal) return;
    const k = wsKey("proposal", selectedStoryId);
    const saved = lsGet(k);
    setProposalDraftPresent(!!saved);
    if (saved && (!proposalTitle && !proposalText && !proposalIntent && !proposalIllustrationUrl)) {
      setProposalTitle(String(saved.title || ""));
      setProposalText(String(saved.text || ""));
      setProposalManuscriptContent(
        saved.manuscriptContent &&
        typeof saved.manuscriptContent === "object" &&
        !Array.isArray(saved.manuscriptContent)
          ? saved.manuscriptContent
          : null
      );
      setProposalIntent(String(saved.intent || ""));
      setProposalIllustrationUrl(String(saved.illustrationUrl || ""));
      setProposalIllustrationCaption(String(saved.illustrationCaption || ""));
      setProposalIllustrationCredit(String(saved.illustrationCredit || ""));
      setProposalIllustrationAltText(String(saved.illustrationAltText || ""));
      setProposalIllustrationConfirmed(false);
      setProposalAutoStatus(t.draftRestored);
      setTimeout(() => setProposalAutoStatus(""), 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddProposalModal, selectedStoryId]);

  useEffect(() => {
    if (!showIntentModal) return;
    const k = wsKey("intent", selectedStoryId);
    const saved = lsGet(k);
    if (saved && !intentDraft) {
      setIntentDraft(String(saved.text || ""));
      setIntentAutoStatus(t.draftRestored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIntentModal, selectedStoryId]);

  useEffect(() => {
    if (!showDecisionModal) return;
    const k = wsKey("decision", selectedStoryId);
    const saved = lsGet(k);
    if (saved && !decisionDraft) {
      setDecisionDraft(String(saved.text || ""));
      setDecisionAutoStatus(t.draftRestored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDecisionModal, selectedStoryId]);

  // Debounced saves while typing
  useEffect(() => {
    if (!showAddCanonModal) return;
    if (canonAutoTimer.current) clearTimeout(canonAutoTimer.current);
    canonAutoTimer.current = setTimeout(() => {
      const k = canonDraftKey(selectedStoryId);
      const ok = lsSet(k, {
        title: canonTitle || "",
        text: canonText || "",
        manuscriptContent: canonManuscriptContent || null,
        illustrationUrl: canonIllustrationUrl || "",
        illustrationCaption: canonIllustrationCaption || "",
        illustrationCredit: canonIllustrationCredit || "",
        illustrationAltText: canonIllustrationAltText || "",
        ts: Date.now(),
      });
      setCanonAutoStatus(ok ? t.draftSaved : "");
      if (ok) setTimeout(() => setCanonAutoStatus(""), 1200);
    }, 650);
    return () => {
      if (canonAutoTimer.current) clearTimeout(canonAutoTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canonTitle,
    canonText,
    canonManuscriptContent,
    canonIllustrationUrl,
    canonIllustrationCaption,
    canonIllustrationCredit,
    canonIllustrationAltText,
    showAddCanonModal,
    selectedStoryId,
    editingCanonSceneId,
    targetChapterId,
    focusedChapterId,
  ]);

  useEffect(() => {
    if (!showAddProposalModal) return;
    if (proposalAutoTimer.current) clearTimeout(proposalAutoTimer.current);
    proposalAutoTimer.current = setTimeout(() => {
      const k = wsKey("proposal", selectedStoryId);
      const ok = lsSet(k, {
        title: proposalTitle || "",
        text: proposalText || "",
        manuscriptContent: proposalManuscriptContent || null,
        intent: proposalIntent || "",
        illustrationUrl: proposalIllustrationUrl || "",
        illustrationCaption: proposalIllustrationCaption || "",
        illustrationCredit: proposalIllustrationCredit || "",
        illustrationAltText: proposalIllustrationAltText || "",
        ts: Date.now(),
      });
      setProposalDraftPresent(!!ok);
      if (!proposalDraftJustSaved) {
        setProposalAutoStatus(ok ? t.autosaved : "");
        if (ok) setTimeout(() => setProposalAutoStatus(""), 1200);
      }
    }, 650);
    return () => {
      if (proposalAutoTimer.current) clearTimeout(proposalAutoTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalTitle, proposalText, proposalManuscriptContent, proposalIntent, proposalIllustrationUrl, proposalIllustrationCaption, proposalIllustrationCredit, proposalIllustrationAltText, showAddProposalModal, selectedStoryId, proposalDraftJustSaved]);

  useEffect(() => {
    if (!showIntentModal) return;
    if (intentAutoTimer.current) clearTimeout(intentAutoTimer.current);
    intentAutoTimer.current = setTimeout(() => {
      const k = wsKey("intent", selectedStoryId);
      const ok = lsSet(k, { text: intentDraft || "", ts: Date.now() });
      setIntentAutoStatus(ok ? t.draftSaved : "");
    }, 650);
    return () => {
      if (intentAutoTimer.current) clearTimeout(intentAutoTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentDraft, showIntentModal, selectedStoryId]);

  useEffect(() => {
    if (!showDecisionModal) return;
    if (decisionAutoTimer.current) clearTimeout(decisionAutoTimer.current);
    decisionAutoTimer.current = setTimeout(() => {
      const k = wsKey("decision", selectedStoryId);
      const ok = lsSet(k, { text: decisionDraft || "", ts: Date.now() });
      setDecisionAutoStatus(ok ? t.draftSaved: "");
    }, 650);
    return () => {
      if (decisionAutoTimer.current) clearTimeout(decisionAutoTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionDraft, showDecisionModal, selectedStoryId]);

  function clearDraft(kind) {
    const k =
      kind === "canon"
        ? canonDraftKey(selectedStoryId)
        : wsKey(kind, selectedStoryId);
    lsDel(k);
    if (kind === "proposal") setProposalDraftPresent(false);
    if (kind === "canon") {
      setCanonManuscriptContent(null);
      setCanonIllustrationUrl("");
      setCanonIllustrationCaption("");
      setCanonIllustrationCredit("");
      setCanonIllustrationAltText("");
      setCanonIllustrationConfirmed(false);
    }
    if (kind === "proposal") {
      setProposalManuscriptContent(null);
      setProposalIllustrationUrl("");
      setProposalIllustrationCaption("");
      setProposalIllustrationCredit("");
      setProposalIllustrationAltText("");
      setProposalIllustrationConfirmed(false);
    }
    if (kind === "canon") setCanonAutoStatus(t.draftCleared);
    if (kind === "proposal") setProposalAutoStatus(t.draftCleared);
    if (kind === "intent") setIntentAutoStatus(t.draftCleared);
    if (kind === "decision") setDecisionAutoStatus(t.draftCleared);
    setTimeout(() => {
      if (kind === "canon") setCanonAutoStatus("");
      if (kind === "proposal") setProposalAutoStatus("");
      if (kind === "intent") setIntentAutoStatus("");
      if (kind === "decision") setDecisionAutoStatus("");
    }, 1200);
  }
  const [pendingDecisionScene, setPendingDecisionScene] = useState(null);

  const [showDecisionLog, setShowDecisionLog] = useState(false);
  const [decisionLog, setDecisionLog] = useState([]);

  const [userVotes, setUserVotes] = useState({});
  const [voteTotals, setVoteTotals] = useState({});
  const [voteProposalScene, setVoteProposalScene] = useState(null);
  const [proposalVoteBusy, setProposalVoteBusy] = useState(false);

  const proposalsContainerRef = useRef(null);
  const collaborationProposalShelfRef = useRef(null);
  const storyItemRefs = useRef({});
  const storyStripRef = useRef(null);

  // Feedback sync refs
  const activeStoryIdRef = useRef(null);
  const lastCommentsSigRef = useRef("0||");
  const feedbackPollRef = useRef(null);
  const serverStoriesSignatureRef = useRef("");
  const writerStudioStageRef = useRef("write");

  function replaceStoriesFromServer(serverStories) {
    const list = Array.isArray(serverStories) ? serverStories : [];
    let signature = "";
    try {
      signature = JSON.stringify(list);
    } catch {}

    if (
      signature &&
      serverStoriesSignatureRef.current === signature
    ) {
      return;
    }

    serverStoriesSignatureRef.current = signature;
    setStories(list);
    saveStoredStories(list);
  }

  function setStoryInState(updatedStory) {
    if (!updatedStory) return;
    const uid = String(updatedStory._id || updatedStory.id || "").trim();
    if (!uid) return;

    setStories((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const next = prevList.map((s) => {
        const sid = String(s?._id || s?.id || "").trim();
        return sid === uid ? { ...s, ...updatedStory } : s;
      });

      const found = next.some(
        (s) => String(s?._id || s?.id || "").trim() === uid
      );
      const finalList = found ? next : [updatedStory, ...next];

      saveStoredStories(finalList);
      return finalList;
    });
  }

  async function fetchRemote(
    fallbackStored,
    { silent = false } = {}
  ) {
    try {
      if (!silent) {
        setLoadingStories(true);
      }
      setStoriesError("");
      const data = await api("/api/stories", { method: "GET" });

      if (Array.isArray(data)) {
        // SERVER IS SOURCE OF TRUTH: no merging that can discard proposals.
        replaceStoriesFromServer(data);
      }
    } catch (err) {
      console.error("WriterStudio: error loading stories:", err?.message || err);
      if (!fallbackStored || fallbackStored.length === 0)
        setStoriesError(t.storiesLoadFailed);
      else setStoriesError("");
    } finally {
      if (!silent) {
        setLoadingStories(false);
      }
    }
  }

  useEffect(() => {
    const stored =
      initialWriterStudioStories;

    const canRefreshSilently =
      stored.length > 0 &&
      (
        !initialRequestedStoryId ||
        initialRequestedStoryIsCached
      );

    fetchRemote(
      stored,
      {
        silent:
          canRefreshSilently,
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writerStudioStageRef.current =
      writerStudioStage;
  }, [writerStudioStage]);

  // Live Story refresh is intentionally limited to Collaboration.
  // The focused writing path must remain visually still while the author writes.
  useEffect(() => {
    let refreshInFlight = false;

    const refreshStoriesSilently = async () => {
      if (
        writerStudioStageRef.current !==
        "collaboration"
      ) {
        return;
      }

      if (refreshInFlight) return;

      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }

      refreshInFlight = true;

      try {
        const data =
          await api("/api/stories", {
            method: "GET",
          });

        if (Array.isArray(data)) {
          replaceStoriesFromServer(data);
        }
      } catch (err) {
        console.warn(
          "WriterStudio: silent collaboration refresh failed:",
          err?.message || err
        );
      } finally {
        refreshInFlight = false;
      }
    };

    const handleFocus = () => {
      refreshStoriesSilently();
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState ===
        "visible"
      ) {
        refreshStoriesSilently();
      }
    };

    const handlePageShow = () => {
      refreshStoriesSilently();
    };

    window.addEventListener(
      "focus",
      handleFocus
    );
    window.addEventListener(
      "pageshow",
      handlePageShow
    );
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    const timer =
      window.setInterval(
        refreshStoriesSilently,
        15000
      );

    return () => {
      window.clearInterval(timer);
      window.removeEventListener(
        "focus",
        handleFocus
      );
      window.removeEventListener(
        "pageshow",
        handlePageShow
      );
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
    // Deliberately mounted once. The current Blueprint stage is read via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!stories || stories.length === 0) return;

    setSelectedStoryId((current) => {
      if (current) return current;

      const target = readThreadTargetFromUrlOrStorage();
      if (
        target &&
        stories.some((s) => {
          const id = s._id || s.id;
          return id && String(id) === String(target);
        })
      ) {
        return String(target);
      }

      let storedId = null;
      try {
        storedId = window.localStorage.getItem("ns.selectedStoryId");
      } catch {}

      if (
        storedId &&
        stories.some((s) => {
          const id = s._id || s.id;
          return id && String(id) === String(storedId);
        })
      ) {
        return String(storedId);
      }

      const first = stories[0];
      const firstId = first && (first._id || first.id);
      return firstId ? String(firstId) : current;
    });
  }, [stories]);

  // The old Storyline strip used to auto-scroll whenever the selected
  // Story changed. Writer Studio is now a focused workspace, so selection
  // must never move the page without an explicit user action.

  const activeStory =
    stories.find((s) => {
      const id = s._id || s.id;
      return id && String(id) === String(selectedStoryId);
    }) || null;

  const writingModeLabel = String(
    activeStory?.writingMode || activeStory?.mode || "Solo"
  );
  const writingModeLower = writingModeLabel.trim().toLowerCase();
  const storyModeLabel = String(
    activeStory?.writingMode || activeStory?.mode || ""
  )
    .trim()
    .toLowerCase();
  const isSoloMode = writingModeLower.includes("solo");
  const isCollaborativeMode = writingModeLower.includes("collab");

  const storyOwners = useMemo(() => getStoryOwners(activeStory), [activeStory]);
  const isStoryOwner = useMemo(() => {
  if (!activeStory) return false;
    const owners = storyOwners;
    if (!owners.length) return false;
    return owners.some((o) => identitiesMatch(username, o));
  }, [activeStory, storyOwners, username]);


  // Group Writing (Open/Closed) — derive mode from server (story.groupMode / story.group.mode) first, then fallback to label
  const groupModeFromStory = String(
    activeStory?.groupMode || activeStory?.group?.mode || ""
  )
    .trim()
    .toLowerCase();
  const groupModeLabel =
    groupModeFromStory ||
    (writingModeLower.includes("closed") ? "closed" : writingModeLower.includes("open") ? "open" : "");
  const isGroupStory = writingModeLower.includes("group");
  const isGroupClosed = isGroupStory && groupModeLabel === "closed";
  const isGroupOpen = isGroupStory && !isGroupClosed;

  const activeStoryId = useMemo(
    () => (activeStory ? activeStory._id || activeStory.id : null),
    [activeStory]
  );

  useEffect(() => {
    activeStoryIdRef.current = activeStoryId ? String(activeStoryId) : null;
  }, [activeStoryId]);

  const activeScenes = useMemo(
    () => (Array.isArray(activeStory?.scenes) ? activeStory.scenes : []),
    [activeStory]
  );

  const canonicalScenes = useMemo(() => {
    return activeScenes
      .filter(isCanonical)
      .sort((a, b) => {
        const an = Number(a?.sceneNumber);
        const bn = Number(b?.sceneNumber);

        const aOk = Number.isFinite(an) && an > 0;
        const bOk = Number.isFinite(bn) && bn > 0;

        if (aOk && bOk && an !== bn) return an - bn;
        if (aOk && !bOk) return -1;
        if (!aOk && bOk) return 1;

        const at = new Date(a?.createdAt || 0).getTime();
        const bt = new Date(b?.createdAt || 0).getTime();
        return at - bt;
      });
  }, [activeScenes]);

  const proposedScenes = useMemo(() => {
    return activeScenes
      .filter(isPendingProposal)
      .sort(
        (a, b) =>
          new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime()
      );
  }, [activeScenes]);

  const proposalsOpen = !!activeStory?.proposalsOpen;

  // Governance writer: admin OR story owner
  const isWriter = useMemo(() => {
    if (norm(role) === "admin") return true;
    return isStoryOwner;
  }, [role, isStoryOwner]);
  const canCancelEmptyRound = Boolean(
    isWriter && !isSoloMode && proposalsOpen &&
    activeStory?.viewerAccess?.canCancelEmptyProposalRound === true
  );

  useEffect(() => {
    if (!activeStoryId || isSoloMode) {
      setUserVotes({});
      setVoteTotals({});
      return undefined;
    }

    let cancelled = false;
    let poll = null;

    async function loadProposalVotes() {
      try {
        const data = await api(
          `/api/stories/${encodeURIComponent(
            String(activeStoryId)
          )}/proposal-votes`
        );

        if (cancelled) return;

        setUserVotes(
          data?.myVotes &&
          typeof data.myVotes === "object"
            ? data.myVotes
            : {}
        );

        setVoteTotals(
          data?.totals &&
          typeof data.totals === "object"
            ? data.totals
            : {}
        );
      } catch (err) {
        if (cancelled) return;
        console.warn(
          "Proposal vote state could not be loaded:",
          err?.message || err
        );
      }
    }

    loadProposalVotes();

    if (
      writerStudioStage ===
        "collaboration" &&
      proposalsOpen
    ) {
      poll = window.setInterval(
        loadProposalVotes,
        PROPOSAL_VOTE_POLL_MS
      );
    }

    return () => {
      cancelled = true;
      if (poll) {
        window.clearInterval(
          poll
        );
      }
    };
  }, [
    activeStoryId,
    isSoloMode,
    proposalsOpen,
    proposedScenes.length,
    writerStudioStage,
  ]);

  useEffect(() => {
    if (sceneContextHandled) return;
    if (!activeStoryId || !activeStory) return;

    const requestedStoryId =
      writerStudioSceneContext.storyId;

    if (
      requestedStoryId &&
      String(activeStoryId) !== String(requestedStoryId)
    ) {
      return;
    }

    if (
      writerStudioSceneContext.sceneId
    ) {
      const scene = canonicalScenes.find(
        (item) =>
          getSceneId(item) ===
          writerStudioSceneContext.sceneId
      );

      if (!scene) return;

      // An explicit Chapter/Scene handoff has priority over the generic
      // focus-latest-Canon effect on initial load. Future NEW Canon saves
      // still advance the focus when a genuinely newer Scene exists.
      const latestCanonFromHandoff = getSceneId(canonicalScenes[canonicalScenes.length - 1]);
      if (latestCanonFromHandoff) {
        latestCanonFocusAppliedRef.current = `${String(activeStoryId)}:${latestCanonFromHandoff}`;
      }
      setFocusedSceneId(
        writerStudioSceneContext.sceneId
      );
      setFocusedChapterId(
        writerStudioSceneContext.chapterId || ""
      );
      setWriterStudioStage("write");
      setSceneContextHandled(true);
      return;
    }

    if (
      writerStudioSceneContext.newScene &&
      writerStudioSceneContext.chapterId
    ) {
      const latestCanonForNewChapter = getSceneId(canonicalScenes[canonicalScenes.length - 1]);
      if (latestCanonForNewChapter) {
        latestCanonFocusAppliedRef.current = `${String(activeStoryId)}:${latestCanonForNewChapter}`;
      }
      setEditingCanonSceneId("");

      // A Book Structure "Write First Scene" handoff is a
      // fresh Chapter-scoped draft. Never inherit the legacy
      // story-wide Canon draft that older Writer Studio builds used.
      lsDel(
        wsKey(
          "canon",
          activeStoryId
        )
      );

      setTargetChapterId(
        writerStudioSceneContext.chapterId
      );
      setFocusedChapterId(
        writerStudioSceneContext.chapterId
      );
      setFocusedSceneId("");
      setWriterStudioStage("write");
      setCanonTitle("");
      setCanonText("");
      setCanonManuscriptContent(null);
      setCanonIllustrationUrl("");
      setCanonIllustrationCaption("");
      setCanonIllustrationCredit("");
      setCanonIllustrationAltText("");
      setCanonIllustrationConfirmed(false);
      setCanonMessage(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "Nouvelle scène pour le chapitre sélectionné."
          : "New Scene for the selected Chapter."
      );
      setShowAddCanonModal(true);
      setSceneContextHandled(true);
    }
  }, [
    activeStory,
    activeStoryId,
    canonicalScenes,
    lang,
    sceneContextHandled,
    writerStudioSceneContext,
  ]);

  const isWriterRole = useMemo(() => {
    const r = norm(role);
    return r === "writer" || r === "power" || r === "admin";
  }, [role]);

  const isGroupMember = useMemo(() => {
    if (!isGroupStory) return true;
    if (norm(role) === "admin") return true;
    if (isStoryOwner) return true;

    const userId = String(user?.id || user?._id || "").trim();
    if (!userId) return false;

    const members = Array.isArray(activeStory?.group?.members)
      ? activeStory.group.members
      : [];
    // Match the backend's userId-only group membership authority.
    return members.some((m) => String(m?.userId || "").trim() === userId);
  }, [isGroupStory, role, isStoryOwner, user, activeStory]);

  const canSeeProposals = !isSoloMode && (!isGroupStory || isGroupMember);
  const canCommentInStudio = !isGroupStory || isGroupOpen || isGroupMember;

  // Reader feedback participation rules (what users feel in the UI)
  const commentGateReason = useMemo(() => {
  if (!activeStory) return "";
  if (!isGroupStory) return "";
  if (isGroupClosed && !isGroupMember) {
    return t.writerStudioClosedGroupCommentReplyOnly;
  }
  if (!isGroupMember && !isGroupOpen) {
    return t.writerStudioGroupCommentReplyOnly;
  }
  return "";
}, [activeStory, isGroupStory, isGroupClosed, isGroupMember, isGroupOpen, t]);

  const modeBadge = useMemo(() => {
  if (!activeStory) return null;
  if (proposalsOpen) {
    return {
      title: t.writerStudioProposalRoundOpen,
      subtitle: t.writerStudioProposalRoundOpenDesc,
    };
  }
  return {
    title: t.writerStudioCanonMode,
    subtitle: t.writerStudioCanonModeDesc,
  };
}, [activeStory, proposalsOpen, t]);

  const storyConstitutionRows = useMemo(() => {
  if (!activeStory) return [];

  const ownerLabel = getStoryOwnerLabel(activeStory);
  const modeLabel = deriveModeForDisplay(activeStory);

  let contributionType = t.onlyAuthorWritesCanonScenes;
  let participation = t.externalProposalsDisabled;

  if (isCollaborativeMode) {
    contributionType = t.writerStudioProposalsOpenOnlyWhenAuthorOpens;
    participation = t.writerStudioRegisteredWritersMaySubmit;
  } else if (isGroupOpen) {
    contributionType =
      t.writerStudioGroupOwnerProposalsOpen;
    participation =
      t.writerStudioGroupAcceptedMembersMayContribute;
  } else if (isGroupClosed) {
    contributionType =
      t.writerStudioClosedGroupOwnerProposalsOpen;
    participation =
      t.writerStudioOnlyInvitedGroupMembersMayContribute;
  }

  return [
    { label: t.writerStudioWritingMode, value: modeLabel },
    { label: t.writerStudioStoryOwner, value: ownerLabel },
    { label: t.writerStudioCanonAuthority, value: t.writerStudioStoryOwner },
    { label: t.writerStudioContributionType, value: contributionType },
    {
      label: t.writerStudioCurrentMode,
      value: proposalsOpen ? t.writerStudioProposalRoundOpen : t.writerStudioCanonMode,
    },
    { label: t.writerStudioParticipation, value: participation },
    {
      label: t.writerStudioCanonProtection,
      value: proposalsOpen
  ? t.writerStudioCanonLockedUntilDecision
  : t.writerStudioApprovedScenesPermanent,
    },
  ];
}, [activeStory, isCollaborativeMode, isGroupClosed, isGroupOpen, proposalsOpen, t]);

  const storyHistoryEntries = useMemo(() => {
  return buildStoryHistoryEntries({
    t,
    story: activeStory,
    canonicalScenes,
    decisionLog,
    proposalsOpen,
    proposedScenes,
  });
}, [activeStory, canonicalScenes, decisionLog, proposalsOpen, proposedScenes, t]);

const storyHistoryStats = useMemo(() => {
  const rows = Array.isArray(storyHistoryEntries) ? storyHistoryEntries : [];
  const contributorSet = new Set();

  rows.forEach((entry) => {
    const explicitBy = String(entry?.by || "").trim();
    if (explicitBy) contributorSet.add(explicitBy.toLowerCase());

    const detail = String(entry?.detail || "");
    const byLabel = String(t.by || "By").trim();
    const escapedBy = byLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const matches = detail.match(new RegExp(`${escapedBy}\\s+([^·]+)`, "gi")) || [];

    matches.forEach((match) => {
      const cleaned = String(match || "")
        .replace(new RegExp(`^${escapedBy}\\s+`, "i"), "")
        .trim();
      if (cleaned) contributorSet.add(cleaned.toLowerCase());
    });
  });

  return {
    total: rows.length,
    canon: rows.filter((entry) => String(entry?.type || "") === "canon").length,
    proposal: rows.filter((entry) => String(entry?.type || "") === "proposal").length,
    decision: rows.filter((entry) => String(entry?.type || "") === "decision").length,
    contributors: contributorSet.size,
  };
}, [storyHistoryEntries, t]);

  const filteredStoryHistoryEntries = useMemo(() => {
    const q = String(storyInfoSearch || "").trim().toLowerCase();
    const scope = String(storyInfoScope || "all").trim().toLowerCase();
    const rows = Array.isArray(storyHistoryEntries) ? storyHistoryEntries : [];

    return rows.filter((entry) => {
      const type = String(entry?.type || "history").toLowerCase();
      const scopeOk =
        scope === "all" ||
        (scope === "canon" && type === "canon") ||
        (scope === "proposal" && type === "proposal") ||
        (scope === "decision" && type === "decision") ||
        (scope === "history" && type !== "constitution");

      if (!scopeOk) return false;
      if (!q) return true;

      const hay = `${entry?.action || ""} ${entry?.detail || ""} ${entry?.at || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [storyHistoryEntries, storyInfoSearch, storyInfoScope]);

  const visibleStoryHistoryEntries = useMemo(() => {
    const rows = Array.isArray(filteredStoryHistoryEntries) ? filteredStoryHistoryEntries : [];
    return rows.slice(0, Math.min(storyHistoryVisibleCount, STORY_HISTORY_MAX_RENDER));
  }, [filteredStoryHistoryEntries, storyHistoryVisibleCount]);

  const hiddenStoryHistoryCount = Math.max(
    0,
    filteredStoryHistoryEntries.length - visibleStoryHistoryEntries.length
  );

  useEffect(() => {
    setStoryHistoryVisibleCount(STORY_HISTORY_PAGE_SIZE);
  }, [activeStoryId, storyInfoSearch, storyInfoScope, showStoryInfo]);

  // Server feedback: fetch latest for this story
  async function fetchCommentsFromServer(storyId) {
    if (!storyId) return [];
    const qs = new URLSearchParams();
    // Reuse the same authorised storyline response channel as Reader View.
    // Older Home and newer Writer Studio feedback share one conversation.
    const combined = [];
    for (let page = 1; page <= 10; page += 1) {
      const data = await api(
        `/api/feedback/story/${encodeURIComponent(storyId)}?page=${page}&limit=100`,
        { method: "GET" }
      );
      const batch = Array.isArray(data?.items) ? data.items : [];
      combined.push(...batch);
      if (!data?.hasMore || batch.length < 100) break;
    }
    return mapFeedbackItemsToComments(combined);
  }

  async function refreshCommentsFromServer(storyId, opts) {
    const silent = !!opts?.silent;
    if (!storyId) return;

    try {
      const next = await fetchCommentsFromServer(storyId);

      // Ignore if user switched story mid-flight
      if (
        activeStoryIdRef.current &&
        String(activeStoryIdRef.current) !== String(storyId)
      )
        return;

      const sig = commentsSignature(next);
      if (sig !== lastCommentsSigRef.current) {
        lastCommentsSigRef.current = sig;
        setComments(next);
        saveComments(storyId, next); // cache
      } else if (!silent) {
        saveComments(storyId, next);
      }
    } catch (e) {
      if (!silent) {
        console.error("WriterStudio: feedback refresh failed:", e?.message || e);
      }
    }
  }

  useEffect(() => {
    const storyId = activeStoryId;
    setShowAllReaderDiscussions(false);

    // Stop old poller
    if (feedbackPollRef.current) {
      clearInterval(feedbackPollRef.current);
      feedbackPollRef.current = null;
    }

    if (!storyId) {
      setComments([]);
      setReplyOpenFor(null);
      setReplyDrafts({});
      setProposalIntent("");
      setIntentDraft("");
      setShowIntentModal(false);
      setShowDecisionModal(false);
      setPendingDecisionScene(null);
      setDecisionDraft("");
      setDecisionError("");
      setShowDecisionLog(false);
      setDecisionLog([]);
      setShowAddCanonModal(false);
      setShowAddProposalModal(false);
      setShowStoryInfo(false);
      setStoryInfoSearch("");
      setStoryInfoScope("all");
      setStoryHistoryVisibleCount(STORY_HISTORY_PAGE_SIZE);

      setHighlightCommentId("");
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }

      lastCommentsSigRef.current = "0||";
      return;
    }

    // Fast UI: show cache immediately, then server-authoritative refresh
    const cached = loadComments(storyId);
    setComments(cached);
    lastCommentsSigRef.current = commentsSignature(cached);

    setCommentText("");
    setCommentMessage("");
    setReplyOpenFor(null);
    setReplyDrafts({});

    const storedIntent = readIntentFromStorage(storyId);
    setProposalIntent(storedIntent);
    setIntentDraft(storedIntent);
    setShowIntentModal(false);

    const log = loadDecisionLog(storyId);
    setDecisionLog(log);
    setShowDecisionLog(false);

    setShowDecisionModal(false);
    setPendingDecisionScene(null);
    setDecisionDraft("");
    setDecisionError("");

    const preserveBookStructureNewScene =
      Boolean(
        writerStudioSceneContext.newScene &&
        writerStudioSceneContext.chapterId &&
        (
          !writerStudioSceneContext.storyId ||
          String(
            writerStudioSceneContext.storyId
          ) === String(storyId)
        )
      );

    if (!preserveBookStructureNewScene) {
      setShowAddCanonModal(false);
    }
    setShowAddProposalModal(false);

    setHighlightCommentId("");
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }

    // Immediate server refresh (authoritative)
    refreshCommentsFromServer(storyId, { silent: true });

    // Poll server for cross-device sync
    feedbackPollRef.current = setInterval(() => {
      const sid = activeStoryIdRef.current;
      if (sid) refreshCommentsFromServer(sid, { silent: true });
    }, FEEDBACK_POLL_MS);

    return () => {
      if (feedbackPollRef.current) {
        clearInterval(feedbackPollRef.current);
        feedbackPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStoryId]);

  // Same-device sync (multi-tab): mirror cache writes across tabs quickly.
  useEffect(() => {
    const storyId = activeStoryId;
    if (!storyId) return;

    function onStorage(e) {
      try {
        if (!e) return;
        if (String(e.key || "") !== commentsKey(storyId)) return;
        const next = loadComments(storyId);
        const sig = commentsSignature(next);
        if (sig !== lastCommentsSigRef.current) {
          lastCommentsSigRef.current = sig;
          setComments(next);
        }
      } catch {}
    }

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [activeStoryId]);

  useEffect(() => {
    if (!activeStoryId) return;
    if (!comments || comments.length === 0) return;

    const targetId = readCommentTargetFromUrlOrStorage();
    if (!targetId) return;

    const node = commentItemRefs.current[String(targetId)];
    if (node && typeof node.scrollIntoView === "function") {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {}
      setHighlightCommentId(String(targetId));

      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => {
        setHighlightCommentId("");
        highlightTimerRef.current = null;
      }, 2600);
    }

    clearCommentTargetInStorage();
  }, [activeStoryId, comments]);

  // Reader Feedback remains reader-led.
  // Eligible participants may start feedback threads.
  // The Storyline owner may reply to feedback on their own work.
  const hasFeedbackIdentity = Boolean(
    username &&
      username.trim()
  );

  const canPostComment = Boolean(
    activeStory &&
      canCommentInStudio &&
      canParticipate &&
      hasFeedbackIdentity &&
      !isWriter
  );

  const canReply = Boolean(
    activeStory &&
      canCommentInStudio &&
      canParticipate &&
      hasFeedbackIdentity &&
      (!isWriter || isStoryOwner)
  );

  const commentBlockNote = useMemo(() => {
    if (!activeStory) return "";
    if (isStoryOwner) {
      return t.writerMayReplyToReaderFeedback ||
        "You may reply to reader comments on your Storyline.";
    }
    if (commentGateReason) return commentGateReason;
    if (!username || !username.trim()) return t.commentUsernameRequired;
    return "";
  }, [
    activeStory,
    isStoryOwner,
    commentGateReason,
    username,
    t,
  ]);

  // Threaded view (flat storage -> threaded render)
  const threadedComments = useMemo(() => {
    const list = Array.isArray(comments) ? comments : [];
    const parents = [];
    const repliesByParent = {};

    list.forEach((x) => {
      const id = String(x?.id || "").trim();
      if (!id) return;
      const parentId = String(x?.parentId || "").trim();
      if (!parentId) parents.push(x);
      else {
        if (!repliesByParent[parentId]) repliesByParent[parentId] = [];
        repliesByParent[parentId].push(x);
      }
    });

    parents.sort(
      (a, b) =>
        new Date(b?.createdAt || 0).getTime() -
        new Date(a?.createdAt || 0).getTime()
    );
    Object.keys(repliesByParent).forEach((pid) => {
      repliesByParent[pid].sort(
        (a, b) =>
          new Date(a?.createdAt || 0).getTime() -
          new Date(b?.createdAt || 0).getTime()
      );
    });

    return { parents, repliesByParent };
  }, [comments]);

  // Active comment highlight: always target the most recent *item* (parent or reply)
  const mostRecentCommentId = useMemo(() => {
    if (!Array.isArray(comments) || comments.length === 0) return "";
    let bestId = "";
    let bestTs = -1;

    for (const c of comments) {
      const ts = new Date(c?.createdAt || 0).getTime();
      const safeTs = Number.isFinite(ts) ? ts : 0;
      if (safeTs > bestTs) {
        bestTs = safeTs;
        bestId = String(c.id || "");
      }
    }
    return bestId;
  }, [comments]);

  useEffect(() => {
    if (!mostRecentCommentId) return;
    setHighlightCommentId(mostRecentCommentId);
  }, [mostRecentCommentId]);

  const ownPendingProposal = useMemo(
    () =>
      proposedScenes.find((scene) => {
        const meRaw =
          String(
            username || ""
          ).trim();

        return Boolean(
          meRaw &&
          getSceneAuthors(
            scene
          ).some((value) =>
            identitiesMatch(
              value,
              meRaw
            )
          )
        );
      }) ||
      null,
    [
      proposedScenes,
      username,
    ]
  );

  const hasOwnPendingProposal =
    Boolean(
      ownPendingProposal
    );

  const hasOwnProposalThisRound =
    Boolean(
      hasOwnPendingProposal ||
      activeStory
        ?.viewerAccess
        ?.proposalSubmittedThisRound ===
        true
    );

  const isContributorWorkspace =
    Boolean(
      activeStory &&
      !isWriter &&
      !isSoloMode &&
      (
        isCollaborativeMode
          ? isWriterRole
          : (
              isGroupStory &&
              isGroupMember
            )
      )
    );

  // A contributor gets exactly one proposal in each Proposal Round.
  // Rejecting/removing that proposal does not reopen submission eligibility;
  // eligibility resets only when the Owner opens the next Proposal Round.
  // The Owner remains the only Canon authority.
  const canPropose = Boolean(
    activeStory &&
      canParticipate &&
      proposalsOpen &&
      !isWriter &&
      !hasOwnProposalThisRound &&
      !storyModeLabel.includes("solo") &&
      (isGroupStory ? isGroupMember : isWriterRole)
  );

  useEffect(() => {
    if (!isContributorWorkspace) {
      return;
    }

    if (
      writerStudioStage !==
        "collaboration" &&
      writerStudioStage !==
        "memory" &&
      writerStudioStage !==
        "reader"
    ) {
      setWriterStudioStage(
        "collaboration"
      );
    }
  }, [
    isContributorWorkspace,
    writerStudioStage,
  ]);

  useEffect(() => {
    if (
      showAddProposalModal &&
      hasOwnProposalThisRound
    ) {
      setShowAddProposalModal(false);
    }
  }, [
    showAddProposalModal,
    hasOwnProposalThisRound,
  ]);

  useEffect(() => {
    if (
      isWriter &&
      isCollaborativeMode &&
      proposalsOpen &&
      writerStudioStage ===
        "write"
    ) {
      setWriterStudioStage(
        "collaboration"
      );
    }
  }, [
    isWriter,
    isCollaborativeMode,
    proposalsOpen,
    writerStudioStage,
  ]);

  function persistComments(next) {
    const storyId = activeStory && (activeStory._id || activeStory.id);
    if (!storyId) return;
    const normalized = normalizeComments(next);
    setComments(normalized);
    saveComments(storyId, normalized);
    lastCommentsSigRef.current = commentsSignature(normalized);
  }

  async function postFeedbackToServer(story, text, parentId) {
    if (!story) return false;
    const storyId = story._id || story.id;
    if (!storyId) return false;

    const payload = {
      storyId: String(storyId),
      storyTitle: String(story.title || "").slice(0, 200),
      forum: String(story.forum || "").slice(0, 120),
      text: String(text || "").slice(0, 2000),
      source: FEEDBACK_SOURCE,
      target: FEEDBACK_TARGET,
      parentId: parentId ? String(parentId).slice(0, 80) : "",
      author: username && username.trim() ? username.trim() : getUiText().anonymous,
    };

    await api("/api/feedback", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    return true;
  }

  async function handlePostComment(e) {
    e.preventDefault();
    setCommentMessage("");

    if (!activeStory) return;

    if (!canPostComment) {
      setCommentMessage(
        isStoryOwner
          ? (
              t.writerMayReplyToReaderFeedback ||
              "You may reply to reader comments, but Reader Feedback threads are started by readers."
            )
          : commentGateReason
          ? commentGateReason
          : t.commentUsernameRequiredReply
      );
      return;
    }

    if (!commentText.trim()) {
      setCommentMessage(t.commentEmpty);
      return;
    }

    const text = commentText.trim();

    try {
      await postFeedbackToServer(activeStory, text, "");
      setCommentText("");
      setCommentMessage(t.commentPosted);

      const sid = activeStory._id || activeStory.id;
      if (sid) {
        await refreshCommentsFromServer(String(sid), { silent: true });
      }
    } catch (err) {
      console.error("Post comment error:", err?.message || err);
      setCommentMessage(t.commentFailed);
    }
  }

  function toggleReplyBox(commentId) {
    if (!commentId) return;
    setReplyOpenFor((cur) => (cur === commentId ? null : commentId));
  }

  async function handlePostReply(e, commentId) {
    e.preventDefault();
    setCommentMessage("");

    if (!activeStory) return;

    if (!canReply) {
      setCommentMessage(
        commentGateReason
          ? commentGateReason
          : t.replyUsernameRequired
      );
      return;
    }

    const draft = (replyDrafts[commentId] || "").trim();
    if (!draft) {
      setCommentMessage(t.replyEmpty);
      return;
    }

    try {
      await postFeedbackToServer(activeStory, draft, String(commentId));

      setReplyDrafts((prev) => ({ ...prev, [commentId]: "" }));
      setReplyOpenFor(null);
      setCommentMessage(t.replyPosted)

      const sid = activeStory._id || activeStory.id;
      if (sid) {
        await refreshCommentsFromServer(String(sid), { silent: true });
      }
    } catch (err) {
      console.error("Post reply error:", err?.message || err);
      setCommentMessage(t.replyFailed);
    }
  }

  function handleSelectStory(story) {
    const id = story._id || story.id;
    const sid = id ? String(id) : null;
    setSelectedStoryId(sid);

    if (sid) {
      try {
        window.localStorage.setItem("ns.selectedStoryId", sid);
        window.localStorage.setItem("ns.writerStudio.focusStoryId", sid);
      } catch {}
    }

    setCanonMessage("");
    setProposalMessage("");
    setFullProposalScene(null);
    setVoteProposalScene(null);
    setShowIntentModal(false);

    setShowDecisionModal(false);
    setPendingDecisionScene(null);
    setDecisionDraft("");
    setDecisionError("");

    setShowDecisionLog(false);

    setShowAddCanonModal(false);
    setShowAddProposalModal(false);
    setShowStoryInfo(false);
    setStoryInfoSearch("");
    setStoryInfoScope("all");
    setStoryHistoryVisibleCount(STORY_HISTORY_PAGE_SIZE);
    setHighlightCommentId("");
  }


  // NEXTSCENES_WRITER_STUDIO_MOBILE_FOCUS_V2
  function openWriterRoom(story) {
    handleSelectStory(story);

    const scrollToRoom = () => {
      const node = document.getElementById("writer-studio-active-room");
      if (!node || typeof node.scrollIntoView !== "function") return;

      try {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        node.scrollIntoView();
      }

      try {
        node.focus({ preventScroll: true });
      } catch {
        // Focus support varies across older mobile browsers.
      }
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => window.requestAnimationFrame(scrollToRoom));
    } else {
      window.setTimeout(scrollToRoom, 0);
    }
  }

  function sceneIllustrationUploadLabel() {
    return String(lang || "en").toLowerCase().startsWith("fr")
      ? "Téléverser une illustration"
      : "Upload illustration";
  }

  function sceneIllustrationUploadingLabel() {
    return String(lang || "en").toLowerCase().startsWith("fr")
      ? "Téléversement…"
      : "Uploading…";
  }

  function sceneIllustrationUploadSuccessLabel() {
    return String(lang || "en").toLowerCase().startsWith("fr")
      ? "Illustration téléversée. L’URL a été ajoutée pour vérification."
      : "Illustration uploaded. The URL has been added for review.";
  }

  function sceneIllustrationUploadFailedLabel() {
    return String(lang || "en").toLowerCase().startsWith("fr")
      ? "Le téléversement de l’illustration a échoué. Veuillez réessayer."
      : "Illustration upload failed. Please try again.";
  }

  function sceneIllustrationUploadHint() {
    return String(lang || "en").toLowerCase().startsWith("fr")
      ? "JPG, PNG ou WEBP. Maximum 5 MB. L’image reste en attente jusqu’à approbation."
      : "JPG, PNG, or WEBP. Maximum 5 MB. The image remains pending until approved.";
  }

  async function handleSceneIllustrationUpload(kind, file) {
    if (!file) return;

    const isCanon = kind === "canon";
    const canUpload = isCanon ? isWriter : canPropose;
    if (!canUpload) return;

    const setUploading = isCanon ? setCanonIllustrationUploading : setProposalIllustrationUploading;
    const setMessage = isCanon ? setCanonMessage : setProposalMessage;
    const setUrl = isCanon ? setCanonIllustrationUrl : setProposalIllustrationUrl;
    const setAlt = isCanon ? setCanonIllustrationAltText : setProposalIllustrationAltText;
    const currentAlt = isCanon ? canonIllustrationAltText : proposalIllustrationAltText;

    setUploading(true);
    setMessage("");

    try {
      if (typeof api.uploadSceneIllustration !== "function") {
        throw new Error("Scene illustration upload is not available yet.");
      }

      const res = await api.uploadSceneIllustration(file);
      const uploadedUrl = String(res?.url || res?.publicUrl || "").trim();
      if (!uploadedUrl) {
        throw new Error("No illustration URL returned by the server.");
      }

      setUrl(uploadedUrl);
      if (isCanon) setCanonIllustrationConfirmed(false);
      else setProposalIllustrationConfirmed(false);
      if (!String(currentAlt || "").trim()) {
        setAlt(String(file?.name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim());
      }
      setMessage(sceneIllustrationUploadSuccessLabel());
    } catch (err) {
      console.error("Scene illustration upload error:", err?.message || err);
      setMessage(getApiErrorMessage(err, sceneIllustrationUploadFailedLabel()));
    } finally {
      setUploading(false);
    }
  }

  function sceneReviewKey(scene) {
    return String(getSceneId(scene) || scene?.createdAt || "").trim();
  }

  function sceneHasIllustration(scene) {
    return !!String(scene?.illustrationUrl || "").trim();
  }

  function sceneIllustrationStatus(scene) {
    return String(scene?.illustrationStatus || "").trim().toLowerCase() || "pending";
  }

  function getIllustrationReviewDraft(scene) {
    const key = sceneReviewKey(scene);
    const saved = key ? illustrationReviewDrafts[key] : null;
    return {
      reason: String(saved?.reason || scene?.illustrationReviewReason || ""),
      note: String(saved?.note || scene?.illustrationReviewNote || ""),
    };
  }

  function updateIllustrationReviewDraft(scene, patch) {
    const key = sceneReviewKey(scene);
    if (!key) return;
    setIllustrationReviewDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev?.[key] || {}), ...(patch || {}) },
    }));
  }

  function canManageSceneIllustration(scene) {
    return !!activeStoryId && !!sceneReviewKey(scene) && isWriter && sceneHasIllustration(scene);
  }

  async function handleSceneIllustrationDecision(scene, action) {
    if (!canManageSceneIllustration(scene)) return;

    const sceneId = sceneReviewKey(scene);
    const storyId = String(activeStoryId || "").trim();
    const draft = getIllustrationReviewDraft(scene);
    const reviewAction = String(action || "").trim().toLowerCase();

    if (reviewAction === "reject" && !String(draft.reason || "").trim()) {
      setProposalMessage(t.sceneIllustrationRejectionReasonRequired || "Please choose a rejection reason.");
      return;
    }

    setIllustrationReviewBusy((prev) => ({ ...prev, [sceneId]: true }));
    setProposalMessage("");
    setCanonMessage("");

    try {
      let res = null;
      if (reviewAction === "approve") {
        res = await api.approveSceneIllustration(storyId, sceneId);
      } else if (reviewAction === "reject") {
        res = await api.rejectSceneIllustration(storyId, sceneId, {
          reason: draft.reason,
          note: draft.note,
        });
      } else if (reviewAction === "clear") {
        res = await api.clearSceneIllustration(storyId, sceneId);
      } else {
        return;
      }

      if (res?.story) setStoryInState(res.story);
      setProposalMessage(t.sceneIllustrationReviewSaved || "Illustration review saved.");
    } catch (err) {
      console.error("Scene illustration review error:", err?.message || err);
      setProposalMessage(getApiErrorMessage(err, t.sceneIllustrationReviewFailed || "Illustration review failed. Please try again."));
    } finally {
      setIllustrationReviewBusy((prev) => ({ ...prev, [sceneId]: false }));
    }
  }


  async function handleReplaceExistingSceneIllustration(scene, file) {
    if (!file || !canManageSceneIllustration(scene)) return;

    const sceneId = sceneReviewKey(scene);
    const storyId = String(activeStoryId || "").trim();

    setIllustrationReviewBusy((prev) => ({ ...prev, [sceneId]: true }));
    setProposalMessage("");
    setCanonMessage("");

    try {
      if (typeof api.uploadSceneIllustration !== "function") {
        throw new Error("Scene illustration upload is not available yet.");
      }

      const uploaded = await api.uploadSceneIllustration(file);
      const illustrationUrl = String(uploaded?.url || uploaded?.publicUrl || uploaded?.imageUrl || "").trim();
      if (!illustrationUrl) throw new Error("No illustration URL returned by the server.");

      const payload = {
        illustrationUrl,
        illustrationCaption: String(scene?.illustrationCaption || "").trim(),
        illustrationCredit: String(scene?.illustrationCredit || "").trim(),
        illustrationAltText:
          String(scene?.illustrationAltText || "").trim() ||
          String(file?.name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim(),
        illustrationType: "scene_illustration",
        illustrationStatus: "pending",
      };

      const res =
        typeof api.replaceSceneIllustration === "function"
          ? await api.replaceSceneIllustration(storyId, sceneId, payload)
          : await api.reviewSceneIllustration(storyId, sceneId, { action: "replace", ...payload });

      if (res?.story) setStoryInState(res.story);
      setProposalMessage(t.sceneIllustrationReplaceSaved || "Illustration replaced. Review and approve it before public display.");
    } catch (err) {
      console.error("Scene illustration replace error:", err?.message || err);
      setProposalMessage(getApiErrorMessage(err, t.sceneIllustrationReplaceFailed || "Illustration replacement failed. Please try again."));
    } finally {
      setIllustrationReviewBusy((prev) => ({ ...prev, [sceneId]: false }));
    }
  }

  function renderIllustrationReviewControls(scene, compact = false) {
    if (!canManageSceneIllustration(scene)) return null;

    const sceneId = sceneReviewKey(scene);
    const busy = !!illustrationReviewBusy[sceneId];
    const draft = getIllustrationReviewDraft(scene);
    const status = sceneIllustrationStatus(scene);
    const statusLabel =
      status === "approved"
        ? t.sceneIllustrationApproved
        : status === "rejected"
        ? t.sceneIllustrationRejected
        : t.sceneIllustrationPending;

    return (
      <details className="ns-scene-illustration-review" style={{ marginTop: compact ? 8 : 12 }}>
        <summary>
          <span>{t.sceneIllustrationReviewPanel || "Illustration review"}</span>
          <span className={`ns-scene-illustration-status ns-scene-illustration-status-${status || "pending"}`}>
            {statusLabel}
          </span>
        </summary>

        <div className="ns-scene-illustration-review-body">
          <div className="ns-scene-illustration-review-actions">
            <button
              type="button"
              className="ns-button ns-button-primary"
              disabled={busy || status === "approved"}
              onClick={() => handleSceneIllustrationDecision(scene, "approve")}
            >
              {t.sceneIllustrationApproveButton || "Approve illustration"}
            </button>
            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={busy}
              onClick={() => handleSceneIllustrationDecision(scene, "clear")}
            >
              {t.sceneIllustrationClearButton || "Clear illustration"}
            </button>
            <label
              className="ns-button ns-button-outline ns-scene-illustration-change-button"
              aria-disabled={busy ? "true" : "false"}
              title={t.sceneIllustrationChangeButton || "Change image"}
            >
              {busy ? (t.working || "Working…") : (t.sceneIllustrationChangeButton || "Change image")}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={busy}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  e.target.value = "";
                  if (file) handleReplaceExistingSceneIllustration(scene, file);
                }}
              />
            </label>
          </div>

          <div className="ns-illustration-grid ns-illustration-review-grid">
            <div className="ns-field">
              <label>{t.sceneIllustrationReviewReason || "Reason"}</label>
              <select
                style={INPUT_STYLE}
                value={draft.reason}
                disabled={busy}
                onChange={(e) => updateIllustrationReviewDraft(scene, { reason: e.target.value })}
              >
                <option value="">{t.sceneIllustrationChooseReason || "Choose a reason"}</option>
                <option value="does_not_match_scene">{t.sceneIllustrationReasonDoesNotMatch}</option>
                <option value="image_quality">{t.sceneIllustrationReasonImageQuality}</option>
                <option value="consent_or_copyright">{t.sceneIllustrationReasonConsentCopyright}</option>
                <option value="safety_or_policy">{t.sceneIllustrationReasonSafetyPolicy}</option>
                <option value="other">{t.sceneIllustrationReasonOther}</option>
              </select>
            </div>

            <div className="ns-field">
              <label>{t.sceneIllustrationReviewNote || "Note"}</label>
              <textarea
                style={{ ...INPUT_STYLE, minHeight: 76, resize: "vertical" }}
                value={draft.note}
                disabled={busy}
                placeholder={t.sceneIllustrationRejectNotePlaceholder || "Optional note for the contributor…"}
                onChange={(e) => updateIllustrationReviewDraft(scene, { note: e.target.value })}
              />
            </div>
          </div>

          <div className="ns-scene-illustration-review-actions">
            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={busy || !draft.reason}
              onClick={() => handleSceneIllustrationDecision(scene, "reject")}
            >
              {t.sceneIllustrationRejectButton || "Reject illustration"}
            </button>
          </div>
        </div>
      </details>
    );
  }

  function validateCanonForm() {
    if (!canonTitle.trim()) {
      setCanonMessage(t.provideSceneTitle);
      return false;
    }
    if (!canonText.trim()) {
      setCanonMessage(t.writeSceneText);
      return false;
    }

    const wc = countWords(canonText);
    if (!editingCanonSceneId && wc < MIN_PROPOSAL_WORDS) {
      setCanonMessage(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "Scène en cours : vous pouvez l’enregistrer maintenant et continuer plus tard."
          : "Scene in progress: you can save it now and continue later."
      );
    }
    if (wc > MAX_PROPOSAL_WORDS) {
      setCanonMessage(
        t.sceneTooLongMessage
          .replace("{count}", wc)
          .replace("{max}", MAX_PROPOSAL_WORDS)
      );
      return false;
    }

    if (wc > RECOMMENDED_MAX_PROPOSAL_WORDS) {
      setCanonMessage(
        (t.sceneGraceRangeMessage ||
          "This scene is {count} words. The standard target is {recommendedMax} words, but it can be saved because it stays within the {max}-word grace range. Use this exception only when trimming would hurt clarity or readability.")
          .replace("{count}", wc)
          .replace("{recommendedMax}", RECOMMENDED_MAX_PROPOSAL_WORDS)
          .replace("{max}", MAX_PROPOSAL_WORDS)
      );
    }

    if (!hasParagraphBreaks(canonText)) {
      setCanonMessage(t.breakSceneIntoParagraphsTip);
      // Not a hard block, just guidance.
    }

    return true;
  }

  async function handleCreateCanonScene(e) {
    e.preventDefault();
    if (!activeStoryId) return;
    setCanonMessage("");

    if (!isWriter) {
      setCanonMessage(t.onlyWriterCanAddCanonScenes);
      return;
    }    // Writing-mode constitution (UI guardrails to match server rules)
    if (isCollaborativeMode && !isStoryOwner) {
      setCanonMessage(t.collaborativeCanonClosedOnlyAuthor);
      return;
    }

    if (isGroupStory && !isStoryOwner) {
      setCanonMessage(t.groupCanonClosedOnlyAuthor);
      return;
    }


    if (!isSoloMode && proposalsOpen) {
      setCanonMessage(t.canonLockedWhileProposalsOpen);
      return;
    }


if (isSoloMode && !isStoryOwner) {
  setCanonMessage(t.soloAuthorOnlyCanon);
  return;
}

    if (!validateCanonForm()) return;
    if (canonIllustrationUrl.trim() && !canonIllustrationConfirmed) {
      setCanonMessage(t.sceneIllustrationConfirmRequired || "Please confirm this illustration before saving the scene.");
      return;
    }
    // Duplicate guard: prevent saving the exact same canon scene repeatedly by clicking again
    const _canonTitle = canonTitle.trim();
    const _canonText = canonText.trim();
    const _canonFP = `${_canonTitle}\n---\n${_canonText}`;
    const _sid = String(activeStoryId);
    if (
      !editingCanonSceneId &&
      lastCanonSavedRef.current[_sid] === _canonFP
    ) {
      setCanonMessage(t.sceneAlreadySaved);
      return;
    }


    // An explicit Chapter selection travels with the scene in the same
    // server request. No client-side second PATCH or automatic fallback.
    const bookHandoffChapterId = (
      writerStudioSceneContext.newScene &&
      (
        !writerStudioSceneContext.storyId ||
        String(writerStudioSceneContext.storyId) === String(activeStoryId)
      )
    ) ? String(writerStudioSceneContext.chapterId || "").trim() : "";
    const requestedChapterId = editingCanonSceneId
      ? ""
      : String(targetChapterId || focusedChapterId || bookHandoffChapterId || "").trim();
    setCreatingCanon(true);
    try {
      const res = editingCanonSceneId
        ? await api(
            `/api/stories/${encodeURIComponent(
              String(activeStoryId)
            )}/scenes/${encodeURIComponent(
              editingCanonSceneId
            )}`,
            {
              method: "PATCH",
              body: JSON.stringify({
                title: _canonTitle,
                text: _canonText,
                manuscriptContent:
                  canonManuscriptContent || null,
              }),
            }
          )
        : await api(
            `/api/stories/${encodeURIComponent(
              String(activeStoryId)
            )}/scenes`,
            {
              method: "POST",
              body: JSON.stringify({
                type: "canon",
                chapterId: requestedChapterId,
                sceneNumber:
                  (canonicalScenes?.length || 0) + 1,
                title: _canonTitle,
                text: _canonText,
                manuscriptContent:
                  canonManuscriptContent || null,
                illustrationUrl:
                  canonIllustrationUrl.trim(),
                illustrationCaption:
                  canonIllustrationCaption.trim(),
                illustrationCredit:
                  canonIllustrationCredit.trim(),
                illustrationAltText:
                  canonIllustrationAltText.trim(),
                illustrationType: "scene_illustration",
              }),
            }
          );

      let updated = res?.story || res;

      const createdSceneId =
        !editingCanonSceneId
          ? getSceneId(res?.scene)
          : "";

      // Verify the server's authoritative, already-atomic assignment.
      // A partial/old API response must never advertise a successful Chapter
      // placement, or invite the Owner to resave an already-saved Scene.
      const assignedChapterId = String(res?.assignedChapterId || "").trim();
      const chapterPlacementUnconfirmed = Boolean(
        createdSceneId && requestedChapterId &&
        (
          assignedChapterId !== requestedChapterId ||
          !Array.isArray(updated?.chapters) ||
          !updated.chapters.some((chapter) =>
            String(chapter?.id || "") === requestedChapterId &&
            Array.isArray(chapter?.sceneIds) &&
            chapter.sceneIds.some((id) => String(id) === String(createdSceneId))
          )
        )
      );
      const savedChapter = Array.isArray(updated?.chapters)
        ? updated.chapters.find((chapter) =>
            String(chapter?.id || "") === requestedChapterId)
        : null;

      if (updated && (updated._id || updated.id)) {
        setStoryInState(updated);
      }

      const savedSceneId =
        editingCanonSceneId || createdSceneId;
      const savedChapterId =
        chapterPlacementUnconfirmed ? "" :
        (editingCanonSceneId
          ? String(targetChapterId || focusedChapterId || "").trim()
          : (assignedChapterId || requestedChapterId));

      if (savedSceneId) {
        setFocusedSceneId(
          String(savedSceneId)
        );
        if (savedChapterId) {
          setFocusedChapterId(
            String(savedChapterId)
          );
        }
        setWriterStudioStage("write");

        try {
          const url =
            new URL(window.location.href);
          url.searchParams.set(
            "storyId",
            String(activeStoryId)
          );
          if (savedChapterId) {
            url.searchParams.set(
              "chapterId",
              String(savedChapterId)
            );
          } else {
            url.searchParams.delete("chapterId");
          }
          url.searchParams.set(
            "sceneId",
            String(savedSceneId)
          );
          url.searchParams.delete(
            "newScene"
          );
          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`
          );
        } catch {}
      }

      setCanonTitle("");
      setCanonText("");
      setCanonManuscriptContent(null);
      setCanonIllustrationUrl("");
      setCanonIllustrationCaption("");
      setCanonIllustrationCredit("");
      setCanonIllustrationAltText("");
      setCanonIllustrationConfirmed(false);
      // Clear auto-saved draft so the modal does not resurrect the just-saved text
      lsDel(canonDraftKey(activeStoryId));
      // Remember fingerprint to prevent accidental duplicate saves on repeated clicks
      lastCanonSavedRef.current[String(activeStoryId)] = _canonFP;

      setCanonMessage(
        chapterPlacementUnconfirmed
          ? (
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Scène Canon enregistrée. Son placement dans le chapitre n’a pas pu être confirmé. Vérifiez Book Workspace avant de réessayer."
                : "Canon Scene saved. Chapter placement could not be confirmed. Check Book Workspace before trying again."
            )
          : !editingCanonSceneId && requestedChapterId
          ? (
              String(lang || "en").toLowerCase().startsWith("fr")
                ? `Scène enregistrée dans le chapitre : ${savedChapter?.title || requestedChapterId}.`
                : `Scene saved to Chapter: ${savedChapter?.title || requestedChapterId}.`
            )
          : editingCanonSceneId
          ? (
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Scène Canon mise à jour."
                : "Canon Scene updated."
            )
          : t.canonSceneSavedValidated
      );
      setEditingCanonSceneId("");
      setTargetChapterId("");
      setShowAddCanonModal(false);

      const stored = loadStoredStories() || [];
      fetchRemote(stored, { silent: true });
    } catch (err) {
      console.error("Create canon error:", err?.message || err);
      setCanonMessage(getApiErrorMessage(err, t.failedToSaveCanonScene));
    } finally {
      setCreatingCanon(false);
    }
  }

  function handleRequestOpenProposals() {
    if (!activeStoryId) return;

    if (!isWriter) {
      setProposalMessage(t.onlyWriterCanCallProposals);
      return;
    }
    if (proposalsOpen) {
      setProposalMessage(t.proposalsAlreadyOpenLong);
      return;
    }
    setIntentDraft(proposalIntent || "");
    if (isSoloMode) {
      setProposalMessage(t.proposalsDisabledForStoryline);
      return;
    }
    setShowIntentModal(true);
  }

  async function handleConfirmOpenProposals() {
    if (!activeStoryId) return;
    if (!isWriter) return;

    const cleaned = (intentDraft || "").trim();
    writeIntentToStorage(activeStoryId, cleaned);
    setProposalIntent(cleaned);

    try {
      const res = await api(
        `/api/stories/${encodeURIComponent(String(activeStoryId))}/proposals`,
        {
          method: "PATCH",
          body: JSON.stringify({ action: "open", open: true }),
        }
      );

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) setStoryInState(updated);

      pushDecisionLogEntry(activeStoryId, {
        id: `d_${Date.now()}`,
        action: "open_round",
        by: username?.trim() || activeStory?.author || t.writerRoleLabel,
        at: new Date().toISOString(),
        note: cleaned || t.proposalRoundOpened,
        sceneTitle: t.callForProposals,
      });

      setShowIntentModal(false);
      if (intentAutoTimer.current) {
        clearTimeout(intentAutoTimer.current);
        intentAutoTimer.current = null;
      }
      lsDel(
        wsKey(
          "intent",
          activeStoryId
        )
      );
      setIntentDraft("");
      setIntentAutoStatus("");
      setWriterStudioStage(
        "collaboration"
      );
      setProposalMessage(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "Mode Proposition actif. Le Canon est maintenant verrouillé jusqu’à Approuver et adopter ou Rejeter toutes les propositions."
          : "Proposal Mode is active. Canon is now locked until you Approve & Adopt a proposal or Reject All Proposals."
      );

      const stored = loadStoredStories() || [];
      fetchRemote(stored, { silent: true });
    } catch (err) {
      console.error("Open proposals error:", err?.message || err);
      setProposalMessage(t.failedToOpenProposals);
    }
  }

  function validateProposalForm() {
    if (!proposalTitle.trim() || proposalTitle.trim().length < 6) {
      setProposalMessage(t.provideClearerTitle);
      return false;
    }

    if (!proposalText.trim()) {
      setProposalMessage(t.writeProposedSceneText);
      return false;
    }

    const wc = countWords(proposalText);

    if (wc < MIN_PROPOSAL_WORDS) {
      setProposalMessage(
        `Take a little more space to develop the idea. Strong scenes usually need at least ${MIN_PROPOSAL_WORDS} words.`
      );
      return false;
    }

    if (wc > MAX_PROPOSAL_WORDS) {
      setProposalMessage(
        (t.proposalTooLongMessage ||
          "This proposal is {count} words. Please trim it to {max} words or fewer.")
          .replace("{count}", wc)
          .replace("{max}", MAX_PROPOSAL_WORDS)
      );
      return false;
    }

    if (wc > RECOMMENDED_MAX_PROPOSAL_WORDS) {
      setProposalMessage(
        (t.proposalGraceRangeMessage ||
          "This proposal is {count} words. The standard target is {recommendedMax} words, but it can be submitted because it stays within the {max}-word grace range. Use this exception only when trimming would hurt clarity or readability.")
          .replace("{count}", wc)
          .replace("{recommendedMax}", RECOMMENDED_MAX_PROPOSAL_WORDS)
          .replace("{max}", MAX_PROPOSAL_WORDS)
      );
    }

    return true;
  }

  async function handleCreateProposal(e) {
    e.preventDefault();
    if (!activeStoryId) return;

    setProposalMessage("");

    if (isWriter) {
      setProposalMessage(t.mainWriterCannotSubmitProposals);
      return;
    }

    if (!proposalsOpen) {
      setProposalMessage(t.writerHasNotOpenedProposalRound);
      return;
    }

    if (!validateProposalForm()) return;
    if (proposalIllustrationUrl.trim() && !proposalIllustrationConfirmed) {
      setProposalMessage(t.sceneIllustrationConfirmRequired || "Please confirm this illustration before submitting the proposal.");
      return;
    }

    setCreatingProposal(true);
    try {
      const res = await api(
        `/api/stories/${encodeURIComponent(String(activeStoryId))}/scenes`,
        {
          method: "POST",
          body: JSON.stringify({
            type: "proposed",
            sceneNumber: null,
            title: proposalTitle.trim(),
            text: proposalText.trim(),
            manuscriptContent:
              proposalManuscriptContent || null,
            illustrationUrl: proposalIllustrationUrl.trim(),
            illustrationCaption: proposalIllustrationCaption.trim(),
            illustrationCredit: proposalIllustrationCredit.trim(),
            illustrationAltText: proposalIllustrationAltText.trim(),
            illustrationType: "scene_illustration",
          }),
        }
      );

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) setStoryInState(updated);

      // Close first so cleared fields cannot schedule a fresh autosave.
      setShowAddProposalModal(false);

      if (proposalAutoTimer.current) {
        clearTimeout(proposalAutoTimer.current);
        proposalAutoTimer.current = null;
      }
      if (proposalSavedTimer.current) {
        clearTimeout(proposalSavedTimer.current);
        proposalSavedTimer.current = null;
      }

      lsDel(wsKey("proposal", activeStoryId));
      setProposalDraftPresent(false);
      setProposalDraftJustSaved(false);
      setProposalAutoStatus("");
      clearIntentForStory(activeStoryId);

      setProposalTitle("");
      setProposalText("");
      setProposalManuscriptContent(null);
      setProposalIllustrationUrl("");
      setProposalIllustrationCaption("");
      setProposalIllustrationCredit("");
      setProposalIllustrationAltText("");
      setProposalIllustrationConfirmed(false);
      setProposalMessage(t.proposedSceneSubmitted);
      setWriterStudioStage(
        "collaboration"
      );

      const stored = loadStoredStories() || [];
      fetchRemote(stored, { silent: true });
    } catch (err) {
      console.error("Create proposal error:", err?.message || err);
      setProposalMessage(
        getApiErrorMessage(
          err,
          t.failedToSubmitProposal
        )
      );
    } finally {
      setCreatingProposal(false);
    }
  }

  function clearIntentForStory(storyId) {
    if (!storyId) return;
    writeIntentToStorage(storyId, "");
    setProposalIntent("");
    setIntentDraft("");
  }

  function requestApproveProposal(scene) {
    if (!activeStoryId) return;
    if (!isWriter) {
      setProposalMessage(
        t.onlyWriterCanApproveProposals
      );
      return;
    }
    if (!scene) return;

    const selection =
      buildProposalAdoptionSelection(
        scene
      );

    if (!selection.sceneId) {
      setProposalMessage(
        t.couldNotIdentifyProposal
      );
      return;
    }

    // Freeze the exact proposal identity the Owner reviewed.
    // New proposals, votes, or refreshes cannot replace this selection.
    setPendingDecisionScene(
      selection
    );
    setDecisionDraft("");
    setDecisionError("");
    setShowDecisionModal(true);
  }

  function cancelProposalAdoptionDecision() {
    setShowDecisionModal(false);
    setPendingDecisionScene(null);
    setDecisionDraft("");
    setDecisionError("");
  }

  function pushDecisionLogEntry(storyId, entry) {
    const current = loadDecisionLog(storyId);
    const next = [entry, ...current].slice(0, 200);
    saveDecisionLog(storyId, next);
    setDecisionLog(next);
  }

  async function confirmApproveProposalWithDecision() {
    if (!activeStoryId) return;
    if (!isWriter) return;

    const selection =
      pendingDecisionScene
        ? {
            ...pendingDecisionScene,
          }
        : null;

    const note =
      (decisionDraft || "").trim();

    if (!note) {
      setDecisionError(
        t.writeShortDecisionNote
      );
      return;
    }

    const targetId =
      String(
        selection?.sceneId || ""
      ).trim();

    if (!targetId) {
      setDecisionError(
        t.couldNotIdentifyProposal
      );
      return;
    }

    const currentProposal =
      (
        Array.isArray(
          activeStory?.scenes
        )
          ? activeStory.scenes
          : []
      ).find(
        (scene) =>
          getSceneId(scene) ===
          targetId
      ) || null;

    if (
      !currentProposal ||
      !isPendingProposal(
        currentProposal
      ) ||
      !proposalSelectionMatchesScene(
        currentProposal,
        selection
      )
    ) {
      setDecisionError(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "La proposition sélectionnée a changé ou n’est plus disponible. Annulez et relisez la proposition avant de l’adopter."
          : "The selected proposal changed or is no longer available. Cancel and review the proposal again before adopting it."
      );
      return;
    }

    const now =
      new Date().toISOString();

    try {
      const res = await api(
        `/api/stories/${encodeURIComponent(String(activeStoryId))}/proposals`,
        {
          method: "PATCH",
          body: JSON.stringify({
            action: "approve",
            proposalId: targetId,
            sceneId: targetId,
            id: targetId,
            decisionNote: note,
            expectedProposal:
              selection,
          }),
        }
      );

      const approvedScene =
        res?.approvedScene || null;
      const approvedSceneId =
        getSceneId(
          approvedScene
        );
      const verification =
        res?.adoptionVerification ||
        null;

      if (
        !approvedScene ||
        String(
          approvedSceneId || ""
        ) !== targetId ||
        verification
          ?.selectionVerified !==
          true ||
        String(
          verification
            ?.requestedProposalId ||
            ""
        ) !== targetId ||
        String(
          verification
            ?.approvedSceneId ||
            ""
        ) !== targetId ||
        !proposalSelectionMatchesScene(
          approvedScene,
          selection
        )
      ) {
        setDecisionError(
          String(lang || "en")
            .toLowerCase()
            .startsWith("fr")
            ? "La confirmation du serveur ne correspond pas à la proposition sélectionnée. L’adoption n’est pas confirmée ; vérifiez l’historique avant de continuer."
            : "The server confirmation does not match the selected proposal. Adoption is not confirmed; check Story History before continuing."
        );
        return;
      }

      const updated =
        res?.story || res;

      if (
        updated &&
        (updated._id || updated.id)
      ) {
        setStoryInState(updated);
      }

      clearIntentForStory(
        activeStoryId
      );

      pushDecisionLogEntry(
        activeStoryId,
        {
          id: `d_${Date.now()}`,
          action: "approve",
          by:
            username?.trim() ||
            activeStory?.author ||
            t.writerRoleLabel,
          at: now,
          note,
          sceneTitle:
            selection?.title ||
            t.untitledProposal,
          sceneId: targetId,
        }
      );

      setShowDecisionModal(false);
      if (decisionAutoTimer.current) {
        clearTimeout(
          decisionAutoTimer.current
        );
        decisionAutoTimer.current = null;
      }
      lsDel(
        wsKey(
          "decision",
          activeStoryId
        )
      );
      setPendingDecisionScene(null);
      setDecisionDraft("");
      setDecisionAutoStatus("");
      setDecisionError("");

      if (approvedSceneId) {
        const approvedSceneIdString =
          String(
            approvedSceneId
          );

        const updatedChapters =
          Array.isArray(
            updated?.chapters
          )
            ? updated.chapters
            : [];

        const approvedChapter =
          updatedChapters.find(
            (chapter) =>
              Array.isArray(
                chapter?.sceneIds
              ) &&
              chapter.sceneIds.some(
                (sceneId) =>
                  String(
                    sceneId || ""
                  ) ===
                  approvedSceneIdString
              )
          ) || null;

        const approvedChapterId =
          String(
            approvedChapter?.id ||
            approvedChapter?._id ||
            ""
          ).trim();

        setFocusedSceneId(
          approvedSceneIdString
        );
        setFocusedChapterId(
          approvedChapterId
        );
        setTargetChapterId(
          approvedChapterId
        );
        syncWriterStudioWorkUrl({
          chapterId:
            approvedChapterId,
          sceneId:
            approvedSceneIdString,
        });
      }

      setWriterStudioStage(
        "write"
      );

      setProposalMessage(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "La proposition sélectionnée a été vérifiée et adoptée dans le Canon. Mode Canon actif."
          : "The selected proposal was verified and adopted into Canon. Canon Mode is active again."
      );

      const stored =
        loadStoredStories() || [];
      fetchRemote(
        stored,
        {
          silent: true,
        }
      );
    } catch (err) {
      console.error(
        "Approve proposal error:",
        err?.message || err
      );

      setDecisionError(
        getApiErrorMessage(
          err,
          t.failedToApproveProposal
        )
      );
    }
  }

  async function handleRejectProposal(sceneIdRaw) {
    if (!activeStoryId) return;
    if (!isWriter) {
      setProposalMessage(t.onlyWriterCanRejectProposals);
      return;
    }

    const sceneId = String(sceneIdRaw || "").trim();
    if (!sceneId) return;

    try {
      const res = await api(
        `/api/stories/${encodeURIComponent(String(activeStoryId))}/proposals`,
        {
          method: "PATCH",
          body: JSON.stringify({ action: "rejectOne", proposalId: sceneId }),
        }
      );

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) setStoryInState(updated);

      const now = new Date().toISOString();
      pushDecisionLogEntry(activeStoryId, {
        id: `d_${Date.now()}`,
        action: "reject_one",
        by: username?.trim() || activeStory?.author || t.writerRoleLabel,
        at: now,
        note: t.rejectedProposalNote,
        sceneTitle: t.proposalRemoved,
      });

      setProposalMessage(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "Proposition rejetée et retirée. Mode Proposition reste ouvert ; le Canon reste verrouillé."
          : "Proposal rejected and removed. Proposal Mode remains open; Canon remains locked."
      );

      const stored = loadStoredStories() || [];
      fetchRemote(stored, { silent: true });
    } catch (err) {
      console.error("Reject proposal error:", err?.message || err);
      setProposalMessage(t.failedToRejectProposal);
    }
  }

  async function handleConfirmCancelEmptyProposalRound() {
    if (!activeStoryId || !canCancelEmptyRound || cancelEmptyRoundBusy) return;
    setCancelEmptyRoundBusy(true);
    try {
      const res=await api(
        `/api/stories/${encodeURIComponent(String(activeStoryId))}/proposals`,
        {method:"PATCH",body:JSON.stringify({action:"cancelEmpty"})}
      );
      const updated=res?.story||res;
      if (updated && (updated._id||updated.id)) setStoryInState(updated);
      clearIntentForStory(activeStoryId);
      setShowCancelEmptyRoundModal(false);
      setWriterStudioStage("write");
      setProposalMessage(String(lang||"en").toLowerCase().startsWith("fr")
        ? "Tour vide annulé. Mode Canon réactivé ; aucune proposition supprimée."
        : "Empty Proposal Round cancelled. Canon Mode restored; no proposals discarded.");
      const stored=loadStoredStories()||[];
      fetchRemote(stored,{silent:true});
    } catch(err) {
      setProposalMessage(getApiErrorMessage(err,
        "Cancellation refused: a proposal may have arrived. Refresh and review the round."));
      setShowCancelEmptyRoundModal(false);
      const stored=loadStoredStories()||[];
      fetchRemote(stored,{silent:true});
    } finally {
      setCancelEmptyRoundBusy(false);
    }
  }

  async function handleRejectAllProposals() {
    if (!activeStoryId) return;
    if (!isWriter) {
      setProposalMessage(t.onlyWriterCanRejectAllProposals);
      return;
    }

    try {
      const res = await api(
        `/api/stories/${encodeURIComponent(String(activeStoryId))}/proposals`,
        {
          method: "PATCH",
          body: JSON.stringify({ action: "rejectAll" }),
        }
      );

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) setStoryInState(updated);

      clearIntentForStory(activeStoryId);

      const now = new Date().toISOString();
      pushDecisionLogEntry(activeStoryId, {
        id: `d_${Date.now()}`,
        action: "reject_all",
        by: username?.trim() || activeStory?.author || t.writerRoleLabel,
        at: now,
        note: t.rejectedAllProposalsRound,
        sceneTitle: t.roundCleared,
      });

      setWriterStudioStage(
        "write"
      );

      setProposalMessage(
        String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "Toutes les propositions ont été rejetées. Mode Proposition fermé ; Mode Canon actif et déverrouillé."
          : "All proposals rejected. Proposal Mode is closed; Canon Mode is active and unlocked."
      );

      const stored = loadStoredStories() || [];
      fetchRemote(stored, { silent: true });
    } catch (err) {
      console.error("Reject all error:", err?.message || err);
      setProposalMessage(t.failedToRejectAllProposals);
    }
  }

  async function handleVote(scene, voteValue) {
    if (!activeStoryId) return;

    if (!proposalsOpen) {
      setProposalMessage(t.votingOnlyWhenProposalsOpen);
      return;
    }

    if (isWriter) {
      setProposalMessage(t.writerCannotVoteOnProposals);
      return;
    }

    if (!username || !username.trim()) {
      setProposalMessage(t.setUsernameBeforeVoting);
      return;
    }

    const sceneId = getSceneId(scene);
    if (!sceneId) return;

    const sceneAuthors = getSceneAuthors(scene);
    const meRaw = String(username || "").trim();

    if (sceneAuthors.some((a) => identitiesMatch(a, meRaw))) {
      setProposalMessage(t.cannotVoteOwnProposal);
      return;
    }

    const currentVote = userVotes[sceneId];
    if (currentVote !== undefined) {
      setProposalMessage(t.alreadyVotedOnProposal);
      return;
    }

    setProposalVoteBusy(true);
    setProposalMessage("");

    try {
      const res = await api(
        `/api/stories/${encodeURIComponent(
          String(activeStoryId)
        )}/proposals/${encodeURIComponent(
          String(sceneId)
        )}/vote`,
        {
          method: "POST",
          body: JSON.stringify({
            value: voteValue,
          }),
        }
      );

      setVoteTotals((current) => ({
        ...(current || {}),
        [sceneId]: {
          up: toSafeInt(
            res?.summary?.up
          ),
          down: toSafeInt(
            res?.summary?.down
          ),
          neutral: toSafeInt(
            res?.summary?.neutral
          ),
        },
      }));

      setUserVotes((current) => ({
        ...(current || {}),
        [sceneId]:
          Number(
            res?.vote
          ),
      }));

      setVoteProposalScene(null);
      setProposalMessage(t.voteRecorded);
    } catch (err) {
      console.error(
        "Proposal vote error:",
        err?.message || err
      );
      setProposalMessage(
        getApiErrorMessage(
          err,
          t.alreadyVotedOnProposal ||
            "Could not record vote."
        )
      );
    } finally {
      setProposalVoteBusy(false);
    }
  }

  function scrollProposals(direction) {
    const container = proposalsContainerRef.current;
    if (!container) return;
    const amount = 320;
    container.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }

  function scrollCollaborationProposals(direction) {
    const container =
      collaborationProposalShelfRef.current;

    if (!container) return;

    const amount =
      Math.max(
        300,
        Math.min(
          container.clientWidth * 0.82,
          520
        )
      );

    container.scrollBy({
      left:
        direction === "left"
          ? -amount
          : amount,
      behavior: "smooth",
    });
  }

  const studioChapters =
    Array.isArray(activeStory?.chapters)
      ? activeStory.chapters
      : [];

  const recentCanonSnippets =
    canonicalScenes.length <= 2 ? canonicalScenes : canonicalScenes.slice(-2);
  const validatedFive =
    canonicalScenes.length <= 5 ? canonicalScenes : canonicalScenes.slice(-5);

  // Only the full-book presentation uses saved Book Structure ordering.
  // Current Canon chronology and collaboration decisions remain unchanged.
  const bookDraftSequence = useMemo(
    () => buildNovelDraftSequence(
      canonicalScenes,
      activeStory?.chapters,
      getSceneId
    ),
    [canonicalScenes, activeStory?.chapters]
  );

  function formatBookDraftEntryLocation(entry) {
    if (!entry?.hasSavedChapters) {
      return formatCanonSceneLocation(entry?.scene);
    }
    const isFR = String(lang || "en").toLowerCase().startsWith("fr");
    const sceneLabel = isFR ? "Scène" : "Scene";
    if (entry.isUnassigned) {
      return `${isFR ? "Scènes non affectées" : "Unassigned Scenes"} · ${sceneLabel} ${entry.scenePosition}`;
    }
    const chapterLabel = isFR ? "Chapitre" : "Chapter";
    const chapterNumber = entry.chapterIndex + 1;
    const chapterTitle = String(entry.chapterTitle || "").trim();
    const isDefaultTitle = new RegExp(
      `^(chapter|chapitre)\\s+${chapterNumber}$`, "i"
    ).test(chapterTitle);
    const titledChapter = chapterTitle && !isDefaultTitle
      ? ` · ${chapterTitle}`
      : "";
    return `${chapterLabel} ${chapterNumber}${titledChapter} · ${sceneLabel} ${entry.scenePosition}`;
  }

  const draftText =
    bookDraftSequence.length === 0
      ? ""
      : bookDraftSequence
          .map((entry, index) => {
            const scene = entry.scene;
            const header = `${formatBookDraftEntryLocation(
              entry
            )} – ${scene.title || t.card_untitled_story}`;
            const decision =
              scene.decisionNote && String(scene.decisionNote).trim()
                ? `${t.decisionNoteLabel} (${scene.decisionBy || t.writerRoleLabel}): ${scene.decisionNote}\n`
                : "";
            const startsUnassigned =
              entry.isUnassigned &&
              (index === 0 || !bookDraftSequence[index - 1].isUnassigned);
            const sectionHeading = startsUnassigned
              ? (String(lang || "en").toLowerCase().startsWith("fr")
                  ? "SCÈNES NON AFFECTÉES (HORS CHAPITRES)\n\n"
                  : "UNASSIGNED SCENES (NOT PLACED IN CHAPTERS)\n\n")
              : "";
            return `${sectionHeading}${header}\n${"=".repeat(header.length)}\n${decision}${scene.text}\n\n`;
          })
          .join("");

  function handleDownloadDraft() {
    if (!draftText) return;
    const blob = new Blob([draftText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const safeTitle = (activeStory?.title || "draft-manuscript").replace(
      /[/\\?%*:|"<>]/g,
      "_"
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeTitle}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function buildShareUrl() {
    const storyId = activeStory && (activeStory._id || activeStory.id);
    const base = window.location?.origin || "";
    const path = window.location?.pathname || "/";
    return `${base}${path}?storyId=${encodeURIComponent(String(storyId || ""))}`;
  }

  async function handleShareStory() {
    if (!activeStory) return;
    const title = activeStory.title || t.nextScenesStoryline;
    const url = buildShareUrl();

    try {
      if (navigator.share) {
        await navigator.share({ title, text: `${t.readAndFollow}: ${title}`, url });
        setProposalMessage(t.linkShared);
        return;
      }
    } catch {}

    try {
      await navigator.clipboard.writeText(url);
      setProposalMessage(t.linkCopied);
    } catch {
      setProposalMessage(t.copyLinkBlocked);
    }
  }

  const analytics = useMemo(() => {
    const canonCount = canonicalScenes.length;
    const proposalCount = proposedScenes.length;

    let votesUp = 0;
    let votesDown = 0;
    let votesNeutral = 0;

    proposedScenes.forEach((p) => {
      const sid = getSceneId(p);
      const local = sid && voteTotals && voteTotals[sid] ? voteTotals[sid] : null;

      const up = local ? toSafeInt(local.up) : toSafeInt(p.votesUp);
      const down = local ? toSafeInt(local.down) : toSafeInt(p.votesDown);
      const neutral = local ? toSafeInt(local.neutral) : toSafeInt(p.votesNeutral);

      votesUp += up;
      votesDown += down;
      votesNeutral += neutral;
    });

    const commentCount = threadedComments.parents.length;
    let replyCount = 0;
    threadedComments.parents.forEach((p) => {
      const pid = String(p?.id || "");
      replyCount += Array.isArray(threadedComments.repliesByParent[pid])
        ? threadedComments.repliesByParent[pid].length
        : 0;
    });

    return {
      canonCount,
      proposalCount,
      votesUp,
      votesDown,
      votesNeutral,
      commentCount,
      replyCount,
    };
  }, [canonicalScenes, proposedScenes, voteTotals, threadedComments]);

  const proposalWordCount = useMemo(() => countWords(proposalText), [proposalText]);
  const canonWordCount = useMemo(() => countWords(canonText), [canonText]);
  const canonHasParagraphs = useMemo(() => hasParagraphBreaks(canonText), [canonText]);

  const canonReadiness = useMemo(() => {
  if (!canonText.trim()) return t.waiting;
  const wc = canonWordCount;
  if (wc > MAX_PROPOSAL_WORDS) return t.tooLong;
  if (wc > RECOMMENDED_MAX_PROPOSAL_WORDS) {
    return t.allowedException || "Allowed exception";
  }
  if (wc < MIN_PROPOSAL_WORDS) return t.tooShort;
  if (!canonHasParagraphs) return t.addParagraphs;
  return t.good;
}, [canonWordCount, canonText, canonHasParagraphs, t]);


  const canonTokenSet = useMemo(() => {
    const set = new Set();
    const lastTwo =
      canonicalScenes.length <= 2 ? canonicalScenes : canonicalScenes.slice(-2);
    lastTwo.forEach((s) => {
      extractCanonTokensFromText(s?.title || "").forEach((t) => set.add(t));
      extractCanonTokensFromText(s?.text || "").forEach((t) => set.add(t));
    });
    return set;
  }, [canonicalScenes]);

  const mentionsCanonElement = useMemo(() => {
    const t = proposalText || "";
    if (!t.trim()) return false;
    const tokens = Array.from(canonTokenSet);
    if (tokens.length === 0) return false;

    const limit = Math.min(tokens.length, 40);
    for (let i = 0; i < limit; i += 1) {
      const token = tokens[i];
      if (!token) continue;
      const re = new RegExp(
        `\\b${token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`
      );
      if (re.test(t)) return true;
    }
    return false;
  }, [proposalText, canonTokenSet]);

  const proposalHasParagraphs = useMemo(
    () => hasParagraphBreaks(proposalText),
    [proposalText]
  );

  const proposalReadiness = useMemo(() => {
  const wc = proposalWordCount;
  const within = wc >= MIN_PROPOSAL_WORDS && wc <= RECOMMENDED_MAX_PROPOSAL_WORDS;
  const withinGrace = wc > RECOMMENDED_MAX_PROPOSAL_WORDS && wc <= MAX_PROPOSAL_WORDS;
  if (!proposalText.trim()) return t.waiting;
  if (wc > MAX_PROPOSAL_WORDS) return t.tooLong;
  if (withinGrace) return t.allowedException || "Allowed exception";
  if (within && proposalHasParagraphs) return t.good;
  if (within) return t.needsTightening;
  return t.notReady;
}, [proposalWordCount, proposalText, proposalHasParagraphs, t]);

  const wordCountColor = useMemo(() => {
    if (proposalWordCount === 0) return "#6b7280";
    if (proposalWordCount > MAX_PROPOSAL_WORDS) return "#b91c1c";
    if (proposalWordCount > RECOMMENDED_MAX_PROPOSAL_WORDS) return "#92400e";
    if (proposalWordCount >= MIN_PROPOSAL_WORDS) return "#065f46";
    return "#92400e";
  }, [proposalWordCount]);

  const canonWordCountColor = useMemo(() => {
    if (canonWordCount === 0) return "#6b7280";
    if (canonWordCount > MAX_PROPOSAL_WORDS) return "#b91c1c";
    if (canonWordCount > RECOMMENDED_MAX_PROPOSAL_WORDS) return "#92400e";
    if (canonWordCount >= MIN_PROPOSAL_WORDS) return "#065f46";
    return "#92400e";
  }, [canonWordCount]);

  const writerNoteText = useMemo(() => {
  if (!activeStory) return "";
  if (isWriter) return t.ownersManageProposals;
  if (isGroupStory && !isGroupMember) {
    return isGroupClosed
      ? t.closedGroupOnlyInvited
      : t.groupOnlyInvited;
  }
  if (!isGroupStory && !isWriterRole) {
    return t.collabRequiresWriterAccount;
  }
  if (!proposalsOpen) return t.proposalsClosedWaitWriter;
  return "";
}, [
  activeStory,
  isWriter,
  isGroupStory,
  isGroupMember,
  isGroupClosed,
  isWriterRole,
  proposalsOpen,
  t,
]);

  const writerStudioAiDraft = useMemo(() => {
    const canon = String(canonText || "").trim();
    const proposal = String(proposalText || "").trim();

    if (proposal) {
      return {
        context: "proposal",
        title: proposalTitle,
        text: proposal,
      };
    }

    return {
      context: "canon",
      title: canonTitle,
      text: canon,
    };
  }, [canonTitle, canonText, proposalTitle, proposalText]);

  const writerStudioAiDisabledReason = useMemo(() => {
    if (!activeStory) return String(lang || "en").toLowerCase().startsWith("fr") ? "Choisissez d’abord une storyline." : "Choose a storyline first.";
    if (getKnownPublicationInfo(activeStory, lang).isPublished) {
      return String(lang || "en").toLowerCase().startsWith("fr")
        ? "La relecture est désactivée pour les livres publiés."
        : "Review is disabled for published books.";
    }
    if (!String(writerStudioAiDraft?.text || "").trim()) {
      return String(lang || "en").toLowerCase().startsWith("fr")
        ? "Ouvrez une fenêtre d’écriture et rédigez une scène avant la relecture."
        : "Open a writing window and draft a scene before review.";
    }
    return "";
  }, [activeStory, lang, writerStudioAiDraft]);

  const activeWorkspaceStories = useMemo(() => {
    return (stories || []).filter((story) => !getKnownPublicationInfo(story, lang).isPublished);
  }, [stories, lang]);

  const focusedChapter = useMemo(() => {
    if (!activeStory || !focusedChapterId) return null;
    const chapters = Array.isArray(activeStory?.chapters)
      ? activeStory.chapters
      : [];
    return (
      chapters.find(
        (chapter) =>
          String(chapter?.id || "") ===
          String(focusedChapterId)
      ) || null
    );
  }, [activeStory, focusedChapterId]);

  const focusedScene = useMemo(() => {
    if (!focusedSceneId) return null;
    return (
      canonicalScenes.find(
        (scene) =>
          getSceneId(scene) ===
          String(focusedSceneId)
      ) || null
    );
  }, [canonicalScenes, focusedSceneId]);

  const focusedChapterScenes = useMemo(() => {
    if (!focusedChapter) return [];

    const ids =
      Array.isArray(focusedChapter?.sceneIds)
        ? focusedChapter.sceneIds
            .map((value) =>
              String(value || "").trim()
            )
            .filter(Boolean)
        : [];

    if (ids.length === 0) return [];

    const byId =
      new Map(
        canonicalScenes.map((scene) => [
          getSceneId(scene),
          scene,
        ])
      );

    return ids
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [canonicalScenes, focusedChapter]);

  const focusedSceneBelongsToChapter =
    Boolean(
      focusedScene &&
      focusedChapter &&
      focusedChapterScenes.some(
        (scene) =>
          getSceneId(scene) ===
          getSceneId(focusedScene)
      )
    );

  const currentStudioScene =
    focusedChapter
      ? (
          focusedSceneBelongsToChapter
            ? focusedScene
            : focusedChapterScenes[
                focusedChapterScenes.length - 1
              ] || null
        )
      : (
          focusedScene ||
          (canonicalScenes.length > 0
            ? canonicalScenes[
                canonicalScenes.length - 1
              ]
            : null)
        );

  const focusedChapterIndex =
    studioChapters.findIndex(
      (chapter) =>
        String(chapter?.id || "") ===
        String(focusedChapterId || "")
    );

  function getCanonSceneLocation(scene) {
    if (!scene) {
      return {
        chapterIndex: -1,
        chapterNumber: null,
        chapterTitle: "",
        chapterSceneIndex: -1,
        chapterSceneNumber: null,
        canonSceneNumber: null,
      };
    }

    const sceneId =
      String(
        getSceneId(scene) || ""
      ).trim();

    const chapterIndex =
      sceneId
        ? studioChapters.findIndex(
            (chapter) =>
              Array.isArray(
                chapter?.sceneIds
              ) &&
              chapter.sceneIds.some(
                (value) =>
                  String(
                    value || ""
                  ).trim() === sceneId
              )
          )
        : -1;

    const chapter =
      chapterIndex >= 0
        ? studioChapters[
            chapterIndex
          ]
        : null;

    const chapterSceneIndex =
      chapter && sceneId
        ? (
            Array.isArray(
              chapter?.sceneIds
            )
              ? chapter.sceneIds
              : []
          ).findIndex(
            (value) =>
              String(
                value || ""
              ).trim() === sceneId
          )
        : -1;

    const explicitSceneNumber =
      Number(
        scene?.sceneNumber
      );

    const canonicalIndex =
      canonicalScenes.findIndex(
        (item) =>
          getSceneId(item) ===
          sceneId
      );

    return {
      chapterIndex,
      chapterNumber:
        chapterIndex >= 0
          ? chapterIndex + 1
          : null,
      chapterTitle:
        String(
          chapter?.title || ""
        ).trim(),
      chapterSceneIndex,
      chapterSceneNumber:
        chapterSceneIndex >= 0
          ? chapterSceneIndex + 1
          : null,
      canonSceneNumber:
        Number.isFinite(
          explicitSceneNumber
        ) &&
        explicitSceneNumber > 0
          ? explicitSceneNumber
          : (
              canonicalIndex >= 0
                ? canonicalIndex + 1
                : null
            ),
    };
  }

  function formatCanonSceneLocation(
    scene,
    { upper = false } = {}
  ) {
    const location =
      getCanonSceneLocation(scene);

    const isFR =
      String(lang || "en")
        .toLowerCase()
        .startsWith("fr");

    const chapterPart =
      location.chapterNumber
        ? (
            isFR
              ? `Chapitre ${location.chapterNumber}`
              : `Chapter ${location.chapterNumber}`
          )
        : (
            isFR
              ? "Chapitre non attribué"
              : "Chapter unassigned"
          );

    // Match Book Structure: the Scene label is Chapter-local whenever a
    // saved Chapter owns it. The book-wide Canon number remains unchanged.
    const displayedSceneNumber = displayedNovelSceneNumberV1(location);
    const scenePart =
      displayedSceneNumber
        ? (
            isFR
              ? `Scène ${displayedSceneNumber}`
              : `Scene ${displayedSceneNumber}`
          )
        : (
            isFR
              ? "Scène"
              : "Scene"
          );

    const label =
      `${chapterPart} · ${scenePart}`;

    return upper
      ? label.toUpperCase()
      : label;
  }

  const currentChapterSceneIndex =
    currentStudioScene
      ? focusedChapterScenes.findIndex(
          (scene) =>
            getSceneId(scene) ===
            getSceneId(currentStudioScene)
        )
      : -1;

  // "Current Canon" means the newest approved Canon scene.
  // On initial load and whenever a new Canon scene is created/adopted,
  // focus the latest Canon once. After that, the Owner may still browse
  // older chapter scenes without being snapped back on every render.
  useEffect(() => {
    // A chapter-scoped new-Scene handoff must not be overridden by the
    // latest-Canon focus effect before the Owner saves that Scene.
    if (
      showAddCanonModal && !editingCanonSceneId &&
      writerStudioSceneContext.newScene &&
      writerStudioSceneContext.chapterId &&
      (
        !writerStudioSceneContext.storyId ||
        String(writerStudioSceneContext.storyId) === String(activeStoryId)
      )
    ) return;
    if (
      !activeStoryId ||
      canonicalScenes.length === 0
    ) {
      return;
    }

    const latestScene =
      canonicalScenes[
        canonicalScenes.length - 1
      ];

    const latestSceneId =
      String(
        getSceneId(
          latestScene
        ) || ""
      ).trim();

    if (!latestSceneId) return;

    const focusKey =
      `${String(
        activeStoryId
      )}:${latestSceneId}`;

    if (
      latestCanonFocusAppliedRef
        .current === focusKey
    ) {
      return;
    }

    const location =
      getCanonSceneLocation(
        latestScene
      );

    const latestChapterId =
      location.chapterIndex >= 0
        ? String(
            studioChapters[
              location.chapterIndex
            ]?.id || ""
          ).trim()
        : "";

    latestCanonFocusAppliedRef.current =
      focusKey;

    setFocusedSceneId(
      latestSceneId
    );
    setFocusedChapterId(
      latestChapterId
    );
    setTargetChapterId(
      latestChapterId
    );

    syncWriterStudioWorkUrl({
      chapterId:
        latestChapterId,
      sceneId:
        latestSceneId,
    });
  }, [
    activeStoryId,
    canonicalScenes,
    studioChapters,
    showAddCanonModal,
    editingCanonSceneId,
    writerStudioSceneContext,
  ]);

  const ownerWriterStudioPathWorkspaces =
    activeStory
      ? [
          {
            id: "write",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Écrire"
                : "Write",
            status:
              isCollaborativeMode &&
              proposalsOpen
                ? "waiting"
                : currentStudioScene
                ? "ready"
                : "in_progress",
            statusLabel:
              isCollaborativeMode &&
              proposalsOpen
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Canon verrouillé"
                      : "Canon locked"
                  )
                : isCollaborativeMode
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Mode Canon actif"
                      : "Canon Mode active"
                  )
                : currentStudioScene
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Scène enregistrée"
                      : "Scene saved"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Commencer"
                      : "Start"
                  ),
          },
          {
            id: "review",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Réviser"
                : "Review",
            status:
              currentStudioScene
                ? "ready"
                : "waiting",
            statusLabel:
              currentStudioScene
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Disponible"
                      : "Available"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "En attente"
                      : "Waiting"
                  ),
          },
          {
            id: "collaboration",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Collaborer"
                : "Collaborate",
            status:
              isSoloMode
                ? "waiting"
                : proposalsOpen ||
                  proposedScenes.length > 0
                ? "needs_attention"
                : "ready",
            statusLabel:
              isSoloMode
                ? "Solo"
                : proposalsOpen
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Mode Proposition actif"
                      : "Proposal Mode active"
                  )
                : isCollaborativeMode
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Propositions verrouillées"
                      : "Proposals locked"
                  )
                : proposedScenes.length > 0
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "À examiner"
                      : "Review"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Disponible"
                      : "Available"
                  ),
          },
          {
            id: "memory",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Soutien"
                : "Story Support",
            status: "ready",
            statusLabel:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Disponible"
                : "Available",
          },
          {
            id: "reader",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Lecteurs"
                : "Reader Response",
            status:
              canonicalScenes.length > 0
                ? "ready"
                : "waiting",
            statusLabel:
              comments.length > 0
                ? String(comments.length)
                : canonicalScenes.length > 0
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Disponible"
                      : "Available"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Plus tard"
                      : "Later"
                  ),
          },
        ]
      : [];

  const contributorWriterStudioPathWorkspaces =
    activeStory
      ? [
          {
            id:
              "collaboration",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Collaborer"
                : "Collaborate",
            status:
              hasOwnProposalThisRound
                ? "ready"
                : proposalsOpen
                ? "in_progress"
                : "waiting",
            statusLabel:
              hasOwnProposalThisRound
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Proposition soumise"
                      : "Proposal submitted"
                  )
                : proposalsOpen
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Soumettre une proposition"
                      : "Submit one proposal"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Verrouillé · Mode Canon actif"
                      : "Locked · Canon Mode active"
                  ),
          },
          {
            id:
              "memory",
            label:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Référence de l’histoire"
                : "Story Reference",
            status:
              "ready",
            statusLabel:
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Lecture seule"
                : "Read only",
          },
          {
            id: "reader",
            label: String(lang || "en").toLowerCase().startsWith("fr")
              ? "Réponse des lecteurs" : "Reader Response",
            status: canonicalScenes.length > 0 ? "ready" : "waiting",
            statusLabel: comments.length > 0 ? String(comments.length)
              : canonicalScenes.length > 0
              ? (String(lang || "en").toLowerCase().startsWith("fr") ? "Disponible" : "Available")
              : (String(lang || "en").toLowerCase().startsWith("fr") ? "Plus tard" : "Later"),
          },
        ]
      : [];

  const writerStudioPathWorkspaces =
    isContributorWorkspace
      ? contributorWriterStudioPathWorkspaces
      : ownerWriterStudioPathWorkspaces;

  function syncWriterStudioWorkUrl({
    chapterId = "",
    sceneId = "",
    newScene = false,
  } = {}) {
    try {
      const url =
        new URL(window.location.href);

      if (activeStoryId) {
        url.searchParams.set(
          "storyId",
          String(activeStoryId)
        );
      }

      if (chapterId) {
        url.searchParams.set(
          "chapterId",
          String(chapterId)
        );
      } else {
        url.searchParams.delete(
          "chapterId"
        );
      }

      if (sceneId) {
        url.searchParams.set(
          "sceneId",
          String(sceneId)
        );
      } else {
        url.searchParams.delete(
          "sceneId"
        );
      }

      if (newScene) {
        url.searchParams.set(
          "newScene",
          "1"
        );
      } else {
        url.searchParams.delete(
          "newScene"
        );
      }

      window.history.replaceState(
        {},
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch {}
  }

  function selectStudioScene(sceneId) {
    const id =
      String(sceneId || "").trim();

    if (!id) return;

    const scene =
      focusedChapterScenes.find(
        (item) =>
          getSceneId(item) === id
      );

    if (!scene) return;

    setFocusedSceneId(id);
    setWriterStudioStage(
      isContributorWorkspace
        ? "collaboration"
        : "write"
    );
    setShowAddCanonModal(false);
    setEditingCanonSceneId("");

    syncWriterStudioWorkUrl({
      chapterId: focusedChapterId,
      sceneId: id,
    });
  }

  function selectStudioBookSection(sectionId) {
    // Book Matter is edited in Book Workspace, not in Canon Chapters.
    if (sectionId === "__book_front_matter__") {
      returnToNovelBook("front");
      return;
    }
    if (sectionId === "__book_back_matter__") {
      returnToNovelBook("back");
      return;
    }
    if (sectionId) selectStudioChapter(sectionId);
  }

  function selectStudioChapter(chapterId) {
    const id =
      String(chapterId || "").trim();

    if (!id) return;

    const chapter =
      studioChapters.find(
        (item) =>
          String(item?.id || "") === id
      );

    if (!chapter) return;

    const sceneIds =
      Array.isArray(chapter?.sceneIds)
        ? chapter.sceneIds
            .map((value) =>
              String(value || "").trim()
            )
            .filter(Boolean)
        : [];

    const lastSceneId =
      [...sceneIds]
        .reverse()
        .find((sceneId) =>
          canonicalScenes.some(
            (scene) =>
              getSceneId(scene) ===
              sceneId
          )
        ) || "";

    setFocusedChapterId(id);
    setTargetChapterId(id);
    setFocusedSceneId(lastSceneId);
    setWriterStudioStage(
      isContributorWorkspace
        ? "collaboration"
        : "write"
    );
    setShowAddCanonModal(false);
    setEditingCanonSceneId("");

    syncWriterStudioWorkUrl({
      chapterId: id,
      sceneId: lastSceneId,
    });
  }

  function moveStudioChapter(direction) {
    if (
      focusedChapterIndex < 0 ||
      studioChapters.length === 0
    ) {
      return;
    }

    const nextIndex =
      direction === "previous"
        ? focusedChapterIndex - 1
        : focusedChapterIndex + 1;

    if (
      nextIndex < 0 ||
      nextIndex >= studioChapters.length
    ) {
      return;
    }

    selectStudioChapter(
      studioChapters[nextIndex]?.id
    );
  }

  function moveStudioScene(direction) {
    if (
      currentChapterSceneIndex < 0 ||
      focusedChapterScenes.length === 0
    ) {
      return;
    }

    const nextIndex =
      direction === "previous"
        ? currentChapterSceneIndex - 1
        : currentChapterSceneIndex + 1;

    if (
      nextIndex < 0 ||
      nextIndex >= focusedChapterScenes.length
    ) {
      return;
    }

    selectStudioScene(
      getSceneId(
        focusedChapterScenes[nextIndex]
      )
    );
  }

  function openCanonSceneForEditing(scene) {
    if (!scene) return;

    const sceneId = getSceneId(scene);
    if (!sceneId) return;

    setEditingCanonSceneId(sceneId);
    setTargetChapterId(
      focusedChapterId || ""
    );
    setCanonTitle(
      String(scene?.title || "")
    );
    setCanonText(
      String(scene?.text || "")
    );
    setCanonManuscriptContent(
      scene?.manuscriptContent &&
      typeof scene.manuscriptContent === "object" &&
      !Array.isArray(
        scene.manuscriptContent
      )
        ? scene.manuscriptContent
        : null
    );
    setCanonIllustrationUrl("");
    setCanonIllustrationCaption("");
    setCanonIllustrationCredit("");
    setCanonIllustrationAltText("");
    setCanonIllustrationConfirmed(false);
    setCanonMessage(
      String(lang || "en")
        .toLowerCase()
        .startsWith("fr")
        ? "Modification de la scène enregistrée."
        : "Editing the saved Scene."
    );
    setShowAddCanonModal(true);
  }

  function openNewCanonSceneForFocusedChapter() {
    const currentSceneId =
      String(
        focusedSceneId ||
        getSceneId(
          currentStudioScene
        ) ||
        ""
      ).trim();

    const currentSceneChapter =
      currentSceneId
        ? studioChapters.find(
            (chapter) =>
              Array.isArray(
                chapter?.sceneIds
              ) &&
              chapter.sceneIds.some(
                (sceneId) =>
                  String(
                    sceneId || ""
                  ) === currentSceneId
              )
          ) || null
        : null;

    const fallbackChapterId =
      String(
        focusedChapterId ||
        currentSceneChapter?.id ||
        currentSceneChapter?._id ||
        (
          studioChapters.length === 1
            ? (
                studioChapters[0]?.id ||
                studioChapters[0]?._id ||
                ""
              )
            : ""
        )
      ).trim();

    setEditingCanonSceneId("");
    setTargetChapterId(
      fallbackChapterId
    );
    setCanonTitle("");
    setCanonText("");
    setCanonManuscriptContent(null);
    setCanonIllustrationUrl("");
    setCanonIllustrationCaption("");
    setCanonIllustrationCredit("");
    setCanonIllustrationAltText("");
    setCanonIllustrationConfirmed(false);
    setCanonMessage(
      String(lang || "en")
        .toLowerCase()
        .startsWith("fr")
        ? (
            fallbackChapterId
              ? "Nouvelle scène pour le chapitre sélectionné."
              : "Nouvelle scène Canon."
          )
        : (
            fallbackChapterId
              ? "New Scene for the selected Chapter."
              : "New Canon Scene."
          )
    );
    setShowAddCanonModal(true);
  }

  function returnToNovelBook(bookSection = "", chapterReturnId = "") {
    if (!activeStoryId) return;
    // One Writer Studio, but retain the saved book's real category on return.
    const nativeChildrensBook = activeStory?.bookCategory === "children_book";

    const matter =
      bookSection === "front" || bookSection === "back"
        ? bookSection
        : "";

    if (
      typeof onNavigatePage ===
      "function"
    ) {
      onNavigatePage(
        "projectHub",
        {
          projectCategory: nativeChildrensBook ? "children" : "story",
          projectId:
            String(activeStoryId),
          projectTitle:
            String(
              activeStory?.title || ""
            ),
          ...(matter ? { bookSection: matter } : {}),
          ...(nativeChildrensBook && chapterReturnId ? { chapterId: chapterReturnId } : {}),
        }
      );
      return;
    }

    try {
      navigate(
        nativeChildrensBook
          ? "/my-projects/childrens-book"
          : `/my-projects?storyId=${encodeURIComponent(
              String(activeStoryId)
            )}${matter ? `&bookSection=${matter}` : ""}`
      );
    } catch {}
  }

  // Filters: Forum options from current stories
  const forumOptions = useMemo(() => {
  const labels = t?.forum_labels && typeof t.forum_labels === "object"
    ? Object.keys(t.forum_labels)
    : [];

  return ["All", ...labels.sort((a, b) => a.localeCompare(b))];
}, [t]);

  const filteredStories = useMemo(() => {
    const q = String(storySearch || "").trim().toLowerCase();
    const ff = String(filterForum || "All").trim();
    const fm = String(filterMode || "All").trim();

    return (activeWorkspaceStories || []).filter((story) => {
      const title = String(story?.title || "").toLowerCase();
      const forum = String(story?.forum || "").toLowerCase();
      const author = String(
        story?.author || story?.authorUsername || story?.createdBy || ""
      ).toLowerCase();

      const modeDisplay = deriveModeForDisplay(story);

      const matchesSearch =
        !q || title.includes(q) || forum.includes(q) || author.includes(q);
      const matchesForum =
        ff === "All" || String(story?.forum || "").trim() === ff;
      const matchesMode = modeMatchesFilter(modeDisplay, fm);

      return matchesSearch && matchesForum && matchesMode;
    });
  }, [activeWorkspaceStories, storySearch, filterForum, filterMode]);

  function clearFilters() {
    setStorySearch("");
    setFilterForum("All");
    setFilterMode("All");
  }

  function scrollStorylineStrip(direction) {
    const el = storyStripRef.current;
    if (!el) return;
    const amount = Math.max(320, Math.floor(el.clientWidth * 0.82));
    try {
      el.scrollBy({ left: direction * amount, behavior: "smooth" });
    } catch {
      el.scrollLeft += direction * amount;
    }
  }

  // Reader View: use the same navigation mechanism as the top nav
// (App is page-state driven; router paths may redirect to Home).
function goReaderViewForStory(storyId) {
  const sid = String(storyId || "").trim();
  if (!sid) return;

  // Keep ReaderView in sync with the studio selection
  try {
    localStorage.setItem("ns.selectedStoryId", sid);
  } catch (e) {}

  // If App passes a page switcher, use it (matches the working top nav).
  if (typeof onNavigatePage === "function") {
    // Try the most likely route keys (App.js variations over time)
      const targets = ["readerView", "reader", "readerview", "reader_view"];
      for (const t of targets) {
        try {
          onNavigatePage(t);
          break;
        } catch (e) {
          // keep trying
        }
      }
    return;
  }

  // Router fallback (if your App is route-based in a given environment)
  const candidates = ["/reader", "/readerview", "/reader-view"];
  for (const path of candidates) {
    try {
      navigate(path);
      return;
    } catch (e) {}
  }

  try {
    window.location.assign("/reader");
  } catch (e) {
    window.location.href = "/reader";
  }
}




// Unified, calm loading gate: avoids blank layout + flicker during session restore / story fetch.
const studioLoading = !!booting || !!loadingStories;
if (studioLoading) {
  return (
  <div className="ns-page">
    <header
      className="ns-page-header ns-writer-desk-hero"
      style={{
        backgroundImage:
          'linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("/images/storyhub/storyhub-background.png")',
        backgroundSize: "cover",
        backgroundPosition: "center center",
      }}
    >
      <div style={{ maxWidth: 760 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            width: "fit-content",
            padding: "5px 10px",
            borderRadius: 999,
            background: "rgba(255, 250, 240, 0.12)",
            border: "1px solid rgba(211, 181, 115, 0.34)",
            color: "#e2c991",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          NextScenes
        </div>

        <h1
          className="ns-page-title"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          {t.writerStudioTitle}
        </h1>

        <p className="ns-page-subtitle">
          {t.preparingWorkspace}
        </p>

        <p className="ns-card-meta">
          {t.writerStudioAnchor}
        </p>
      </div>
    </header>

      <div
        className="ns-card"
        style={{ maxWidth: 760, margin: "18px auto", textAlign: "center" }}
      >
        <p className="ns-card-meta" style={{ marginBottom: 6 }}>
          {t.restoringSession}
        </p>
        <p className="ns-card-meta" style={{ margin: 0 }}>
          {t.pleaseWait}
        </p>
      </div>
    </div>
  );
}

  return (
    <div
      className="ns-page ns-writer-studio-page"
      style={{
        "--ns-writer-bg": `url(${process.env.PUBLIC_URL}/images/writer/writer-background.png)`,
      }}
    >
      <ProjectBreadcrumbTrail
        onNavigatePage={onNavigatePage}
        lang={lang}
        pageKey="writerStudio"
        projectTitle={
          activeStory?.title ||
          projectHubContext?.projectTitle
        }
      />

      <header
        className="ns-page-header ns-writer-desk-hero"
        style={{
          display: activeStory
            ? "none"
            : undefined,
          backgroundImage:
            'linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("/images/storyhub/storyhub-background.png")',
          backgroundSize: "cover",
          backgroundPosition: "center center",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0, maxWidth: 780 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                width: "fit-content",
                padding: "5px 10px",
                borderRadius: 999,
                background: "rgba(255, 250, 240, 0.12)",
                border: "1px solid rgba(211, 181, 115, 0.34)",
                color: "#e2c991",
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              NextScenes
            </div>

            <h1
              className="ns-page-title"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              {t.writerStudioTitle}
            </h1>

            <p className="ns-page-subtitle">
              {t.writerStudioAnchor || t.writerStudioSubtitle}
            </p>

            {storiesError && (
              <p
                className="ns-card-meta"
                style={{
                  color: "#fecaca",
                  marginTop: 10,
                  padding: "8px 10px",
                  borderRadius: 12,
                  background: "rgba(127, 29, 29, 0.22)",
                  border: "1px solid rgba(254, 202, 202, 0.26)",
                }}
              >
                {storiesError}
              </p>
            )}
          </div>

          <aside
            aria-label={lang === "fr" ? "État du Writer Studio" : "Writer Studio status"}
            style={{
              flex: "0 1 260px",
              minWidth: 220,
              alignSelf: "stretch",
              borderRadius: 20,
              padding: "16px 18px",
              background: "rgba(255, 250, 240, 0.13)",
              border: "1px solid rgba(211, 181, 115, 0.34)",
              boxShadow: "0 16px 36px rgba(0, 0, 0, 0.18)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                color: "rgba(255, 250, 240, 0.74)",
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
              }}
            >
              {lang === "fr" ? "Mode actuel" : "Current mode"}
            </span>
            <strong
              style={{
                color: "#fffaf0",
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 22,
                lineHeight: 1.1,
              }}
            >
              {modeBadge?.title || (lang === "fr" ? "Salle d’écriture" : "Writer Room")}
            </strong>
            <span style={{ color: "rgba(255, 250, 240, 0.78)", fontSize: 13, lineHeight: 1.45 }}>
              {activeStory
                ? `${canonicalScenes.length} ${lang === "fr" ? "scènes canon" : "canon scenes"} · ${proposedScenes.length} ${lang === "fr" ? "propositions" : "proposals"}`
                : lang === "fr"
                ? "Choisissez une storyline"
                : "Choose a storyline"}
            </span>
          </aside>
        </div>
      </header>

      {!activeStory ? (
        <WriterStudioCommandCenter
          activeStory={activeStory}
          lang={lang}
          modeBadge={modeBadge}
          proposalsOpen={proposalsOpen}
          canonicalScenes={canonicalScenes}
          proposedScenes={proposedScenes}
          analytics={analytics}
          isWriter={isWriter}
          isSoloMode={isSoloMode}
          canSeeProposals={canSeeProposals}
          canPropose={canPropose}
          onAddCanon={() => {
            setCanonMessage("");
            setShowAddCanonModal(true);
          }}
          onOpenStoryInfo={() => setShowStoryInfo(true)}
          onSubmitProposal={() => {
            setProposalMessage("");
            setShowAddProposalModal(true);
          }}
          onOpenProposalRound={handleRequestOpenProposals}
          onReviewProposals={() => {}}
          onOpenReaderView={() => {
            goReaderViewForStory(
              activeStory?._id ||
                activeStory?.id ||
                activeStoryId
            );
          }}
        />
      ) : null}

      {activeStory ? (
        <div
          data-writer-studio-blueprint-focus="true"
          style={{
            maxWidth: 1180,
            margin: "0 auto",
          }}
        >
          <NextScenesBookBlueprintNavigator
            currentWorkspace={writerStudioStage}
            projectTitle={activeStory?.title || ""}
            untitledTitle={
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
                ? "Histoire sans titre"
                : "Untitled Story"
            }
            workspaces={writerStudioPathWorkspaces}
            readyCount={
              writerStudioPathWorkspaces.filter(
                (item) =>
                  item.status === "ready" ||
                  item.status === "published"
              ).length
            }
            statusTotal={writerStudioPathWorkspaces.length}
            onNavigate={(workspaceId) => {
              if (
                isContributorWorkspace &&
                workspaceId !==
                  "collaboration" &&
                workspaceId !==
                  "memory" &&
                workspaceId !==
                  "reader"
              ) {
                return;
              }

              if (
                isWriter &&
                isCollaborativeMode &&
                proposalsOpen &&
                workspaceId ===
                  "write"
              ) {
                setProposalMessage(
                  String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Mode Canon verrouillé pendant le Mode Proposition. Résolvez le tour par Approuver et adopter ou Rejeter toutes les propositions."
                    : "Canon Mode is locked while Proposal Mode is active. Resolve the round with Approve & Adopt or Reject All Proposals."
                );
                setWriterStudioStage(
                  "collaboration"
                );
                return;
              }

              setWriterStudioStage(
                workspaceId
              );
            }}
            isFR={
              String(lang || "en")
                .toLowerCase()
                .startsWith("fr")
            }
            accent="#047857"
            accentDark="#065f46"
            accentSoft="#ecfdf5"
            border="rgba(4,120,87,0.24)"
            background="linear-gradient(180deg, rgba(236,253,245,0.96) 0%, #ffffff 100%)"
            blueprintLabel={
              isContributorWorkspace
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Parcours Contributeur"
                      : "Contributor Path"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Parcours Writer Studio"
                      : "Writer Studio Path"
                  )
            }
          />

          <section
            className="ns-card ws-current-work-card"
            data-writer-studio-current-work="true"
            style={{
              padding: 18,
              borderRadius: 20,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#047857",
                }}
              >
                {isContributorWorkspace
                  ? (
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "RÉFÉRENCE CANON"
                        : "CANON REFERENCE"
                    )
                  : (
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "ESPACE DE TRAVAIL"
                        : "CURRENT WORK"
                    )}
              </div>
              <h2
                className="ns-section-title ws-current-work-title"
                style={{
                  margin: "4px 0 6px",
                }}
              >
                {activeStory.title}
              </h2>

              <div className="ws-current-work-context">
                {isContributorWorkspace ? (
                  <span className="ws-current-work-context-chip is-scene">
                    {currentStudioScene
                      ? (
                          <>
                            {String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Dernière scène Canon"
                              : "Latest Canon Scene"}{" "}
                            {String(
                              currentStudioScene.sceneNumber ||
                                canonicalScenes.indexOf(
                                  currentStudioScene
                                ) + 1
                            )}
                          </>
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Aucune scène Canon"
                            : "No Canon Scene yet"
                        )}
                  </span>
                ) : (
                  <>
                    <span className="ws-current-work-context-chip">
                      {focusedChapter
                        ? focusedChapter.title ||
                          focusedChapter.label ||
                          (
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Chapitre sélectionné"
                              : "Selected Chapter"
                          )
                        : (
                            currentStudioScene &&
                            getCanonSceneLocation(currentStudioScene).chapterIndex < 0
                              ? (
                                  String(lang || "en")
                                    .toLowerCase()
                                    .startsWith("fr")
                                    ? "Scène non affectée"
                                    : "Unassigned Scene"
                                )
                              : (
                                  String(lang || "en")
                                    .toLowerCase()
                                    .startsWith("fr")
                                    ? "Aucun chapitre sélectionné"
                                    : "No Chapter selected"
                                )
                          )}
                    </span>

                    {currentStudioScene ? (
                      <span className="ws-current-work-context-chip is-scene">
                        {String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Scène"
                          : "Scene"}{" "}
                        {String(
                          currentChapterSceneIndex >= 0
                            ? currentChapterSceneIndex + 1
                            : (
                                currentStudioScene.sceneNumber ||
                                canonicalScenes.indexOf(
                                  currentStudioScene
                                ) + 1
                              )
                        )}
                      </span>
                    ) : (
                      <span className="ws-current-work-context-chip is-empty">
                        {String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Nouvelle scène"
                          : "New Scene"}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div
              className="ws-current-work-controls"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {!isContributorWorkspace ? (
                <>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() =>
                      moveStudioChapter(
                        "previous"
                      )
                    }
                    disabled={
                      focusedChapterIndex <= 0
                    }
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "← Chapitre précédent"
                      : "← Previous Chapter"}
                  </button>

                  <select
                    className="ns-input ws-current-chapter-select"
                    value={String(focusedChapterId || "")}
                    onChange={(event) =>
                      selectStudioBookSection(event.target.value)
                    }
                    aria-label={
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Aller à une section du livre"
                        : "Go to Book Section"
                    }
                  >
                    <option value="">
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Aller à une section du livre"
                        : "Go to Book Section"}
                    </option>
                    <option value="__book_front_matter__">
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Pages liminaires"
                        : "Front Matter"}
                    </option>
                    {studioChapters.map((chapter, index) => (
                      <option
                        key={chapter?.id || index}
                        value={chapter?.id || ""}
                      >
                        {chapter?.title || `Chapter ${index + 1}`}
                      </option>
                    ))}
                    <option value="__book_back_matter__">
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Éléments de fin"
                        : "Back Matter"}
                    </option>
                  </select>

                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() =>
                      moveStudioChapter(
                        "next"
                      )
                    }
                    disabled={
                      focusedChapterIndex < 0 ||
                      focusedChapterIndex >=
                        studioChapters.length - 1
                    }
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Chapitre suivant →"
                      : "Next Chapter →"}
                  </button>
                </>
              ) : null}

              {!isContributorWorkspace &&
              activeWorkspaceStories.length > 1 ? (
                <select
                  className="ns-input"
                  value={
                    String(
                      activeStoryId || ""
                    )
                  }
                  onChange={(event) => {
                    const next =
                      activeWorkspaceStories.find(
                        (story) =>
                          String(
                            story?._id ||
                              story?.id ||
                              ""
                          ) ===
                          String(
                            event.target.value
                          )
                      );
                    if (next) {
                      handleSelectStory(next);
                      setFocusedSceneId("");
                      setFocusedChapterId("");
                      setWriterStudioStage("write");
                    }
                  }}
                  aria-label={
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Changer de projet"
                      : "Change project"
                  }
                  style={{
                    minWidth: 210,
                  }}
                >
                  {activeWorkspaceStories.map(
                    (story) => (
                      <option
                        key={
                          story?._id ||
                          story?.id
                        }
                        value={
                          story?._id ||
                          story?.id
                        }
                      >
                        {story?.title ||
                          "Untitled"}
                      </option>
                    )
                  )}
                </select>
              ) : null}

              {!isContributorWorkspace &&
                activeStory?.bookCategory === "children_book" &&
                focusedChapter &&
                focusedChapterId ? (
                <button type="button"
                  className="ns-button ns-button-outline"
                  data-native-childrens-back-to-current-chapter-v1f2e="true"
                  onClick={() => returnToNovelBook("", String(focusedChapterId))}
                >
                  {String(lang || "en").toLowerCase().startsWith("fr")
                    ? "← Retour au chapitre actuel"
                    : "← Back to Current Chapter"}
                </button>
              ) : null}

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={
                  isContributorWorkspace
                    ? () =>
                        onNavigatePage?.(
                          "projectHub",
                          {
                            projectCategory:
                              "story",
                          }
                        )
                    : () => returnToNovelBook()
                }
              >
                {isContributorWorkspace
                  ? (
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "← Retour à Roman / Histoire"
                        : "← Back to Novel / Story"
                    )
                  : (
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "← Retour au livre"
                        : "← Back to Book"
                    )}
              </button>
            </div>
          </section>

          {isCollaborativeMode ? (
            <section
              className={
                "ws-collaborative-mode-constitution " +
                (
                  proposalsOpen
                    ? "is-proposal-mode"
                    : "is-canon-mode"
                )
              }
              data-collaborative-mode-constitution="true"
              data-active-collaborative-mode={
                proposalsOpen
                  ? "proposal"
                  : "canon"
              }
            >
              <div className="ws-collaborative-mode-summary">
                <div>
                  <div className="ws-collaborative-mode-kicker">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "MODE D’ÉCRITURE ACTIF"
                      : "ACTIVE WRITING MODE"}
                  </div>

                  <h3>
                    {proposalsOpen
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Mode Proposition actif"
                            : "Proposal Mode Active"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Mode Canon actif"
                            : "Canon Mode Active"
                        )}
                  </h3>

                  <p>
                    {isContributorWorkspace
                      ? (
                          proposalsOpen
                            ? (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Le Canon est verrouillé pour l’auteur pendant ce tour. Les contributeurs peuvent soumettre une proposition en attente à la fois, comparer les alternatives et voter sur celles des autres."
                                  : "Canon is locked for the owner during this round. Contributors may keep one pending proposal at a time, compare alternatives, and vote on other contributors’ proposals."
                              )
                            : (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Le Mode Proposition est verrouillé pendant que l’auteur écrit le Canon. Aucune proposition ni vote n’est possible jusqu’à l’ouverture d’un nouveau tour."
                                  : "Proposal Mode is locked while the owner writes Canon. No proposal or vote is available until the owner opens a new Proposal Round."
                              )
                        )
                      : (
                          proposalsOpen
                            ? (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Le Canon est verrouillé. Le tour reste ouvert jusqu’à ce que vous approuviez et adoptiez une proposition ou rejetiez toutes les propositions. Rejeter une proposition individuelle ne ferme pas le tour."
                                  : "Canon is locked. The round remains open until you Approve & Adopt one proposal or Reject All Proposals. Rejecting an individual proposal does not close the round."
                              )
                            : (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Vous pouvez écrire, modifier et continuer le Canon comme en mode Solo. Pour inviter des contributeurs, ouvrez Collaboration, puis choisissez « Ouvrir un tour de propositions »."
                                  : "You may write, edit, and continue Canon as in Solo mode. To invite contributors, open Collaboration and choose Open Proposal Round."
                              )
                        )}
                  </p>
                </div>

              </div>

              <div className="ws-collaborative-mode-switch">
                <div
                  className={
                    "ws-collaborative-mode-card " +
                    (
                      proposalsOpen
                        ? "is-locked"
                        : "is-active"
                    )
                  }
                >
                  <span className="ws-collaborative-mode-card-label">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "MODE CANON"
                      : "CANON MODE"}
                  </span>
                  <strong>
                    {proposalsOpen
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "VERROUILLÉ"
                            : "LOCKED"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "ACTIF"
                            : "ACTIVE"
                        )}
                  </strong>
                  <small>
                    {proposalsOpen
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Aucune modification du Canon pendant le tour."
                            : "No Canon changes while the round is open."
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "L’auteur poursuit librement le Canon."
                            : "Owner continues Canon freely."
                        )}
                  </small>
                </div>

                <div
                  className="ws-collaborative-mode-arrow"
                  aria-hidden="true"
                >
                  ⇄
                </div>

                <div
                  className={
                    "ws-collaborative-mode-card " +
                    (
                      proposalsOpen
                        ? "is-active"
                        : "is-locked"
                    )
                  }
                >
                  <span className="ws-collaborative-mode-card-label">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "MODE PROPOSITION"
                      : "PROPOSAL MODE"}
                  </span>
                  <strong>
                    {proposalsOpen
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "ACTIF"
                            : "ACTIVE"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "VERROUILLÉ"
                            : "LOCKED"
                        )}
                  </strong>
                  <small>
                    {proposalsOpen
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Contributeurs : proposer, comparer et voter."
                            : "Contributors: propose, compare, and vote."
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "S’ouvre uniquement lorsque l’auteur demande de l’aide."
                            : "Opens only when the owner asks for help."
                        )}
                  </small>
                </div>
              </div>
            </section>
          ) : null}

          {writerStudioStage === "write" ? (
            <section
              className="ns-card"
              data-writer-studio-stage="write"
              style={{
                padding: 20,
                borderRadius: 20,
                border:
                  "1px solid rgba(4,120,87,0.24)",
                background:
                  "linear-gradient(180deg, #ffffff 0%, rgba(236,253,245,0.54) 100%)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing:
                        "0.12em",
                      textTransform:
                        "uppercase",
                      color: "#047857",
                    }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "ÉTAPE 1"
                      : "STEP 1"}
                  </div>
                  <h3
                    className="ns-card-title"
                    style={{
                      margin: "4px 0 0",
                      fontSize: 24,
                    }}
                  >
                    {currentStudioScene
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Scène enregistrée"
                            : "Saved Scene"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Écrire la scène"
                            : "Write the Scene"
                        )}
                  </h3>
                </div>

                {currentStudioScene ? (
                  <span
                    style={{
                      display:
                        "inline-flex",
                      alignItems:
                        "center",
                      gap: 6,
                      padding:
                        "7px 11px",
                      borderRadius: 999,
                      background:
                        "#dcfce7",
                      color: "#166534",
                      fontWeight: 900,
                      fontSize: 13,
                    }}
                  >
                    ✓{" "}
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Enregistrée"
                      : "Saved"}
                  </span>
                ) : null}
              </div>

              {currentStudioScene ? (
                <div
                  style={{
                    border:
                      "1px solid #dbe2ea",
                    borderRadius: 16,
                    padding: 18,
                    background: "#ffffff",
                  }}
                >
                  <div className="ws-canon-scene-heading">
                    <div>
                      <div className="ws-canon-scene-kicker">
                        {formatCanonSceneLocation(
                          currentStudioScene,
                          {
                            upper: true,
                          }
                        )}
                      </div>

                      <h3
                        style={{
                          margin: "4px 0 0",
                        }}
                      >
                        {currentStudioScene.title ||
                          (
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Scène sans titre"
                              : "Untitled Scene"
                          )}
                      </h3>
                    </div>

                    {focusedChapterScenes.length > 1 ? (
                      <div className="ws-canon-scene-navigation">
                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          onClick={() =>
                            moveStudioScene(
                              "previous"
                            )
                          }
                          disabled={
                            currentChapterSceneIndex <= 0
                          }
                        >
                          {String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "← Scène précédente"
                            : "← Previous Scene"}
                        </button>

                        <select
                          className="ns-input"
                          value={
                            getSceneId(
                              currentStudioScene
                            )
                          }
                          onChange={(event) =>
                            selectStudioScene(
                              event.target.value
                            )
                          }
                          aria-label={
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Choisir une scène"
                              : "Choose Scene"
                          }
                        >
                          {focusedChapterScenes.map(
                            (scene, index) => (
                              <option
                                key={
                                  getSceneId(scene) ||
                                  index
                                }
                                value={
                                  getSceneId(scene)
                                }
                              >
                                {`Scene ${index + 1}`} ·{" "}
                                {scene?.title ||
                                  (
                                    String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "Sans titre"
                                      : "Untitled"
                                  )}
                              </option>
                            )
                          )}
                        </select>

                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          onClick={() =>
                            moveStudioScene(
                              "next"
                            )
                          }
                          disabled={
                            currentChapterSceneIndex < 0 ||
                            currentChapterSceneIndex >=
                              focusedChapterScenes.length - 1
                          }
                        >
                          {String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Scène suivante →"
                            : "Next Scene →"}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div
                    className="ws-canon-scene-reader"
                    data-writer-studio-canon-reader="true"
                  >
                    <NovelStorySceneReadOnly
                      scene={
                        currentStudioScene
                      }
                      lang={lang}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap:
                        "wrap",
                      marginTop: 16,
                    }}
                  >
                    <button
                      type="button"
                      className="ns-button ns-button-primary"
                      onClick={() =>
                        openCanonSceneForEditing(
                          currentStudioScene
                        )
                      }
                      disabled={!isWriter}
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Modifier la scène"
                        : "Edit Scene"}
                    </button>

                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={
                        openNewCanonSceneForFocusedChapter
                      }
                      disabled={
                        !isWriter ||
                        (!isSoloMode &&
                          proposalsOpen)
                      }
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "+ Nouvelle scène"
                        : "+ New Scene"}
                    </button>

                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={() =>
                        setWriterStudioStage(
                          "review"
                        )
                      }
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Réviser cette scène →"
                        : "Review this Scene →"}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    padding: 20,
                    borderRadius: 16,
                    border:
                      "1px dashed rgba(4,120,87,0.36)",
                    background:
                      "rgba(236,253,245,0.55)",
                  }}
                >
                  <p
                    style={{
                      margin:
                        "0 0 14px",
                      lineHeight: 1.6,
                    }}
                  >
                    {focusedChapterId
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Ce chapitre n’a pas encore de scène enregistrée. Commencez ici."
                            : "This Chapter does not yet have a saved Scene. Start here."
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Ouvrez d’abord un chapitre depuis le livre. Writer Studio gardera ensuite ce chapitre et cette scène au centre du travail."
                            : "Open a Chapter from the book first. Writer Studio will then keep that Chapter and Scene at the centre of the work."
                        )}
                  </p>

                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    onClick={
                      focusedChapterId
                        ? openNewCanonSceneForFocusedChapter
                        : returnToNovelBook
                    }
                    disabled={!isWriter}
                  >
                    {focusedChapterId
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Écrire la scène"
                            : "Write Scene"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Choisir un chapitre"
                            : "Choose a Chapter"
                        )}
                  </button>
                </div>
              )}
            </section>
          ) : null}

          {writerStudioStage === "review" ? (
            <section
              className="ns-card"
              data-writer-studio-stage="review"
              style={{
                padding: 20,
                borderRadius: 20,
              }}
            >
              <div
                style={{
                  marginBottom: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing:
                        "0.12em",
                      textTransform:
                        "uppercase",
                      color: "#047857",
                    }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "ÉTAPE 2"
                      : "STEP 2"}
                  </div>
                  <h3
                    className="ns-card-title"
                    style={{
                      margin: "4px 0 4px",
                      fontSize: 24,
                    }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Révision"
                      : "Review"}
                  </h3>
                  <p
                    className="ns-card-meta"
                    style={{ margin: 0 }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Facultatif. Demandez une relecture seulement lorsque vous le souhaitez."
                      : "Optional. Request a review only when you want one."}
                  </p>
                </div>

                <button
                  type="button"
                  className="ns-button ns-button-outline"
                  onClick={() =>
                    setWriterStudioStage("write")
                  }
                  disabled={!currentStudioScene}
                >
                  {String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "← Retour à la scène"
                    : "← Back to Scene"}
                </button>
              </div>

              {currentStudioScene ? (
                <WriterStudioAiAssistancePlaceholder
                  lang={lang}
                  activeStory={activeStory}
                  sceneTitle={
                    currentStudioScene.title ||
                    ""
                  }
                  sceneText={
                    currentStudioScene.text ||
                    ""
                  }
                  reviewContext="canon"
                  compact={true}
                  onBackToScene={() =>
                    setWriterStudioStage("write")
                  }
                  disabled={
                    !isWriter ||
                    getKnownPublicationInfo(
                      activeStory,
                      lang
                    ).isPublished
                  }
                  disabledReason={
                    !isWriter
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Seul l’auteur peut demander une révision de la scène Canon."
                            : "Only the author can request Canon Scene review."
                        )
                      : ""
                  }
                />
              ) : (
                <p className="ns-card-meta">
                  {String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Enregistrez d’abord une scène."
                    : "Save a Scene first."}
                </p>
              )}
            </section>
          ) : null}

          {writerStudioStage === "collaboration" ? (
            <section
              className="ns-card ws-collaboration-stage"
              data-writer-studio-stage="collaboration"
              style={{
                padding: 20,
                borderRadius: 20,
              }}
            >
              <div className="ws-collaboration-stage-head">
                <div>
                  <div className="ws-collaboration-kicker">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "ÉTAPE 3"
                      : "STEP 3"}
                  </div>

                  <h3
                    className="ns-card-title"
                    style={{
                      margin: "4px 0 4px",
                      fontSize: 24,
                    }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Collaboration"
                      : "Collaboration"}
                  </h3>

                  <div className="ws-collaboration-status-row">
                    <span className="ws-collaboration-mode-pill">
                      {deriveModeForDisplay(
                        activeStory
                      )}
                    </span>

                    <span
                      className={
                        "ws-collaboration-round-pill" +
                        (proposalsOpen
                          ? " is-open"
                          : " is-closed")
                      }
                    >
                      {proposalsOpen
                        ? (
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Tour ouvert"
                              : "Round open"
                          )
                        : (
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Canon ouvert"
                              : "Canon open"
                          )}
                    </span>

                    {!isSoloMode ? (
                      <>
                        <span className="ws-collaboration-count-pill">
                          {proposedScenes.length}{" "}
                          {String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? (
                                proposedScenes.length === 1
                                  ? "proposition"
                                  : "propositions"
                              )
                            : (
                                proposedScenes.length === 1
                                  ? "proposal"
                                  : "proposals"
                              )}
                        </span>

                        {isContributorWorkspace &&
                        hasOwnProposalThisRound ? (
                          <span className="ws-collaboration-count-pill is-own">
                            {String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Votre proposition est soumise"
                              : "Your proposal is submitted"}
                          </span>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="ws-collaboration-head-actions">
                  {!isSoloMode &&
                  isWriter &&
                  !proposalsOpen ? (
                    <button
                      type="button"
                      className="ns-button ns-button-primary"
                      onClick={
                        handleRequestOpenProposals
                      }
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Ouvrir un tour de propositions"
                        : "Open Proposal Round"}
                    </button>
                  ) : null}

                  {canCancelEmptyRound ? (
                    <button type="button" className="ns-button ns-button-outline"
                      data-writer-studio-cancel-empty-round="true"
                      onClick={() => setShowCancelEmptyRoundModal(true)}>
                      {String(lang||"en").toLowerCase().startsWith("fr")
                        ? "Annuler le tour vide" : "Cancel Empty Round"}
                    </button>
                  ) : null}

                  {!isWriter &&
                  canPropose ? (
                    <button
                      type="button"
                      className="ns-button ns-button-primary"
                      onClick={() => {
                        setProposalMessage("");
                        setShowAddProposalModal(
                          true
                        );
                      }}
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Soumettre une proposition"
                        : "Submit Proposal"}
                    </button>
                  ) : null}

                  {isContributorWorkspace &&
                  hasOwnPendingProposal ? (
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      disabled
                      title={
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Une seule proposition est autorisée par contributeur pour chaque tour. Attendez que l’auteur ouvre un nouveau tour de propositions."
                          : "One proposal is allowed per contributor per Proposal Round. Wait until the owner opens a new Proposal Round."
                      }
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Proposition soumise"
                        : "Proposal Submitted"}
                    </button>
                  ) : null}

                  {isContributorWorkspace ? (
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={() =>
                        setShowCanonWindow(
                          true
                        )
                      }
                      disabled={
                        canonicalScenes.length ===
                        0
                      }
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Voir les scènes Canon"
                        : "View Validated Scenes"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={() =>
                        setWriterStudioStage("write")
                      }
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? (
                            proposalsOpen
                              ? "← Retour à la scène"
                              : "← Continuer le Canon dans Écrire"
                          )
                        : (
                            proposalsOpen
                              ? "← Back to Scene"
                              : "← Continue Canon in Write"
                          )}
                    </button>
                  )}
                </div>
              </div>

              {isSoloMode ? (
                <div className="ws-collaboration-empty-card">
                  <strong>
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Aucune action de collaboration requise"
                      : "No collaboration action needed"}
                  </strong>
                  <p className="ns-card-meta">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Ce livre est en mode Solo. Continuez simplement à écrire ou à réviser votre scène."
                      : "This book is in Solo mode. Continue writing or reviewing your Scene."}
                  </p>
                </div>
              ) : proposedScenes.length === 0 ? (
                <div className="ws-collaboration-empty-card">
                  <strong>
                    {isContributorWorkspace
                      ? (
                          proposalsOpen
                            ? (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Vous pouvez soumettre une proposition"
                                  : "You may submit one proposal"
                              )
                            : (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Le tour de propositions est fermé"
                                  : "Proposal Round is closed"
                              )
                        )
                      : (
                          proposalsOpen
                            ? (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Le tour est ouvert"
                                  : "Proposal Round is open"
                              )
                            : (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Aucun tour actif"
                                  : "No active Proposal Round"
                              )
                        )}
                  </strong>

                  <p className="ns-card-meta">
                    {isContributorWorkspace
                      ? (
                          proposalsOpen
                            ? (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Soumettez une seule alternative pour ce tour. Après soumission, votre proposition restera visible ici jusqu’à la décision de l’auteur."
                                  : "Submit one alternative for this round. After submission, your proposal stays visible here until the owner decides it."
                              )
                            : (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Aucune nouvelle proposition ne peut être soumise tant que l’auteur n’ouvre pas un nouveau tour."
                                  : "No new proposal can be submitted until the owner opens another round."
                              )
                        )
                      : (
                          proposalsOpen
                            ? (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Aucune proposition n’a encore été soumise. Les propositions apparaîtront ici comme des alternatives à examiner."
                                  : "No proposals have been submitted yet. Proposals will appear here as alternatives to review."
                              )
                            : (
                                String(lang || "en")
                                  .toLowerCase()
                                  .startsWith("fr")
                                  ? "Le Canon reste votre espace d’écriture principal. Ouvrez un tour uniquement lorsque vous souhaitez inviter des alternatives."
                                  : "Canon remains your main writing space. Open a round only when you want to invite alternatives."
                              )
                        )}
                  </p>
                </div>
              ) : (
                <>
                  <div className="ws-collaboration-shelf-head">
                    <div>
                      <strong>
                        {isContributorWorkspace
                          ? (
                              String(lang || "en")
                                .toLowerCase()
                                .startsWith("fr")
                                ? "Propositions de ce tour"
                                : "Proposals in this Round"
                            )
                          : (
                              String(lang || "en")
                                .toLowerCase()
                                .startsWith("fr")
                                ? "Propositions à examiner"
                                : "Proposals to Review"
                            )}
                      </strong>

                      <p className="ns-card-meta">
                        {isContributorWorkspace
                          ? (
                              String(lang || "en")
                                .toLowerCase()
                                .startsWith("fr")
                                ? "Comparez toutes les alternatives soumises. Vous pouvez voter une fois sur la proposition d’un autre contributeur. Seul l’auteur peut approuver ou rejeter."
                                : "Compare all submitted alternatives. You may vote once on another contributor’s proposal. Only the owner can approve or reject."
                            )
                          : (
                              String(lang || "en")
                                .toLowerCase()
                                .startsWith("fr")
                                ? "Toutes les alternatives restent visibles ici. Consultez chacune, puis approuvez et adoptez-en une ou rejetez celles qui ne conviennent pas."
                                : "All alternatives remain visible here. View each one, then approve and adopt one or reject those that do not fit."
                            )}
                      </p>
                    </div>

                    {proposedScenes.length > 1 ? (
                      <div
                        className="ws-collaboration-shelf-nav"
                        aria-label={
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Navigation des propositions"
                            : "Proposal navigation"
                        }
                      >
                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          onClick={() =>
                            scrollCollaborationProposals(
                              "left"
                            )
                          }
                          aria-label={
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Propositions précédentes"
                              : "Previous proposals"
                          }
                        >
                          ◀
                        </button>

                        <span className="ns-card-meta">
                          {proposedScenes.length}{" "}
                          {String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "alternatives"
                            : "alternatives"}
                        </span>

                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          onClick={() =>
                            scrollCollaborationProposals(
                              "right"
                            )
                          }
                          aria-label={
                            String(lang || "en")
                              .toLowerCase()
                              .startsWith("fr")
                              ? "Propositions suivantes"
                              : "Next proposals"
                          }
                        >
                          ▶
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div
                    ref={collaborationProposalShelfRef}
                    className="ws-collaboration-proposal-shelf"
                    data-novel-story-proposal-shelf="true"
                  >
                    {proposedScenes.map(
                      (scene, index) => {
                        const sceneId =
                          getSceneId(scene);

                        const author =
                          scene.authorName ||
                          scene.authorUsername ||
                          scene.author ||
                          t.unknown;

                        const previewText =
                          String(
                            scene.text || ""
                          ).trim();

                        const myVote =
                          sceneId
                            ? userVotes?.[
                                sceneId
                              ]
                            : undefined;

                        const localTotals =
                          sceneId
                            ? voteTotals?.[
                                sceneId
                              ]
                            : null;

                        const votesUp =
                          localTotals
                            ? toSafeInt(
                                localTotals.up
                              )
                            : toSafeInt(
                                scene.votesUp
                              );

                        const votesDown =
                          localTotals
                            ? toSafeInt(
                                localTotals.down
                              )
                            : toSafeInt(
                                scene.votesDown
                              );

                        const votesNeutral =
                          localTotals
                            ? toSafeInt(
                                localTotals.neutral
                              )
                            : toSafeInt(
                                scene.votesNeutral
                              );

                        const meRaw =
                          String(
                            username || ""
                          ).trim();

                        const isOwnProposal =
                          getSceneAuthors(
                            scene
                          ).some((value) =>
                            identitiesMatch(
                              value,
                              meRaw
                            )
                          );

                        const mayVote =
                          isContributorWorkspace &&
                          proposalsOpen &&
                          !isOwnProposal &&
                          myVote ===
                            undefined;

                        return (
                          <article
                            key={
                              sceneId ||
                              scene.createdAt
                            }
                            className={
                              "ws-collaboration-proposal-row" +
                              (
                                isOwnProposal
                                  ? " is-own"
                                  : ""
                              )
                            }
                            data-proposal-index={
                              index + 1
                            }
                          >
                            <div className="ws-collaboration-proposal-main">
                              <div className="ws-collaboration-proposal-number">
                                {index + 1}
                              </div>

                              <div className="ws-collaboration-proposal-copy">
                                <strong>
                                  {scene.title ||
                                    t.untitledProposal}
                                </strong>

                                <div className="ns-card-meta">
                                  {t.by}{" "}
                                  {author}
                                  {isOwnProposal
                                    ? (
                                        String(lang || "en")
                                          .toLowerCase()
                                          .startsWith("fr")
                                          ? " · Votre proposition"
                                          : " · Your proposal"
                                      )
                                    : ""}
                                </div>

                                <div className="ws-collaboration-proposal-votes">
                                  <span>
                                    👍 {votesUp}
                                  </span>
                                  <span>
                                    ◻︎ {votesNeutral}
                                  </span>
                                  <span>
                                    👎 {votesDown}
                                  </span>
                                </div>

                                {previewText ? (
                                  <p className="ws-collaboration-proposal-preview">
                                    {previewText.length > 180
                                      ? previewText.slice(
                                          0,
                                          180
                                        ) + "…"
                                      : previewText}
                                  </p>
                                ) : null}
                              </div>
                            </div>

                            <div className="ws-collaboration-proposal-actions">
                              <button
                                type="button"
                                className={
                                  isWriter
                                    ? "ns-button ns-button-primary"
                                    : "ns-button ns-button-outline"
                                }
                                onClick={() =>
                                  setFullProposalScene(
                                    scene
                                  )
                                }
                              >
                                {isWriter
                                  ? (
                                      String(lang || "en")
                                        .toLowerCase()
                                        .startsWith("fr")
                                        ? "Relire et sélectionner"
                                        : "Review & Select"
                                    )
                                  : (
                                      String(lang || "en")
                                        .toLowerCase()
                                        .startsWith("fr")
                                        ? "Voir"
                                        : "View"
                                    )}
                              </button>

                              {isContributorWorkspace ? (
                                isOwnProposal ? (
                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    disabled
                                    title={
                                      String(lang || "en")
                                        .toLowerCase()
                                        .startsWith("fr")
                                        ? "Vous ne pouvez pas voter pour votre propre proposition."
                                        : "You cannot vote on your own proposal."
                                    }
                                  >
                                    {String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "Votre proposition"
                                      : "Your Proposal"}
                                  </button>
                                ) : myVote !==
                                  undefined ? (
                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    disabled
                                  >
                                    {String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "Vote enregistré"
                                      : "Voted"}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    disabled={
                                      !mayVote ||
                                      proposalVoteBusy
                                    }
                                    onClick={() => {
                                      setProposalMessage(
                                        ""
                                      );
                                      setVoteProposalScene(
                                        scene
                                      );
                                    }}
                                  >
                                    {String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "Voter"
                                      : "Vote"}
                                  </button>
                                )
                              ) : null}

                              {isWriter ? (
                                <>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={() =>
                                      handleRejectProposal(
                                        sceneId
                                      )
                                    }
                                  >
                                    {String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "Rejeter"
                                      : "Reject"}
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </article>
                        );
                      }
                    )}
                  </div>

                  {isWriter &&
                  proposalsOpen ? (
                    <div className="ws-collaboration-round-footer">
                      <p className="ns-card-meta">
                        {String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Approuver et adopter une proposition termine le tour et écarte les autres. Rejeter une proposition individuelle laisse le tour ouvert."
                          : "Approve & Adopt ends the round and discards the other remaining proposals. Rejecting one proposal keeps the round open."}
                      </p>

                      <button
                        type="button"
                        className="ns-button ns-button-outline"
                        onClick={
                          handleRejectAllProposals
                        }
                      >
                        {String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Rejeter toutes les propositions"
                          : "Reject All Proposals"}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          ) : null}

          {writerStudioStage === "memory" ? (
            <section
              className="ns-card ws-story-memory-stage"
              data-writer-studio-stage="memory"
              style={{
                padding: 20,
                borderRadius: 20,
              }}
            >
              <div className="ws-story-memory-head">
                <div>
                  <div className="ws-story-memory-kicker">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "ÉTAPE 4"
                      : "STEP 4"}
                  </div>

                  <h3
                    className="ns-card-title"
                    style={{
                      margin: "4px 0 4px",
                      fontSize: 24,
                    }}
                  >
                    {isContributorWorkspace
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Référence de l’histoire"
                            : "Story Reference"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Soutien à l’histoire"
                            : "Story Support"
                        )}
                  </h3>

                  <p
                    className="ns-card-meta"
                    style={{ margin: 0 }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Deux outils de référence. Ouvrez-les seulement quand la continuité l’exige."
                      : "Two reference tools. Open them only when continuity work needs them."}
                  </p>
                </div>

                <button
                  type="button"
                  className="ns-button ns-button-outline"
                  onClick={() =>
                    setWriterStudioStage(
                      isContributorWorkspace
                        ? "collaboration"
                        : "write"
                    )
                  }
                >
                  {isContributorWorkspace
                    ? (
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "← Retour à la collaboration"
                          : "← Back to Collaboration"
                      )
                    : (
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "← Retour à la scène"
                          : "← Back to Scene"
                      )}
                </button>
              </div>

              <div className="ws-story-memory-tools">
                <button
                  type="button"
                  className="ws-story-memory-tool"
                  onClick={() => {
                    setShowStoryHistory(false);
                    setShowStoryInfo(true);
                  }}
                >
                  <span className="ws-story-memory-tool-index">
                    1
                  </span>

                  <span className="ws-story-memory-tool-copy">
                    <strong>
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Informations sur l’histoire"
                        : "Story Info"}
                    </strong>

                    <span>
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Règles, synopsis, mode d’écriture et repères du projet."
                        : "Story rules, synopsis, writing mode and project reference."}
                    </span>
                  </span>

                  <span className="ws-story-memory-tool-action">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Ouvrir →"
                      : "Open →"}
                  </span>
                </button>

                <button
                  type="button"
                  className="ws-story-memory-tool"
                  onClick={() =>
                    setShowCharactersPanel(
                      true
                    )
                  }
                >
                  <span className="ws-story-memory-tool-index">
                    2
                  </span>

                  <span className="ws-story-memory-tool-copy">
                    <strong>
                      Character Bible
                    </strong>

                    <span>
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Personnages, voix, relations et faits de continuité."
                        : "Characters, voice, relationships and continuity facts."}
                    </span>
                  </span>

                  <span className="ws-story-memory-tool-action">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Ouvrir →"
                      : "Open →"}
                  </span>
                </button>
              </div>

              <div className="ws-story-memory-note">
                {isContributorWorkspace
                  ? (
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Utilisez Informations sur l’histoire et Character Bible comme références de continuité. L’historique complet reste réservé à l’auteur."
                        : "Use Story Info and Character Bible as continuity references. Full Story History remains reserved for the owner."
                    )
                  : (
                      String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "L’historique complet reste disponible dans Informations sur l’histoire, mais il reste masqué jusqu’à ce que vous le demandiez."
                        : "Full Story History remains available inside Story Info, but stays out of the way until you ask for it."
                    )}
              </div>
            </section>
          ) : null}

          {writerStudioStage === "reader" ? (
            <section
              className="ns-card"
              data-writer-studio-stage="reader"
              style={{
                padding: 20,
                borderRadius: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems:
                    "flex-start",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing:
                        "0.12em",
                      textTransform:
                        "uppercase",
                      color: "#047857",
                    }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? (isContributorWorkspace ? "ÉTAPE 3" : "ÉTAPE 5")
                      : (isContributorWorkspace ? "STEP 3" : "STEP 5")}
                  </div>
                  <h3
                    className="ns-card-title"
                    style={{
                      margin:
                        "4px 0 4px",
                      fontSize: 24,
                    }}
                  >
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Réponse des lecteurs"
                      : "Reader Response"}
                  </h3>
                  {isContributorWorkspace ? (
                    <p className="ns-card-meta" data-contributor-reader-response-native="true" style={{ margin: "3px 0 7px" }}>
                      {String(lang || "en").toLowerCase().startsWith("fr")
                        ? "Commentez et répondez ici, dans Writer Studio."
                        : "Comment and reply here in Writer Studio."}
                    </p>
                  ) : null}
                  <p
                    className="ns-card-meta"
                    style={{ margin: 0 }}
                  >
                    {comments.length}{" "}
                    {comments.length === 1
                      ? t.commentSingle
                      : t.comments}
                  </p>
                </div>

                <button
                  type="button"
                  className={isContributorWorkspace ? "ns-button ns-button-outline" : "ns-button ns-button-primary"}
                  onClick={() =>
                    goReaderViewForStory(
                      activeStoryId
                    )
                  }
                  disabled={
                    canonicalScenes.length ===
                    0
                  }
                >
                  {String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Ouvrir Reader View"
                    : "Open Reader View"}
                </button>
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: "grid",
                  gap: 10,
                }}
              >
                {threadedComments.parents.length > 5 ? (
                  <div
                    data-reader-response-discussion-controls="true"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span className="ns-card-meta">
                      {String(lang || "en").toLowerCase().startsWith("fr")
                        ? `Affichage de ${showAllReaderDiscussions ? threadedComments.parents.length : 5} sur ${threadedComments.parents.length} discussions récentes chargées.`
                        : `Showing ${showAllReaderDiscussions ? threadedComments.parents.length : 5} of ${threadedComments.parents.length} recent loaded discussions.`}
                    </span>
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={() => setShowAllReaderDiscussions((previous) => !previous)}
                    >
                      {showAllReaderDiscussions
                        ? (String(lang || "en").toLowerCase().startsWith("fr")
                            ? "Afficher moins de discussions"
                            : "Show fewer discussions")
                        : (String(lang || "en").toLowerCase().startsWith("fr")
                            ? "Afficher plus de discussions"
                            : "Show more discussions")}
                    </button>
                  </div>
                ) : null}

                {threadedComments.parents.length ===
                0 ? (
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      background:
                        "#f8fafc",
                      border:
                        "1px solid #e2e8f0",
                    }}
                  >
                    <p
                      className="ns-card-meta"
                      style={{ margin: 0 }}
                    >
                      {String(lang || "en")
                        .toLowerCase()
                        .startsWith("fr")
                        ? "Aucun commentaire lecteur pour le moment."
                        : "No reader comments yet."}
                    </p>
                  </div>
                ) : (
                  threadedComments.parents
                    .slice(0, showAllReaderDiscussions ? undefined : 5)
                    .map((comment) => {
                      const replies =
                        threadedComments
                          .repliesByParent[
                          String(
                            comment.id
                          )
                        ] || [];
                      const replyOpen =
                        replyOpenFor ===
                        comment.id;

                      return (
                        <article
                          key={
                            comment.id
                          }
                          className="ns-card ns-card-soft"
                          style={{
                            padding: 14,
                          }}
                        >
                          <div
                            className="ns-card-meta"
                            style={{
                              margin: 0,
                            }}
                          >
                            <strong>
                              {
                                comment.author
                              }
                            </strong>
                            {" · "}
                            {formatDateTime(
                              comment.createdAt
                            )}
                          </div>
                          <p
                            style={{
                              lineHeight: 1.55,
                            }}
                          >
                            {comment.text}
                          </p>

                          {replies.length >
                          0 ? (
                            <p
                              className="ns-card-meta"
                              style={{
                                margin:
                                  "6px 0",
                              }}
                            >
                              {replies.length}{" "}
                              {replies.length ===
                              1
                                ? (
                                    String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "réponse"
                                      : "reply"
                                  )
                                : (
                                    String(lang || "en")
                                      .toLowerCase()
                                      .startsWith("fr")
                                      ? "réponses"
                                      : "replies"
                                  )}
                            </p>
                          ) : null}

                          {replies.length > 0 ? (
                            <div
                              data-reader-response-existing-replies="true"
                              style={{
                                display: "grid",
                                gap: 8,
                                margin: "8px 0 12px",
                                paddingLeft: 12,
                                borderLeft: "2px solid #d1fae5",
                              }}
                            >
                              {replies.map((reply) => (
                                <div key={reply.id} className="ns-card-meta">
                                  <strong>{reply.author}</strong>{" · "}
                                  {formatDateTime(reply.createdAt)}
                                  <p style={{ margin: "4px 0 0", lineHeight: 1.5 }}>
                                    {reply.text}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : null}

                          <button
                            type="button"
                            className="ns-button ns-button-outline"
                            onClick={() =>
                              toggleReplyBox(
                                comment.id
                              )
                            }
                            disabled={!canReply}
                          >
                            {replyOpen
                              ? (
                                  String(lang || "en")
                                    .toLowerCase()
                                    .startsWith("fr")
                                    ? "Fermer"
                                    : "Close"
                                )
                              : t.reply}
                          </button>

                          {replyOpen ? (
                            <form
                              onSubmit={(
                                event
                              ) =>
                                handlePostReply(
                                  event,
                                  comment.id
                                )
                              }
                              style={{
                                marginTop: 10,
                              }}
                            >
                              <textarea
                                className="ws-textarea ws-feedback-input"
                                rows={3}
                                value={
                                  replyDrafts[
                                    comment.id
                                  ] || ""
                                }
                                onChange={(
                                  event
                                ) =>
                                  setReplyDrafts(
                                    (
                                      previous
                                    ) => ({
                                      ...previous,
                                      [comment.id]:
                                        event
                                          .target
                                          .value,
                                    })
                                  )
                                }
                                placeholder={
                                  String(lang || "en")
                                    .toLowerCase()
                                    .startsWith("fr")
                                    ? "Écrire une réponse…"
                                    : "Write a reply…"
                                }
                              />
                              <div
                                style={{
                                  marginTop: 8,
                                  display:
                                    "flex",
                                  justifyContent:
                                    "flex-end",
                                }}
                              >
                                <button
                                  type="submit"
                                  className="ns-button ns-button-primary"
                                >
                                  {t.postReply}
                                </button>
                              </div>
                            </form>
                          ) : null}
                        </article>
                      );
                    })
                )}

                {canPostComment ? (
                  <form
                    onSubmit={
                      handlePostComment
                    }
                    style={{
                      paddingTop: 8,
                    }}
                  >
                    <textarea
                      className="ws-textarea ws-feedback-input"
                      rows={3}
                      value={commentText}
                      onChange={(event) =>
                        setCommentText(
                          event.target.value
                        )
                      }
                      placeholder={
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Partager un commentaire…"
                          : "Share feedback…"
                      }
                    />
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        justifyContent:
                          "flex-end",
                      }}
                    >
                      <button
                        type="submit"
                        className="ns-button ns-button-primary"
                      >
                        {t.writerStudioPostComment}
                      </button>
                    </div>
                  </form>
                ) : null}
              </div>
            </section>
          ) : null}

          {writerStudioStage === "reader" ? (
            <GeneralDiscussionsV1 lang={lang} />
          ) : null}

          <details
            className="ns-card"
            style={{
              marginTop: 14,
              padding: "12px 16px",
              borderRadius: 16,
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontWeight: 900,
                color: "#065f46",
              }}
            >
              {isContributorWorkspace
                ? (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Outils de référence"
                      : "Reference tools"
                  )
                : (
                    String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Plus d’outils"
                      : "More tools"
                  )}
            </summary>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 12,
              }}
            >
              {!isContributorWorkspace ? (
                <>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={handleShareStory}
                  >
                    {t.writerStudioShareStoryline}
                  </button>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() =>
                      setShowDraftWindow(true)
                    }
                    disabled={
                      !isWriter ||
                      canonicalScenes.length === 0
                    }
                  >
                    {t.writerStudioReadFullBookDraft}
                  </button>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() =>
                      setShowDecisionLog(true)
                    }
                    disabled={!isWriter}
                  >
                    {t.writerStudioDecisionLog}
                  </button>
                </>
              ) : null}

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  setShowCanonWindow(true)
                }
                disabled={
                  canonicalScenes.length === 0
                }
              >
                {t.writerStudioViewValidatedScenes}
              </button>
            </div>
          </details>
        </div>
      ) : null}

      {!loadingStories && stories.length === 0 && (
        <div className="ns-card">
          <p>{t.noStorylinesMessage}</p>
        </div>
      )}

      {!loadingStories && stories.length > 0 && activeWorkspaceStories.length === 0 && (
        <div className="ns-card ns-published-workspace-empty" role="note">
          <p className="ns-card-meta">
            {lang === "fr"
              ? "Toutes les storylines disponibles ici sont maintenant publiées ou complétées. Writer Studio affiche uniquement les storylines encore actives."
              : "All available storylines here are now published or completed. Writer Studio shows only storylines that are still active."}
          </p>
        </div>
      )}

      {!loadingStories && stories.length > 0 && !activeStory && (
                <div className="ws-studio-layout">
          {/* TOP ROW: Storylines (left) + Writer Room (right) */}
          <section className="ns-column ws-area-sidebar">
            <div className="ws-storyline-browser">
              <div className="ns-card ws-storyline-filterbar">
                <div className="ws-storyline-filterbar-head">
                  <div>
                    <h2 className="ns-section-title">{t.storylines}</h2>
                    <p className="ns-card-meta">{t.selectStorylineInstruction}</p>
                  </div>

                  <span className="ws-storyline-count-pill">
                    {t.showing} {filteredStories.length} {t.of} {activeWorkspaceStories.length}
                  </span>
                </div>

                <div className="ws-storyline-filter-grid">
                  <input
                    className="ns-input ws-storyline-search"
                    placeholder={t.searchPlaceholder}
                    value={storySearch}
                    onChange={(e) => setStorySearch(e.target.value)}
                  />

                  <div className="ws-storyline-filter-field">
                    <label>{t.forum}</label>
                    <select
                      className="ns-input"
                      value={filterForum}
                      onChange={(e) => setFilterForum(e.target.value)}
                    >
                      {forumOptions.map((forum) => (
                        <option key={forum} value={forum}>
                          {forum === "All" ? t.all_forums : (t.forum_labels?.[forum] || forum)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="ws-storyline-filter-field">
                    <label>{t.writingModeLabel}</label>
                    <select
                      className="ns-input"
                      value={filterMode}
                      onChange={(e) => setFilterMode(e.target.value)}
                    >
                      <option value="All">{t.all_writing_modes}</option>
                      {WRITING_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="ns-button ns-button-outline ws-storyline-clear"
                    onClick={clearFilters}
                    title={t.clearFiltersTitle}
                  >
                    {t.clear}
                  </button>
                </div>
              </div>

              <div className="ns-card ws-storyline-strip-card">
                <div className="ws-storyline-strip-head">
                  <div>
                    <h2 className="ns-section-title">
                      {lang === "fr" ? "Storylines disponibles" : "Available Storylines"}
                    </h2>
                    <p className="ns-card-meta">
                      {activeStory
                        ? `${lang === "fr" ? "Sélectionnée" : "Selected"}: ${activeStory.title}`
                        : t.selectStorylineInstruction}
                    </p>
                  </div>

                  <div className="ws-storyline-arrows" aria-label={lang === "fr" ? "Faire défiler les storylines" : "Scroll storylines"}>
                    <button
                      type="button"
                      className="ws-story-arrow"
                      onClick={() => scrollStorylineStrip(-1)}
                      aria-label={lang === "fr" ? "Défiler vers la gauche" : "Scroll left"}
                    >
                      ⇦
                    </button>
                    <button
                      type="button"
                      className="ws-story-arrow"
                      onClick={() => scrollStorylineStrip(1)}
                      aria-label={lang === "fr" ? "Défiler vers la droite" : "Scroll right"}
                    >
                      ⇨
                    </button>
                  </div>
                </div>

                <div
                  ref={storyStripRef}
                  className="ws-storyline-strip"
                  aria-label="Writer Studio storyline results"
                >
                  {(filteredStories || []).map((story) => {
                    const id = story._id || story.id;
                    const sid = id ? String(id) : "";
                    const isActive = sid && sid === String(selectedStoryId || "");
                    const scenes = Array.isArray(story.scenes) ? story.scenes : [];
                    const canonCount = scenes.filter(isCanonical).length;
                    const proposalCount = scenes.filter(isPendingProposal).length;
                    const modeDisplay = deriveModeForDisplay(story);
                    const authorInfo = getStoryAuthorDisplay(story, t.unknown);
                    const authorLabel = authorInfo.displayName;
                    const forumLabel = t.forum_labels?.[story.forum] || story.forum || t.unclassifiedForum;
                    const publicationInfo = getKnownPublicationInfo(story, lang);

                    return (
                      <article
                        key={sid || Math.random()}
                        ref={(el) => {
                          if (sid) storyItemRefs.current[String(sid)] = el;
                        }}
                        className={
                          "ws-story-card ws-story-card-readerlike ns-clickable" +
                          (isActive ? " ns-card-active is-active" : "")
                        }
                        onClick={() => handleSelectStory(story)}
                        aria-current={isActive ? "true" : undefined}
                      >
                        <WriterStudioBookThumb story={story} publicationInfo={publicationInfo} />
                        <div className="ws-story-card-topline">
                          <h3 className="ns-card-title">{story.title}</h3>
                          {isActive ? (
                            <span className="ws-story-selected-pill">
                              {t.selected || (lang === "fr" ? "Sélectionné" : "Selected")}
                            </span>
                          ) : null}
                        </div>

                        <div className="ws-story-card-badges">
                          <ModePill label={modeDisplay} />
                          <span className="ws-story-status-pill">
                            {story.proposalsOpen ? t.roundOpen : t.closed}
                          </span>
                          <PublicationStatusPill info={publicationInfo} />
                          <PreviewStatusPill info={publicationInfo} />
                        </div>

                        <div className="ws-story-card-meta">
                          <div className="ws-meta-line ws-author-inline">
                            <span className="ws-meta-label">{t.by}</span>
                            <WriterStudioAuthorAvatar story={story} label={authorLabel} />
                            <span>{authorLabel}</span>
                          </div>
                          <div className="ws-meta-line">
                            <span className="ws-meta-label">{t.forum}</span>: {forumLabel}
                          </div>
                          <div className="ws-meta-line">
                            <span className="ws-meta-label">{t.canon}</span>: <strong>{canonCount}</strong>
                            <span className="ws-meta-sep">·</span>
                            <span className="ws-meta-label">{t.proposals}</span>: <strong>{proposalCount}</strong>
                          </div>
                          <div className="ws-meta-line">
                            <span className="ws-meta-label">{t.proposals}</span>: {story.proposalsOpen ? t.open : t.closed}
                            <span className="ws-meta-sep">·</span>
                            <span className="ws-meta-label">{t.canon}</span>: {story.proposalsOpen ? t.closed : t.open}
                          </div>
                        </div>

                        <div className="ws-story-card-action-row">
                          <button
                            type="button"
                            className={"ws-story-card-action ws-story-card-action-button" + (isActive ? " is-active" : "")}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              openWriterRoom(story);
                            }}
                            aria-label={
                              isActive
                                ? `${story.title}: ${lang === "fr" ? "aller à la salle d’écriture" : "go to Writer's Room"}`
                                : `${story.title}: ${lang === "fr" ? "ouvrir la salle d’écriture" : "open Writer Room"}`
                            }
                          >
                            {isActive
                              ? (lang === "fr" ? "Dans la salle d’écriture" : "In Writer's Room")
                              : (lang === "fr" ? "Ouvrir cette salle" : "Open Writer Room")}
                          </button>
                          {publicationInfo.isPublished && publicationInfo.amazonHref ? (
                            <button
                              type="button"
                              className="ns-button ns-button-outline ns-amazon-action"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer");
                              }}
                            >
                              {publicationInfo.cta}
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <section id="writer-studio-active-room" tabIndex={-1} className="ns-column ws-area-roomtop">
            <div className="ns-card ns-card-soft ws-middle-shell">
                          {!activeStory && (
                            <p className="ns-card-meta">
                              Select a storyline above to start working.
                            </p>
                          )}

                          {activeStory && (
                            <>
                              <h2 className="ns-section-title ws-writer-room-title">
                                {activeStory.title} – Writer&apos;s Room
                              </h2>
                              {(() => {
                                const publicationInfo = getKnownPublicationInfo(activeStory, lang);
                                if (!publicationInfo.isPublished) return null;
                                return (
                                  <div className="ns-publication-panel" role="note">
                                    <div className="ns-publication-mini-row">
                                      <PublicationStatusPill info={publicationInfo} />
                                      <PreviewStatusPill info={publicationInfo} />
                                    </div>
                                    <p className="ns-card-meta">
                                      {lang === "fr"
                                        ? "Ce livre est publié. NextScenes conserve ici un aperçu et l’espace de suivi de l’histoire."
                                        : "This book is published. NextScenes keeps the preview and story workspace here."}
                                    </p>
                                    {publicationInfo.amazonHref ? (
                                      <button
                                        type="button"
                                        className="ns-button ns-button-outline ns-amazon-action"
                                        onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
                                      >
                                        {publicationInfo.cta}
                                      </button>
                                    ) : null}
                                  </div>
                                );
                              })()}

                              {(() => {
                                const publicationInfo = getKnownPublicationInfo(activeStory, lang);
                                if (!publicationInfo.isPublished) return null;
                                return (
                                  <PublishedWorkspaceNotice
                                    story={activeStory}
                                    publicationInfo={publicationInfo}
                                    lang={lang}
                                    onOpenAmazon={() =>
                                      publicationInfo.amazonHref
                                        ? window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")
                                        : null
                                    }
                                  />
                                );
                              })()}

                              {!getKnownPublicationInfo(activeStory, lang).isPublished ? (
                                <>
                              <div
                                className="ns-card ns-card-soft ns-writing-window"
                                style={{
                                  border: "var(--ns-writing-window-border, 2px solid #16a34a)",
                                  background: "#ffffff",
                                  marginTop: "12px",
                                  boxShadow: "0 10px 26px rgba(15, 23, 42, 0.10)",
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    inset: "0 0 auto 0",
                                    height: "10px",
                                    background: "linear-gradient(90deg, rgba(22,163,74,0.18), rgba(22,163,74,0.06))",
                                  }}
                                />
                                <h3 className="ns-card-title ws-section-header" style={{ marginTop: 0, paddingTop: "6px" }}>
                                    {t.writingWindows}
                                </h3>

                                <div
                                  className="ns-card-soft"
                                  style={{
                                    border: "1px solid #e5e7eb",
                                    background: "#f8fafc",
                                    borderRadius: "12px",
                                    padding: "10px 12px",
                                    marginTop: "8px",
                                  }}
                                >
                                  <div style={{ fontSize: "13px", color: "#0f172a", lineHeight: 1.45 }}>
                                    <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                                      {t.writerStudioWhereWorkHappens}
                                    </div>

                                    <div className="ws-writing-context-row">
                                      <span className="ws-writing-context-item">
                                        <strong>{t.writerStudioMode}</strong>{" "}
                                        {String(activeStory?.writingMode || "").trim() || t.unknown}
                                      </span>
                                      <span className="ws-writing-context-item">
                                        <strong>{t.writerStudioState}</strong>{" "}
                                        {modeBadge?.title || (proposalsOpen ? t.writerStudioProposalRoundOpen : t.writerStudioCanonMode)}
                                      </span>
                                      <span className="ws-writing-context-item ws-writing-context-author">
                                        <strong>{t.author}</strong>{" "}
                                        <WriterStudioAuthorAvatar story={activeStory} label={getStoryAuthorDisplay(activeStory, t.unknown).displayName} />
                                        <span>{getStoryAuthorDisplay(activeStory, t.unknown).displayName}</span>
                                      </span>
                                      <span className="ws-writing-context-item">
                                        <strong>{t.forum}</strong>{" "}
                                        {activeStory.forum || t.unclassifiedForum}
                                      </span>
                                    </div>

                                    <div style={{ marginTop: "6px", color: "#334155" }}>
                                     {proposalsOpen ? (
                                     <>
                                   {t.proposalInstruction}
                                    </>
                                   ) : (
                                    <>
                                     {t.writeCanonInstruction}
                                    </>
                                    )}
                                  </div>

                                     {!isWriter ? (
                                    <div style={{ marginTop: "6px", color: "#b91c1c" }}>
                                    {t.canonLocked}
                                    </div>
                                    ) : null}
                                  </div>
                                </div>

                                {getKnownPublicationInfo(activeStory, lang).isPublished ? (
                                  <p className="ns-card-meta" style={{ marginTop: "10px", color: "#92400e" }}>
                                    {lang === "fr"
                                      ? "Les outils d’écriture sont désactivés pour ce livre publié."
                                      : "Writing tools are disabled for this published book."}
                                  </p>
                                ) : null}

                                <WriterStudioAiAssistancePlaceholder
                                  lang={lang}
                                  activeStory={activeStory}
                                  sceneTitle={writerStudioAiDraft.title}
                                  sceneText={writerStudioAiDraft.text}
                                  reviewContext={writerStudioAiDraft.context}
                                  disabled={Boolean(writerStudioAiDisabledReason)}
                                  disabledReason={writerStudioAiDisabledReason}
                                />

                                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    onClick={() => {
                                      setCanonMessage("");
                                      setShowAddCanonModal(true);
                                    }}
                                    disabled={getKnownPublicationInfo(activeStory, lang).isPublished || !isWriter || (!isSoloMode && proposalsOpen)}
                                    title={
                                      !isWriter
                                        ? t.onlyWriterCanAddCanonScenes
                                        : t.openCanonWritingWindow
                                    }
                                  >
                                    ✍️ {t.writerStudioAddCanonSceneWindow}
                                  </button>

                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={() => setShowStoryInfo(true)}
                                    disabled={!activeStory}
                                    title={t.openStorylineConstitutionAndHistory}
                                  >
                                    📜 {t.writerStudioStoryInfoButton}
                                  </button>

                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={() => {
                                      setProposalMessage("");
                                      setShowAddProposalModal(true);
                                    }}
                                    disabled={getKnownPublicationInfo(activeStory, lang).isPublished || !canPropose || !canSeeProposals}
                                    title={!canSeeProposals ? t.modeDoesNotAllowProposalsForYou : writerNoteText}
                                  >
                                    💡 {t.writerStudioSubmitProposalWindow}
                                  </button>
                                </div>

                                {canonMessage ? (
                                  <p className="ns-card-meta" style={{ marginTop: "8px" }}>
                                    {canonMessage}
                                  </p>
                                ) : null}
                                {proposalMessage ? (
                                  <p className="ns-card-meta" style={{ marginTop: "8px" }}>
                                    {proposalMessage}
                                  </p>
                                ) : null}
                              </div>

                              <div
                                className="ns-card ns-card-soft ws-activity-panel"
                                style={{
                                  marginTop: "10px",
                                  border: "1px solid #e5e7eb",
                                  background: "#f9fafb",
                                }}
                              >
                                <div className="ws-block-kicker" style={{ marginBottom: "6px" }}>
                                     {t.activitySummary}
                                 </div>
                                <p className="ns-card-meta" style={{ margin: 0 }}>
  {t.canon}: <strong>{analytics.canonCount}</strong> · {t.proposals}:{" "}
  <strong>{analytics.proposalCount}</strong> · {t.comments}:{" "}
  <strong>{analytics.commentCount}</strong> · {t.replies}:{" "}
  <strong>{analytics.replyCount}</strong>
</p>

<p className="ns-card-meta" style={{ margin: 0 }}>
  {t.votes}: 👍 <strong>{analytics.votesUp}</strong> · 👎{" "}
  <strong>{analytics.votesDown}</strong> · ◻︎{" "}
  <strong>{analytics.votesNeutral}</strong>
</p>

                                <div className="ws-actions-row">
                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={handleShareStory}
                                  >
                                    {t.writerStudioShareStoryline}
                                  </button>

                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={() => setShowDraftWindow(true)}
                                    disabled={!isWriter || canonicalScenes.length === 0}
                                    title={
                                      !isWriter
                                        ? "Only the writer can read the full draft."
                                        : "Read the full book draft so far."
                                    }
                                  >
                                    {t.writerStudioReadFullBookDraft}
                                  </button>

                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={() => setShowDecisionLog(true)}
                                    disabled={!isWriter}
                                    title={
                                      !isWriter
                                        ? "Only the writer can view the decision log."
                                        : "Review canon decisions and actions."
                                    }
                                  >
                                    {t.writerStudioDecisionLog}
                                  </button>

                                  <button
                                    type="button"
                                    className="ns-button ns-button-outline"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      goReaderViewForStory(
                                        activeStory?._id || activeStory?.id || activeStoryId
                                      );
                                    }} title={t.openReaderViewForStoryline}
                                  >
                                    {t.writerStudioReaderView}
                                  </button>
                                </div>
                              </div>

                              <h3 className="ns-card-title ws-section-header" style={{ marginTop: "12px" }}>
                                {t.writerStudioRecentCanonSnippets}
                              </h3>

                              {recentCanonSnippets.length === 0 ? (
                                <p className="ns-card-meta">
                                  {t.noCanonScenesYetAddScene1}
                                </p>
                              ) : (
                                <div
                                  style={{
                                    marginBottom: "8px",
                                    borderRadius: "8px",
                                    border: "1px solid #e5e7eb",
                                    padding: "6px",
                                    background: "#f9fafb",
                                  }}
                                >
                                  {recentCanonSnippets.map((scene) => (
                                    <div
                                      key={getSceneId(scene) || scene.sceneNumber}
                                      className="ns-card ns-card-soft"
                                      style={{ marginBottom: "6px" }}
                                    >
                                      <p className="ns-card-meta">
                                        {formatCanonSceneLocation(
                                          scene
                                        )} · {scene.title || t.card_untitled_story} ·{" "}
                                        {scene.authorName ||
                                          scene.authorUsername ||
                                          scene.author ||
                                          t.unknown}{" "}
                                        · {formatDateTime(scene.createdAt)}
                                      </p>

                                      {scene.decisionNote && String(scene.decisionNote).trim() ? (
                                        <p className="ns-card-meta" style={{ marginTop: "4px" }}>
                                          <strong>{t.decisionNoteLabel}:</strong> {scene.decisionNote}
                                        </p>
                                      ) : null}

                                      <p className="ns-card-meta">
                                        {String(scene.text || "").length > 220
                                          ? String(scene.text || "").slice(0, 220) + "…"
                                          : String(scene.text || "")}
                                      </p>
                                      <SceneIllustrationPreview scene={scene} t={t} compact />
                                      {renderIllustrationReviewControls(scene, true)}
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="ws-recent-canon-actions" style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={() => setShowCanonWindow(true)}
                                  disabled={canonicalScenes.length === 0}
                                  title={t.validatedScenesSameAsReaderView}
                                >
                                  {t.writerStudioViewValidatedScenes}
                                </button>

                                <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={handleRequestOpenProposals}
  disabled={!isWriter || proposalsOpen || isSoloMode}
  title={
    isSoloMode
      ? t.proposalsDisabledSolo
      : !isWriter
      ? t.onlyWriterCanOpenProposals
      : proposalsOpen
      ? t.proposalsAlreadyOpen
      : t.openNextSceneRound
  }
>
  {proposalsOpen ? t.proposalsOpen : t.writerStudioCallForProposedScenes}
</button>
                              </div>

                              

                  
                              {/* Proposed scenes moved below into the merged Writer Room column */}

                                </>
                              ) : null}
                            </>
                          )}
                        </div>
          </section>

          {/* BOTTOM ROW: merged full-width Writer Room (Proposed Scenes + Reader Feedback) */}
          <section className={"ns-column ws-area-roombottom" + (activeStory && getKnownPublicationInfo(activeStory, lang).isPublished ? " ns-hidden-for-published" : "")}>
            <div className="ns-card ws-roombottom-card">
              <div className={"ws-proposals-section" + (proposalsOpen ? " is-open" : "") + (proposedScenes.length > 0 ? " has-proposals" : "")}>
                <div style={{ marginTop: "14px" }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                      <h3 className="ns-card-title ws-section-header" style={{ margin: 0 }}>
                                        {t.writerStudioProposedScenesNextRound}
                                      </h3>

                                      {proposalsOpen && proposedScenes.length > 0 ? (
                                        <div className="ws-proposal-nav-row">
                                          <button
                                            type="button"
                                            className="ns-button ns-button-outline"
                                            style={{ padding: "2px 10px", fontSize: "12px" }}
                                            onClick={() => scrollProposals("left")}
                                          >
                                            ◀
                                          </button>
                                          <button
                                            type="button"
                                            className="ns-button ns-button-outline"
                                            style={{ padding: "2px 10px", fontSize: "12px" }}
                                            onClick={() => scrollProposals("right")}
                                          >
                                            ▶
                                          </button>
                                        </div>
                                      ) : null}
                                    </div>

                                    {!canSeeProposals ? (
                                      <p className="ns-card-meta">
                                        Proposals are not available for you in this writing mode.
                                      </p>
                                    ) : !proposalsOpen ? (
                                      <p className="ns-card-meta">
                                        {t.writerStudioProposalsClosedCallNextRound}
                                      </p>
                                    ) : proposedScenes.length === 0 ? (
                                      <p className="ns-card-meta">
                                        {t.writerStudioNoProposalsYet}
                                      </p>
                                    ) : (
                                      <>
                                        <div
                                          ref={proposalsContainerRef}
                                          style={{
                                            marginTop: "6px",
                                            borderRadius: "8px",
                                            border: "1px solid #e5e7eb",
                                            padding: "6px",
                                            background: "#f9fafb",
                                            display: "flex",
                                            flexWrap: "nowrap",
                                            overflowX: "auto",
                                            overflowY: "hidden",
                                            gap: "8px",
                                          }}
                                        >
                                          {proposedScenes.map((scene) => {
                                            const sid = getSceneId(scene);
                                            const myVote = sid ? userVotes[sid] : undefined;

                                            const local = sid ? voteTotals?.[sid] : null;
                                            const votesUp = local ? toSafeInt(local.up) : toSafeInt(scene.votesUp);
                                            const votesDown = local ? toSafeInt(local.down) : toSafeInt(scene.votesDown);
                                            const votesNeutral = local ? toSafeInt(local.neutral) : toSafeInt(scene.votesNeutral);

                                            const meRaw = String(username || "").trim();
                                            const isOwn = getSceneAuthors(scene).some((a) => identitiesMatch(a, meRaw));

                                            const voteDisabled =
                                              !proposalsOpen || isWriter || !username || myVote !== undefined || isOwn;

                                            return (
                                              <div
                                                key={sid || scene.createdAt || Math.random()}
                                                className="ns-card ns-card-soft"
                                                style={{ minWidth: "280px", maxWidth: "280px", flex: "0 0 280px" }}
                                              >
                                                <p className="ns-card-meta">
                                                  Proposal · {scene.title || "Untitled"} ·{" "}
                                                  {scene.authorName ||
                                                    scene.authorUsername ||
                                                    scene.author ||
                                                    t.unknown}{" "}
                                                  · {formatDateTime(scene.createdAt)}
                                                </p>

                                                <p className="ns-card-meta">
                                                  Votes · 👍 {votesUp} · 👎 {votesDown} · ◻︎ {votesNeutral}
                                                </p>

                                                <p className="ns-card-meta" style={{ marginTop: "4px" }}>
                                                  {String(scene.text || "").length > 200
                                                    ? String(scene.text || "").slice(0, 200) + "…"
                                                    : String(scene.text || "")}
                                                </p>
                                                <SceneIllustrationPreview scene={scene} t={t} compact />
                                                {renderIllustrationReviewControls(scene, true)}

                                                <div
                                                  className="ns-card-footer"
                                                  style={{
                                                    marginTop: "4px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "4px",
                                                  }}
                                                >
                                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                                    <button
                                                      type="button"
                                                      className="ns-button ns-button-outline"
                                                      disabled={voteDisabled}
                                                      onClick={() => handleVote(scene, 1)}
                                                    >
                                                      👍 {t.good} (+1)
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="ns-button ns-button-outline"
                                                      disabled={voteDisabled}
                                                      onClick={() => handleVote(scene, -1)}
                                                    >
                                                      👎 {t.incompatible} (−1)
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="ns-button ns-button-outline"
                                                      disabled={voteDisabled}
                                                      onClick={() => handleVote(scene, 0)}
                                                    >
                                                      ◻︎ {t.neutral} (0)
                                                    </button>
                                                  </div>

                                                  <button
                                                    type="button"
                                                    className={
                                                      isWriter
                                                        ? "ns-button ns-button-primary"
                                                        : "ns-button ns-button-outline"
                                                    }
                                                    onClick={() => setFullProposalScene(scene)}
                                                  >
                                                    {isWriter
                                                      ? (
                                                          String(lang || "en")
                                                            .toLowerCase()
                                                            .startsWith("fr")
                                                            ? "Relire et sélectionner"
                                                            : "Review & Select"
                                                        )
                                                      : t.viewFullProposal}
                                                  </button>

                                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                                    <button
                                                      type="button"
                                                      className="ns-button ns-button-outline"
                                                      onClick={() => handleRejectProposal(sid)}
                                                      disabled={!isWriter}
                                                    >
                                                      {t.rejectProposal}
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>

                                        <div style={{ marginTop: "6px", display: "flex", justifyContent: "flex-end" }}>
                                          <button
                                            type="button"
                                            className="ns-button ns-button-outline"
                                            onClick={handleRejectAllProposals}
                                            disabled={!isWriter}
                                          >
                                            {t.rejectAll}
                                          </button>
                                        </div>
                                      </>
                                    )}
              </div>
              </div>

{activeStory ? (
  <AiSceneProposalCard
    story={activeStory}
    lang={lang}
    disabled={!isWriter}
    disabledReason={
      !isWriter
        ? String(lang || "en").toLowerCase().startsWith("fr")
          ? "Seul l’auteur ou l’administrateur peut demander des propositions IA pour cette histoire."
          : "Only the story owner or admin can request AI scene proposals for this story."
        : ""
    }
    onAdoptProposal={(draft) => {
      const title = String(draft?.title || "").trim();
      const text = String(draft?.text || "").trim();
      const isFrench = String(lang || "en").toLowerCase().startsWith("fr");

      if (!text) return;

      if (isWriter) {
        setCanonTitle(title || (isFrench ? "Brouillon de scène IA" : "AI scene draft"));
        setCanonText(text);
        setCanonManuscriptContent(null);
        setCanonMessage(
          isFrench
            ? "La proposition IA a été copiée dans votre brouillon canon humain. Relisez-la, utilisez la relecture IA si nécessaire, puis enregistrez seulement si vous décidez qu’elle doit devenir canon."
            : "The AI proposal has been copied into your human canon draft. Review it, use AI review if needed, then save only if you decide it should become canon."
        );
        setShowAddProposalModal(false);
        setShowAddCanonModal(true);
        return;
      }

      if (canPropose) {
        setProposalTitle(title || (isFrench ? "Proposition IA" : "AI scene proposal"));
        setProposalText(text);
        setProposalManuscriptContent(null);
        setProposalMessage(
          isFrench
            ? "La proposition IA a été copiée dans votre brouillon humain. Ouvrez la fenêtre de proposition pour vérifier avant toute soumission."
            : "The AI proposal has been copied into your human draft. Open the proposal window to review before any submission."
        );
        setShowAddCanonModal(false);
        setShowAddProposalModal(true);
        return;
      }

      setProposalMessage(
        isFrench
          ? "La proposition IA a été copiée, mais ce compte ne peut pas soumettre de proposition pour cette histoire pour le moment."
          : "The AI proposal was copied, but this account cannot submit a proposal for this story right now."
      );
    }}
  />
) : null}

              <section
                className="ws-guidance-dock"
                aria-label={lang === "fr" ? "Repères Writer Studio" : "Writer Studio guidance"}
              >
                <details className="ws-guidance-details">
                  <summary>{t.writerConfidenceTitle} →</summary>
                  <div className="ns-trust-notice ns-trust-notice-writer ns-trust-notice-compact" role="note">
                    <div className="ns-trust-notice-icon" aria-hidden="true">✓</div>
                    <div>
                      <div className="ns-trust-notice-title">{t.writerConfidenceTitle}</div>
                      <p className="ns-trust-notice-body">{t.writerConfidenceBody}</p>
                    </div>
                  </div>
                </details>

                <details className="ws-guidance-details">
                  <summary>{t.policyLinksTitle} →</summary>
                  <PolicyLinksCard
                    lang={lang}
                    title={t.policyLinksTitle}
                    body={t.policyLinksBody}
                    compact
                  />
                </details>

                <details className="ws-guidance-details">
                  <summary>{t.foundingWritersPilotTitle} →</summary>
                  <FoundingWritersPilotCard
                    lang={lang}
                    title={t.foundingWritersPilotTitle}
                    body={t.foundingWritersPilotBody}
                    linkLabel={t.foundingWritersPilotLink}
                  />
                </details>
              </section>

              <div className={"ws-feedback-section" + (activeStory ? " is-active" : "") + ((comments?.length || 0) > 0 ? " has-comments" : "")}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px" }}>
                  <h2 className="ws-block-kicker" style={{ margin: 0 }}>{t.writerStudioReaderFeedback}</h2>
                  <p className="ns-card-meta" style={{ margin: 0 }}>
                    {comments?.length || 0} {(comments?.length || 0) === 1 ? t.commentSingle : t.comments}
                  </p>
                </div>

                {activeStory && modeBadge ? (
                  <p className="ns-card-meta" style={{ marginTop: "6px" }}>
                    {t.writerStudioState}: <strong>{modeBadge.title}</strong>
                  </p>
                ) : null}

                <p className="ns-card-meta" style={{ marginTop: "6px" }}>
                  {activeStory
                    ? t.writerStudioReaderFeedbackDesc
                    : t.selectStorylineToViewFeedback}
                </p>

                {activeStory ? (
                  <div className="ws-feedback-box">
                    {/* STREAM (scrollable) */}
                    <div className="ws-feedback-stream ws-feedback-list">
                      {threadedComments.parents.length === 0 ? (
                        <p className="ns-card-meta" style={{ marginTop: 0 }}>
                          No comments yet.
                        </p>
                      ) : (
                        threadedComments.parents.map((c) => {
                          const isHighlighted = highlightCommentId && c.id === highlightCommentId;
                          const isActiveReply = replyOpenFor === c.id;
                          const replies = threadedComments.repliesByParent[String(c.id)] || [];

                          return (
                            <div
                              key={c.id}
                              ref={(el) => {
                                commentItemRefs.current[String(c.id)] = el;
                              }}
                              onClick={() => {
                                setHighlightCommentId(String(c.id));
                                if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
                                highlightTimerRef.current = setTimeout(() => {
                                  setHighlightCommentId("");
                                  highlightTimerRef.current = null;
                                }, 2200);
                              }}
                              className="ns-card ns-card-soft ws-comment"
                              style={{
                                marginBottom: "10px",
                                border: isHighlighted
                                  ? "2px solid #16a34a"
                                  : isActiveReply
                                  ? "2px solid #22c55e"
                                  : "1px solid #e5e7eb",
                                background: isHighlighted
                                  ? "#f0fdf4"
                                  : isActiveReply
                                  ? "#f0fdf4"
                                  : "#ffffff",
                                boxShadow: isHighlighted ? "0 10px 24px rgba(34,197,94,0.16)" : "none",
                                transition: "all 160ms ease",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <AvatarBubble name={c.author} title={c.author} size={30} />
                                <p className="ns-card-meta" style={{ margin: 0 }}>
                                  <strong>{c.author}</strong> · {formatDateTime(c.createdAt)}
                                </p>
                              </div>

                              <p className="ns-card-meta" style={{ marginTop: "8px" }}>
                                {c.text}
                              </p>

                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                                <button
  type="button"
  className="ns-button ns-button-outline"
  disabled={!canReply}
  onClick={() => toggleReplyBox(c.id)}
>
  {isActiveReply ? t.closeReply : t.reply}
</button>
                              </div>

                              {isActiveReply ? (
                                <form onSubmit={(e) => handlePostReply(e, c.id)} style={{ marginTop: "10px" }}>
                                  <div className="ns-field">
                                    <label>
                                      Your reply
                                      <textarea
                                        className="ws-textarea ws-feedback-input"
                                        rows={3}
                                        value={replyDrafts[c.id] || ""}
                                        onChange={(e) =>
                                          setReplyDrafts((prev) => ({
                                            ...prev,
                                            [c.id]: e.target.value,
                                          }))
                                        }
                                        disabled={!canReply}
                                        placeholder={
                                          !canReply
                                            ? isWriter
                                              ? "Writers cannot reply here."
                                              : commentGateReason
                                              ? commentGateReason
                                              : "Set a username to reply."
                                            : "Write your reply…"
                                        }
                                      />
                                    </label>
                                  </div>

                                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                                    <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={() => setReplyOpenFor(null)}
>
  {t.cancel}
</button>
<button
  type="submit"
  className="ns-button ns-button-primary"
  disabled={!canReply}
>
  {t.postReply}
</button>
                                  </div>
                                </form>
                              ) : null}

                              {replies.length > 0 ? (
                                <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #e5e7eb" }}>
                                  {replies.map((r) => (
                                    <div key={r.id} className="ns-card ns-card-soft" style={{ marginBottom: "8px", background: "#f9fafb" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <AvatarBubble name={r.author} title={r.author} size={28} />
                                        <p className="ns-card-meta" style={{ margin: 0 }}>
                                          <strong>{r.author}</strong> · {formatDateTime(r.createdAt)}
                                        </p>
                                      </div>
                                      <p className="ns-card-meta" style={{ marginTop: "8px" }}>
                                        {r.text}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* COMPOSER (fixed) */}
                    <div className="ws-feedback-composer">
                      {!canPostComment && commentBlockNote ? (
                        <p className="ns-card-meta" style={{ marginTop: 0, marginBottom: "8px" }}>
                          {commentBlockNote}
                        </p>
                      ) : null}

                      <form onSubmit={handlePostComment}>
                        <div className="ns-field">
                          <label>
                            {t.writerStudioPostAComment}
                            <textarea
                              className="ws-textarea ws-feedback-input"
                              rows={3}
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              disabled={!canPostComment}
                              placeholder={
                               isWriter
                                ? t.writerStudioWritersCannotCommentPlaceholder
                                : commentGateReason
                                ? commentGateReason
                                : !username || !username.trim()
                                ? t.commentUsernameRequired
                                : t.writerStudioShareYourFeedback
                              }
                            />
                          </label>
                        </div>

                        {commentMessage ? (
                          <p className="ns-card-meta" style={{ marginTop: "6px" }}>
                            {commentMessage}
                          </p>
                        ) : null}

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                          <span className="ns-card-meta" style={{ margin: 0 }}>
                            {t.writerStudioRepliesStayUnder}
                          </span>

                          <button
                            type="submit"
                            className="ns-button ns-button-primary"
                            disabled={!canPostComment}
                          >
                            {t.writerStudioPostComment}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                ) : (
                  <p className="ns-card-meta">{t.selectStorylineToViewFeedback}</p>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      {/* MODALS */}
      {showAddCanonModal && activeStory ? (
        <ModalShell
          key={
            "canon-scene-" +
            String(activeStoryId || "story") +
            "-" +
            String(
              editingCanonSceneId ||
              targetChapterId ||
              focusedChapterId ||
              "new"
            )
          }
          title={
            editingCanonSceneId
              ? (
                  String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Modifier la scène Canon"
                    : "Edit Canon Scene"
                )
              : t.addCanonModalTitle
          }
          subtitle={(t.canonLengthGuidance ||
            "Recommended length: {min} to {recommendedMax} words. A short exception up to {max} words is allowed when readability needs it.")
            .replace("{min}", MIN_PROPOSAL_WORDS)
            .replace("{recommendedMax}", RECOMMENDED_MAX_PROPOSAL_WORDS)
            .replace("{max}", MAX_PROPOSAL_WORDS)}
          onClose={() => setShowAddCanonModal(false)}
          maxWidth={980}
          tone="creative"
        >
          <div className="ns-card ns-card-soft" style={{ marginBottom: 10 }}>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {t.status}: <strong>{canonReadiness}</strong> · {t.wordCount}:{" "}
               <strong style={{ color: canonWordCountColor }}>{canonWordCount}</strong>{" "}
              {!canonHasParagraphs && canonText.trim()
               ? `· ${t.addParagraphBreaks}`
               : ""}
           </p>
            <p className="ns-card-meta" style={{ marginTop: 8 }}>
              {t.writerStudioShortParagraphTip ||
                "Tip: write in short paragraphs. If the text looks clustered, press Enter to start a new paragraph before saving."}
            </p>
          </div>


{isSoloMode && !isStoryOwner ? (
  <div className="ns-muted" style={{ marginTop: 10, color: "#8b1d1d" }}>
    This is a Solo story. Only the author can add canon scenes here.
  </div>
) : null}

          {!editingCanonSceneId ? (
            <p data-novel-scene-chapter-destination="true" className="ns-card-meta"
              style={{margin:"8px 0 14px",fontWeight:800}}>
              {String(targetChapterId || focusedChapterId || "").trim()
                ? (
                    String(lang || "en").toLowerCase().startsWith("fr")
                      ? "Destination : " : "Saving to Chapter: "
                  ) + String(
                    (Array.isArray(activeStory?.chapters) ? activeStory.chapters : [])
                      .find((chapter) => String(chapter?.id || "") ===
                        String(targetChapterId || focusedChapterId || "").trim())?.title ||
                    "Selected Chapter"
                  )
                : (String(lang || "en").toLowerCase().startsWith("fr")
                    ? "Destination : scène non attribuée (aucun chapitre sélectionné)."
                    : "Saving as Unassigned (no Chapter selected).")}
            </p>
          ) : null}

          <form onSubmit={handleCreateCanonScene}>
            <div className="ns-field">
              <label>
                <label>{t.sceneTitle}</label>
                <input
                  style={INPUT_STYLE}
                  value={canonTitle}
                  onChange={(e) => setCanonTitle(e.target.value)}
                  disabled={!isWriter || !!editingCanonSceneId}
                />
              </label>
            </div>

            <div className="ns-field" style={{ marginTop: 10 }}>
              <label>{t.sceneText}</label>

              <NovelStorySceneRichEditor
                id={
                  editingCanonSceneId ||
                  `canon-${String(
                    activeStoryId || "scene"
                  )}-${String(
                    targetChapterId ||
                    focusedChapterId ||
                    "unassigned"
                  )}-new`
                }
                title={canonTitle}
                text={canonText}
                manuscriptContent={
                  canonManuscriptContent
                }
                disabled={!isWriter}
                lang={lang}
                saveActionLabel={
                  editingCanonSceneId
                    ? (
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Enregistrer les modifications"
                          : "Save Changes"
                      )
                    : (
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Enregistrer la scène"
                          : "Save Scene"
                      )
                }
                onChange={(next) => {
                  setCanonText(
                    String(next?.text || "")
                  );
                  setCanonManuscriptContent(
                    next?.manuscriptContent || null
                  );
                }}
              />

              <div
                data-novel-story-scene-save-bar="true"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(5,150,105,0.24)",
                  background: "rgba(240,253,244,0.82)",
                }}
              >
                <span
                  className="ns-card-meta"
                  style={{ margin: 0 }}
                >
                  {String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Enregistrez la scène avant d’actualiser ou de quitter."
                    : "Save the Scene before refreshing or leaving."}
                </span>

                <button
                  type="submit"
                  className="ns-button ns-button-primary"
                  data-novel-story-save-scene="true"
                  disabled={
                    !isWriter ||
                    creatingCanon ||
                    !canonTitle.trim() ||
                    !canonText.trim() ||
                    (isCollaborativeMode && !isStoryOwner) ||
                    (isGroupStory && !isStoryOwner) ||
                    canonWordCount > MAX_PROPOSAL_WORDS ||
                    (isSoloMode && !isStoryOwner) ||
                    (!isSoloMode && proposalsOpen)
                  }
                >
                  {creatingCanon
                    ? t.saving
                    : editingCanonSceneId
                    ? (
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Enregistrer les modifications"
                          : "Save Changes"
                      )
                    : (
                        String(lang || "en")
                          .toLowerCase()
                          .startsWith("fr")
                          ? "Enregistrer la scène"
                          : "Save Scene"
                      )}
                </button>
              </div>

              <button
                type="button"
                className="ns-button ns-button-outline"
                style={{ marginTop: "8px" }}
                onClick={() => {
                  setCanonText(generateText(3));
                  setCanonManuscriptContent(null);
                }}
                disabled={!isWriter || !!editingCanonSceneId}
              >
                {t.generateTestText}
              </button>
            </div>

            <WriterStudioAiAssistancePlaceholder
              lang={lang}
              activeStory={activeStory}
              sceneTitle={canonTitle}
              sceneText={canonText}
              reviewContext="canon"
              compact
              disabled={!isWriter || getKnownPublicationInfo(activeStory, lang).isPublished}
              disabledReason={
                !isWriter
                  ? (lang === "fr" ? "Seul l’auteur peut demander une relecture canon." : "Only the author can request canon review.")
                  : getKnownPublicationInfo(activeStory, lang).isPublished
                  ? (lang === "fr" ? "La relecture est désactivée pour les livres publiés." : "Review is disabled for published books.")
                  : ""
              }
            />

            <div className="ns-scene-illustration-form" role="note">
              <div className="ns-scene-illustration-form-title">{t.sceneIllustrationOptional}</div>
              {editingCanonSceneId ? (
                <p className="ns-card-meta" style={{ marginTop: 6 }}>
                  {String(lang || "en").toLowerCase().startsWith("fr")
                    ? "Pendant la modification du texte, gérez l’illustration existante depuis les outils d’illustration de la scène."
                    : "While editing Scene text, manage the existing illustration from the Scene illustration tools."}
                </p>
              ) : null}
              <details className="ns-collapsible-help">
                <summary>{t.sceneIllustrationHelpSummary || "Rules and review"}</summary>
                <p className="ns-card-meta" style={{ marginTop: 6 }}>{t.sceneIllustrationPendingNotice}</p>
                <p className="ns-policy-microline">
                  {t.sceneIllustrationConsentLine}{" "}
                  <a href={illustrationPolicyHref} target="_blank" rel="noreferrer">
                    {t.sceneIllustrationPolicyLink}
                  </a>
                </p>
              </details>
              <div className="ns-field" style={{ marginTop: 10 }}>
                <label>{sceneIllustrationUploadLabel()}</label>
                <input
                  style={INPUT_STYLE}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={!isWriter || !!editingCanonSceneId || canonIllustrationUploading}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    handleSceneIllustrationUpload("canon", file);
                    e.target.value = "";
                  }}
                />
                <details className="ns-collapsible-help ns-collapsible-help-compact">
                  <summary>{t.sceneIllustrationFileHelpSummary || "File help"}</summary>
                  <p className="ns-card-meta" style={{ marginTop: 6 }}>
                    {canonIllustrationUploading ? sceneIllustrationUploadingLabel() : sceneIllustrationUploadHint()}
                  </p>
                </details>
              </div>
              <div className="ns-field" style={{ marginTop: 10 }}>
                <label>{t.sceneIllustrationUrl}</label>
                <input
                  style={INPUT_STYLE}
                  value={canonIllustrationUrl}
                  onChange={(e) => {
                    setCanonIllustrationUrl(e.target.value);
                    setCanonIllustrationConfirmed(false);
                  }}
                  placeholder="https://.../illustration.jpg"
                  disabled={!isWriter || !!editingCanonSceneId}
                />
              </div>
              <div className="ns-illustration-grid">
                <div className="ns-field">
                  <label>{t.sceneIllustrationCaption}</label>
                  <input style={INPUT_STYLE} value={canonIllustrationCaption} onChange={(e) => setCanonIllustrationCaption(e.target.value)} disabled={!isWriter || !!editingCanonSceneId} />
                </div>
                <div className="ns-field">
                  <label>{t.sceneIllustrationCredit}</label>
                  <input style={INPUT_STYLE} value={canonIllustrationCredit} onChange={(e) => setCanonIllustrationCredit(e.target.value)} disabled={!isWriter || !!editingCanonSceneId} />
                </div>
              </div>
              <div className="ns-field" style={{ marginTop: 10 }}>
                <label>{t.sceneIllustrationAltText}</label>
                <input style={INPUT_STYLE} value={canonIllustrationAltText} onChange={(e) => setCanonIllustrationAltText(e.target.value)} disabled={!isWriter || !!editingCanonSceneId} />
              </div>

              {canonIllustrationUrl.trim() ? (
                <div className="ns-illustration-confirm-compact">
                  <img src={canonIllustrationUrl.trim()} alt={canonIllustrationAltText || canonIllustrationCaption || t.sceneIllustrationLabel} />
                  <label className="ns-illustration-confirm-check">
                    <input
                      type="checkbox"
                      checked={canonIllustrationConfirmed}
                      onChange={(e) => setCanonIllustrationConfirmed(e.target.checked)}
                      disabled={!isWriter || !!editingCanonSceneId}
                    />
                    <span>{t.sceneIllustrationConfirmLabel || "I confirm this is the correct illustration for this scene."}</span>
                  </label>
                </div>
              ) : null}
            </div>

            {canonMessage ? (
              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                {canonMessage}
              </p>
            ) : null}

            {canonAutoStatus ? (
              <p className="ns-card-meta" style={{ marginTop: 8, marginBottom: 0 }}>
  {canonAutoStatus} ·{" "}
  <button
    type="button"
    className="ns-button ns-button-link"
    onClick={() => clearDraft("canon")}
    disabled={!isWriter || !!editingCanonSceneId}
    style={{ padding: 0, marginLeft: 6 }}
  >
    {t.clearDraft}
  </button>
</p>
            ) : null}


            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
              <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={() => {
    setShowAddCanonModal(false);
    setEditingCanonSceneId("");
    setTargetChapterId("");
  }}
>
  {t.cancel}
</button>

            </div>
          </form>
        </ModalShell>
      ) : null}

      {showAddProposalModal && activeStory ? (
        <ModalShell
        title={t.submitProposalModalTitle}
        subtitle={t.submitProposalModalDesc
        .replace("{min}", MIN_PROPOSAL_WORDS)
        .replace("{recommendedMax}", RECOMMENDED_MAX_PROPOSAL_WORDS)
        .replace("{max}", MAX_PROPOSAL_WORDS)}
       onClose={() => setShowAddProposalModal(false)}
       maxWidth={980}
       tone="creative"
       >
          <div className="ns-card ns-card-soft" style={{ marginBottom: 10 }}>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              Status: <strong>{proposalReadiness}</strong> · Word count:{" "}
              <strong style={{ color: wordCountColor }}>{proposalWordCount}</strong>{" "}
              {mentionsCanonElement ? `· ${t.mentionsRecentCanon}` : ""}
            </p>
            <p className="ns-card-meta" style={{ marginTop: 8 }}>
              {writerNoteText}
            </p>
          </div>

          <div className="ns-trust-notice ns-trust-notice-compact" role="note" style={{ marginBottom: 12 }}>
            <div className="ns-trust-notice-icon" aria-hidden="true">✓</div>
            <div>
              <div className="ns-trust-notice-title">{t.proposalOriginalityTitle}</div>
              <p className="ns-trust-notice-body">{t.proposalOriginalityBody}</p>
            </div>
          </div>

          <PolicyLinksCard
            lang={lang}
            title={t.policyLinksProposalTitle}
            body={t.policyLinksProposalBody}
            compact
          />

          <form onSubmit={handleCreateProposal}>
            <div className="ns-field">
              <label>
                <label>{t.proposalTitle}</label>
                 <input
                 style={INPUT_STYLE}
                 value={proposalTitle}
                 onChange={(e) => setProposalTitle(e.target.value)}
                 disabled={!canPropose}
                />
              </label>
            </div>

            <div className="ns-field" style={{ marginTop: 10 }}>
              <label>{t.proposalText}</label>

              <NovelStorySceneRichEditor
                id={
                  `proposal-${String(
                    activeStoryId || "scene"
                  )}`
                }
                title={proposalTitle}
                text={proposalText}
                manuscriptContent={
                  proposalManuscriptContent
                }
                disabled={!canPropose}
                lang={lang}
                saveActionLabel={
                  String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Soumettre la proposition"
                    : "Submit Proposal"
                }
                onChange={(next) => {
                  setProposalText(
                    String(next?.text || "")
                  );
                  setProposalManuscriptContent(
                    next?.manuscriptContent || null
                  );
                }}
              />
            </div>

            <WriterStudioAiAssistancePlaceholder
              lang={lang}
              activeStory={activeStory}
              sceneTitle={proposalTitle}
              sceneText={proposalText}
              reviewContext="proposal"
              compact
              disabled={!canPropose || getKnownPublicationInfo(activeStory, lang).isPublished}
              disabledReason={
                !canPropose
                  ? writerNoteText || (lang === "fr" ? "La relecture des propositions est disponible quand vous pouvez proposer une scène." : "Proposal review is available when you can submit a proposal.")
                  : getKnownPublicationInfo(activeStory, lang).isPublished
                  ? (lang === "fr" ? "La relecture est désactivée pour les livres publiés." : "Review is disabled for published books.")
                  : ""
              }
            />

            <div className="ns-scene-illustration-form" role="note">
              <div className="ns-scene-illustration-form-title">{t.sceneIllustrationOptional}</div>
              <details className="ns-collapsible-help">
                <summary>{t.sceneIllustrationHelpSummary || "Rules and review"}</summary>
                <p className="ns-card-meta" style={{ marginTop: 6 }}>{t.sceneIllustrationPendingNotice}</p>
                <p className="ns-policy-microline">
                  {t.sceneIllustrationConsentLine}{" "}
                  <a href={illustrationPolicyHref} target="_blank" rel="noreferrer">
                    {t.sceneIllustrationPolicyLink}
                  </a>
                </p>
              </details>
              <div className="ns-field" style={{ marginTop: 10 }}>
                <label>{sceneIllustrationUploadLabel()}</label>
                <input
                  style={INPUT_STYLE}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={!canPropose || proposalIllustrationUploading}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    handleSceneIllustrationUpload("proposal", file);
                    e.target.value = "";
                  }}
                />
                <details className="ns-collapsible-help ns-collapsible-help-compact">
                  <summary>{t.sceneIllustrationFileHelpSummary || "File help"}</summary>
                  <p className="ns-card-meta" style={{ marginTop: 6 }}>
                    {proposalIllustrationUploading ? sceneIllustrationUploadingLabel() : sceneIllustrationUploadHint()}
                  </p>
                </details>
              </div>
              <div className="ns-field" style={{ marginTop: 10 }}>
                <label>{t.sceneIllustrationUrl}</label>
                <input
                  style={INPUT_STYLE}
                  value={proposalIllustrationUrl}
                  onChange={(e) => {
                    setProposalIllustrationUrl(e.target.value);
                    setProposalIllustrationConfirmed(false);
                  }}
                  placeholder="https://.../illustration.jpg"
                  disabled={!canPropose}
                />
              </div>
              <div className="ns-illustration-grid">
                <div className="ns-field">
                  <label>{t.sceneIllustrationCaption}</label>
                  <input style={INPUT_STYLE} value={proposalIllustrationCaption} onChange={(e) => setProposalIllustrationCaption(e.target.value)} disabled={!canPropose} />
                </div>
                <div className="ns-field">
                  <label>{t.sceneIllustrationCredit}</label>
                  <input style={INPUT_STYLE} value={proposalIllustrationCredit} onChange={(e) => setProposalIllustrationCredit(e.target.value)} disabled={!canPropose} />
                </div>
              </div>
              <div className="ns-field" style={{ marginTop: 10 }}>
                <label>{t.sceneIllustrationAltText}</label>
                <input style={INPUT_STYLE} value={proposalIllustrationAltText} onChange={(e) => setProposalIllustrationAltText(e.target.value)} disabled={!canPropose} />
              </div>

              {proposalIllustrationUrl.trim() ? (
                <div className="ns-illustration-confirm-compact">
                  <img src={proposalIllustrationUrl.trim()} alt={proposalIllustrationAltText || proposalIllustrationCaption || t.sceneIllustrationLabel} />
                  <label className="ns-illustration-confirm-check">
                    <input
                      type="checkbox"
                      checked={proposalIllustrationConfirmed}
                      onChange={(e) => setProposalIllustrationConfirmed(e.target.checked)}
                      disabled={!canPropose}
                    />
                    <span>{t.sceneIllustrationConfirmLabel || "I confirm this is the correct illustration for this scene."}</span>
                  </label>
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
              <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={() => setProposalText(generateText(3))}
  disabled={!canPropose}
  title={t.generateTestText}
>
  {t.generateTestText}
</button>

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => {
                  const k = wsKey("proposal", selectedStoryId);
                  const ok = lsSet(k, {
                    title: proposalTitle || "",
                    text: proposalText || "",
                    manuscriptContent:
                      proposalManuscriptContent || null,
                    intent: proposalIntent || "",
                    illustrationUrl: proposalIllustrationUrl || "",
                    illustrationCaption: proposalIllustrationCaption || "",
                    illustrationCredit: proposalIllustrationCredit || "",
                    illustrationAltText: proposalIllustrationAltText || "",
                    ts: Date.now(),
                  });
                  setProposalDraftPresent(!!ok);
                  setProposalAutoStatus(ok ? t.draftSaved : t.couldNotSaveDraft);

                  if (proposalSavedTimer.current) clearTimeout(proposalSavedTimer.current);
                  setProposalDraftJustSaved(!!ok);
                  proposalSavedTimer.current = setTimeout(() => setProposalDraftJustSaved(false), 2200);
                }}
                disabled={!canPropose}
title={t.saveDraft}
>
  {proposalDraftJustSaved ? t.draftSaved : t.saveDraft}
</button>
            </div>


            {proposalMessage ? (
              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                {proposalMessage}
              </p>
            ) : null}

            {proposalAutoStatus || proposalDraftPresent ? (
              <p className="ns-card-meta" style={{ marginTop: 8, marginBottom: 0 }}>
                {proposalAutoStatus || t.draftSaved} ·{" "}
                <button
                 type="button"
                   className="ns-button ns-button-link"
                   onClick={() => clearDraft("proposal")}
                   disabled={!canPropose}
                   style={{ padding: 0, marginLeft: 6 }}
                  >
                  {t.clearDraft}
                  </button>
              </p>
            ) : null}


            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
              <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={() => {
    setShowAddProposalModal(false);
    setProposalDraftJustSaved(false);
  }}
>
  {t.cancel}
</button>
<button
  type="submit"
  className="ns-button ns-button-primary"
  disabled={!canPropose || creatingProposal}
>
  {creatingProposal ? t.submitting : t.submitProposalBtn}
</button>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {showCancelEmptyRoundModal && activeStory ? (
        <ModalShell
          title={String(lang||"en").toLowerCase().startsWith("fr")
            ? "Annuler ce tour de propositions vide ?" : "Cancel this empty Proposal Round?"}
          subtitle={String(lang||"en").toLowerCase().startsWith("fr")
            ? "Uniquement si aucune proposition n’a été soumise pendant ce tour."
            : "Only if no proposals were submitted during this round."}
          onClose={() => { if (!cancelEmptyRoundBusy) setShowCancelEmptyRoundModal(false); }}
          maxWidth={640} closeLabel={t.close}
        >
          <p className="ns-card-meta">
            {String(lang||"en").toLowerCase().startsWith("fr")
              ? "Le serveur revérifie les soumissions avant de réactiver Canon. Une proposition rejetée empêche aussi cette annulation."
              : "The server rechecks submissions before restoring Canon. Even a rejected proposal prevents cancellation."}
          </p>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}>
            <button type="button" className="ns-button ns-button-outline"
              onClick={() => setShowCancelEmptyRoundModal(false)}
              disabled={cancelEmptyRoundBusy}>{t.cancel}</button>
            <button type="button" className="ns-button ns-button-primary"
              data-writer-studio-confirm-cancel-empty-round="true"
              onClick={handleConfirmCancelEmptyProposalRound}
              disabled={!canCancelEmptyRound||cancelEmptyRoundBusy}>
              {String(lang||"en").toLowerCase().startsWith("fr")
                ? "Annuler le tour et réactiver Canon" : "Cancel Round & Resume Canon"}
            </button>
          </div>
        </ModalShell>
      ) : null}

      {showIntentModal && activeStory ? (
        <ModalShell
  title={t.openProposalRound}
  subtitle={t.openProposalRoundDesc}
  onClose={() => setShowIntentModal(false)}
  maxWidth={860}
  closeLabel={t.close}
  tone="creative"
>
  <div className="ns-field">
    <label>
      {t.nextSceneIntentLabel}
      <textarea
        rows={4}
        style={{ ...TEXTAREA_STYLE, minHeight: 140 }}
        value={intentDraft}
        onChange={(e) => setIntentDraft(e.target.value)}
        disabled={!isWriter}
      />
    </label>

          <div
            data-writer-studio-intent-status-slot="true"
            style={{
              minHeight: 26,
              marginTop: 8,
            }}
          >
            {intentAutoStatus ? (
              <p
                className="ns-card-meta"
                style={{
                  margin: 0,
                }}
              >
                {intentAutoStatus} ·{" "}
                <button
                  type="button"
                  className="ns-button ns-button-link"
                  onClick={() =>
                    clearDraft("intent")
                  }
                  disabled={!isWriter}
                  style={{
                    padding: 0,
                    marginLeft: 6,
                  }}
                >
                  {t.clearDraft}
                </button>
              </p>
            ) : null}
          </div>


          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={() => setShowIntentModal(false)}
>
  {t.cancel}
</button>

<button
  type="button"
  className="ns-button ns-button-primary"
  onClick={handleConfirmOpenProposals}
  disabled={!isWriter || isSoloMode}
>
  {t.openProposals}
</button>
          </div>
        </ModalShell>
      ) : null}

      {showDecisionModal && activeStory && pendingDecisionScene ? (
        <ModalShell
  title={t.decisionNoteRequired}
  subtitle={t.decisionNoteDesc}
  onClose={cancelProposalAdoptionDecision}
  maxWidth={860}
  closeLabel={t.close}
>
  <div
    className="ns-card ns-card-soft"
    data-writer-studio-locked-adoption-selection="true"
    style={{ marginBottom: 10 }}
  >
    <div
      className="ws-story-info-kicker"
      style={{ marginBottom: 6 }}
    >
      {String(lang || "en")
        .toLowerCase()
        .startsWith("fr")
        ? "SÉLECTION VERROUILLÉE"
        : "LOCKED SELECTION"}
    </div>

    <p className="ns-card-meta" style={{ margin: 0 }}>
      {t.approving}:{" "}
      <strong>
        {pendingDecisionScene.title || t.untitledProposal}
      </strong>
    </p>

    <p className="ns-card-meta" style={{ margin: "6px 0 0" }}>
      {String(lang || "en")
        .toLowerCase()
        .startsWith("fr")
        ? "Par"
        : "By"}{" "}
      <strong>
        {pendingDecisionScene.authorName ||
          pendingDecisionScene.authorUsername ||
          t.unknown}
      </strong>
      {" · "}
      {formatDateTime(
        pendingDecisionScene.createdAt
      )}
    </p>

    <p className="ns-card-meta" style={{ margin: "6px 0 0" }}>
      {String(lang || "en")
        .toLowerCase()
        .startsWith("fr")
        ? "Cette proposition exacte — et aucune autre — sera envoyée au serveur pour adoption."
        : "This exact proposal — and no other proposal — will be sent to the server for adoption."}
    </p>
  </div>

  <div className="ns-field">
    <label>
      {t.decisionNoteLabel}
      <textarea
        rows={4}
        style={{ ...TEXTAREA_STYLE, minHeight: 140 }}
        value={decisionDraft}
        onChange={(e) => setDecisionDraft(e.target.value)}
        disabled={!isWriter}
      />
    </label>
  </div>

          {decisionError ? (
            <p className="ns-card-meta" style={{ marginTop: 8, color: "#b91c1c" }}>
              {decisionError}
            </p>
          ) : null}

          {decisionAutoStatus ? (
            <p className="ns-card-meta" style={{ marginTop: 8, marginBottom: 0 }}>
              {decisionAutoStatus} ·{" "}
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => clearDraft("decision")}
                disabled={!isWriter}
                style={{ marginLeft: 6 }}
              >
                {t.clearDraft}
              </button>
            </p>
          ) : null}



          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button
  type="button"
  className="ns-button ns-button-outline"
  onClick={cancelProposalAdoptionDecision}
>
  {t.cancel}
</button>

<button
  type="button"
  className="ns-button ns-button-primary"
  onClick={confirmApproveProposalWithDecision}
  disabled={!isWriter}
>
  {String(lang || "en")
    .toLowerCase()
    .startsWith("fr")
    ? "Adopter exactement cette proposition"
    : "Adopt this exact proposal"}
</button>
          </div>
        </ModalShell>
      ) : null}

      {showStoryInfo && activeStory ? (
        <ModalShell
          title={
            String(lang || "en")
              .toLowerCase()
              .startsWith("fr")
              ? "Informations sur l’histoire"
              : "Story Info"
          }
          subtitle={
            isContributorWorkspace
              ? (
                  String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Référence de continuité en lecture seule pour préparer votre proposition."
                    : "Read-only continuity reference for preparing your proposal."
                )
              : (
                  String(lang || "en")
                    .toLowerCase()
                    .startsWith("fr")
                    ? "Référence de continuité. L’historique complet reste facultatif."
                    : "Continuity reference. Full Story History remains optional."
                )
          }
          onClose={() => {
            setShowStoryInfo(false);
            setShowStoryHistory(false);
          }}
          maxWidth={980}
          closeLabel={t.close}
        >
          <div
            className="ws-story-info-simple"
            data-writer-studio-story-info-simple="true"
          >
            <section className="ws-story-info-constitution">
              <div className="ws-story-info-section-head">
                <div>
                  <div className="ws-story-info-kicker">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "RÉFÉRENCE PRINCIPALE"
                      : "PRIMARY REFERENCE"}
                  </div>

                  <h3 className="ns-card-title">
                    {t.storyConstitution}
                  </h3>
                </div>

                <span className="ws-story-info-mode-pill">
                  {deriveModeForDisplay(
                    activeStory
                  )}
                </span>
              </div>

              <div className="ws-story-info-constitution-grid">
                {storyConstitutionRows.map(
                  (row) => (
                    <div
                      key={row.label}
                      className="ws-story-info-field"
                    >
                      <span className="ns-card-meta">
                        {row.label}
                      </span>
                      <strong>
                        {row.value}
                      </strong>
                    </div>
                  )
                )}
              </div>
            </section>

            {!isContributorWorkspace ? (
              <section
                ref={storyHistoryGatewayRef}
                className="ws-story-info-history-gateway"
              >
                <div>
                  <div className="ws-story-info-kicker">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "HISTORIQUE"
                      : "HISTORY"}
                  </div>
  
                  <strong>
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Historique de l’histoire"
                      : "Story History"}
                  </strong>
  
                  <p className="ns-card-meta">
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "Canon, propositions et décisions restent disponibles pour vérification, sans encombrer la référence principale."
                      : "Canon, proposal and decision history stays available for checking without crowding the main reference."}
                  </p>
                </div>
  
                <div className="ws-story-info-history-gateway-actions">
                  <span className="ws-story-info-history-count">
                    {storyHistoryStats.total}{" "}
                    {String(lang || "en")
                      .toLowerCase()
                      .startsWith("fr")
                      ? "événements"
                      : "events"}
                  </span>
  
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() => {
                      const nextVisible = !showStoryHistory;
                      storyHistoryScrollIntentRef.current = nextVisible
                        ? "open"
                        : "close";
                      setShowStoryHistory(nextVisible);
                      setStoryHistoryVisibleCount(
                        STORY_HISTORY_PAGE_SIZE
                      );
                    }}
                  >
                    {showStoryHistory
                      ? (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Masquer l’historique"
                            : "Hide Story History"
                        )
                      : (
                          String(lang || "en")
                            .toLowerCase()
                            .startsWith("fr")
                            ? "Voir l’historique"
                            : "View Story History"
                        )}
                  </button>
                </div>
              </section>
            ) : null}

            {!isContributorWorkspace &&
            showStoryHistory ? (
              <section
                ref={storyHistorySectionRef}
                className="ws-story-info-history"
                data-writer-studio-story-history="true"
              >
                <div className="ws-story-info-history-controls">
                  <input
                    type="text"
                    value={storyInfoSearch}
                    onChange={(e) =>
                      setStoryInfoSearch(
                        e.target.value
                      )
                    }
                    placeholder={
                      t.searchHistoryPlaceholder
                    }
                    style={{
                      ...INPUT_STYLE,
                      maxWidth: 420,
                      flex: "1 1 300px",
                    }}
                  />

                  <select
                    value={storyInfoScope}
                    onChange={(e) =>
                      setStoryInfoScope(
                        e.target.value
                      )
                    }
                    style={{
                      ...INPUT_STYLE,
                      width: 210,
                      flex: "0 0 210px",
                    }}
                  >
                    <option value="all">
                      {t.allHistory}
                    </option>
                    <option value="history">
                      {t.allTimelineEvents}
                    </option>
                    <option value="canon">
                      {t.canonEvents}
                    </option>
                    <option value="proposal">
                      {t.proposalEvents}
                    </option>
                    <option value="decision">
                      {t.decisionEvents}
                    </option>
                  </select>

                  {(storyInfoSearch ||
                    storyInfoScope !==
                      "all") ? (
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={() => {
                        setStoryInfoSearch("");
                        setStoryInfoScope(
                          "all"
                        );
                        setStoryHistoryVisibleCount(
                          STORY_HISTORY_PAGE_SIZE
                        );
                      }}
                    >
                      {t.clear}
                    </button>
                  ) : null}
                </div>

                <div className="ws-story-info-history-summary">
                  <span>
                    {t.showing}{" "}
                    <strong>
                      {
                        visibleStoryHistoryEntries.length
                      }
                    </strong>{" "}
                    {t.of}{" "}
                    <strong>
                      {
                        filteredStoryHistoryEntries.length
                      }
                    </strong>
                  </span>

                  <span>
                    Canon:{" "}
                    <strong>
                      {storyHistoryStats.canon}
                    </strong>
                    {" · "}
                    Proposal:{" "}
                    <strong>
                      {
                        storyHistoryStats.proposal
                      }
                    </strong>
                    {" · "}
                    Decisions:{" "}
                    <strong>
                      {
                        storyHistoryStats.decision
                      }
                    </strong>
                  </span>
                </div>

                {filteredStoryHistoryEntries.length ===
                0 ? (
                  <p className="ns-card-meta">
                    {
                      t.noHistoryEntriesMatchSearch
                    }
                  </p>
                ) : (
                  <div className="ws-story-info-timeline">
                    {visibleStoryHistoryEntries.map(
                      (entry) => {
                        const icon =
                          storyHistoryIcon(
                            entry
                          );

                        return (
                          <article
                            key={entry.id}
                            className={
                              "ws-story-info-timeline-entry" +
                              (
                                entry.isCurrent
                                  ? " is-current"
                                  : ""
                              )
                            }
                          >
                            <span
                              className="ws-story-info-timeline-icon"
                              aria-hidden="true"
                            >
                              {icon}
                            </span>

                            <div>
                              <strong>
                                {entry.action}
                              </strong>

                              {entry.detail ? (
                                <p>
                                  {
                                    entry.detail
                                  }
                                </p>
                              ) : null}

                              <span className="ns-card-meta">
                                {formatDateTime(
                                  entry.at
                                )}
                              </span>
                            </div>
                          </article>
                        );
                      }
                    )}

                    {hiddenStoryHistoryCount >
                    0 ? (
                      <div className="ws-story-info-history-more">
                        <span className="ns-card-meta">
                          {
                            hiddenStoryHistoryCount
                          }{" "}
                          {hiddenStoryHistoryCount ===
                          1
                            ? t.olderHistoryItemHidden
                            : t.olderHistoryItemsHidden}
                        </span>

                        <div>
                          <button
                            type="button"
                            className="ns-button ns-button-outline"
                            onClick={() =>
                              setStoryHistoryVisibleCount(
                                (cur) =>
                                  Math.min(
                                    cur +
                                      STORY_HISTORY_PAGE_SIZE,
                                    STORY_HISTORY_MAX_RENDER
                                  )
                              )
                            }
                          >
                            {t.loadMore}
                          </button>

                          {storyHistoryVisibleCount >
                          STORY_HISTORY_PAGE_SIZE ? (
                            <button
                              type="button"
                              className="ns-button ns-button-outline"
                              onClick={() =>
                                setStoryHistoryVisibleCount(
                                  STORY_HISTORY_PAGE_SIZE
                                )
                              }
                            >
                              {t.collapse}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </section>
            ) : null}
          </div>
        </ModalShell>
      ) : null}

      {showDecisionLog && activeStory ? (
        <ModalShell
          title={t.decisionLog}
          subtitle={t.decisionLogDesc}
          onClose={() => setShowDecisionLog(false)}
          maxWidth={980}
          closeLabel={t.close}
        >
          {decisionLog.length === 0 ? (
            <p className="ns-card-meta">{t.noDecisions}</p>
          ) : (
            decisionLog.slice(0, 200).map((d) => (
              <div key={d.id} className="ns-card ns-card-soft" style={{ marginBottom: 10 }}>
                <p className="ns-card-meta" style={{ margin: 0 }}>
                  <strong>{d.action}</strong> · {d.by} · {formatDateTime(d.at)}
                </p>
                <p className="ns-card-meta" style={{ marginTop: 6 }}>
                  {d.sceneTitle}
                </p>
                <p className="ns-card-meta" style={{ marginTop: 6 }}>
                  {d.note}
                </p>
              </div>
            ))
          )}
        </ModalShell>
      ) : null}

      {showCanonWindow && activeStory ? (
        <ModalShell
          title={t.validatedCanonScenes}
          subtitle={t.validatedScenesNote}
          onClose={() => setShowCanonWindow(false)}
          maxWidth={980}
          closeLabel={t.close}
        >
          {validatedFive.length === 0 ? (
            <p className="ns-card-meta">{t.noValidatedScenesYet}</p>
          ) : (
            validatedFive.map((scene) => (
              <div
                key={getSceneId(scene) || scene.sceneNumber}
                className="ns-card ns-card-soft"
                style={{ marginBottom: 10 }}
              >
                <p className="ns-card-meta">
                 {formatCanonSceneLocation(
                   scene
                 )} ·{" "}
                 <strong>{scene.title || t.card_untitled_story}</strong> ·{" "}
                 {scene.authorName || scene.authorUsername || scene.author || t.unknown} ·{" "}
                 {formatDateTime(scene.createdAt)}
                </p>

                {scene.decisionNote && String(scene.decisionNote).trim() ? (
                  <p className="ns-card-meta" style={{ marginTop: 6 }}>
                    <strong>{t.decisionNoteLabel}:</strong> {scene.decisionNote}
                  </p>
                ) : null}

                <FormattedText text={scene.text} />
                <SceneIllustrationPreview scene={scene} t={t} />
                {renderIllustrationReviewControls(scene)}
              </div>
            ))
          )}
        </ModalShell>
      ) : null}

      {showDraftWindow && activeStory ? (
        <ModalShell
  title={t.fullBookDraft}
  subtitle={t.writerOnlyView}
  onClose={() => setShowDraftWindow(false)}
  maxWidth={980}
  closeLabel={t.close}
>
  {!isWriter ? (
    <p className="ns-card-meta">{t.writerOnlyAccess}</p>
  ) : (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          type="button"
          className="ns-button ns-button-outline"
          onClick={handleDownloadDraft}
        >
          {t.downloadDraft}
        </button>
      </div>

      <div className="ns-full-draft-manuscript">
        {canonicalScenes.length === 0 ? (
          <p className="ns-card-meta">{t.noCanonYet}</p>
        ) : (
          bookDraftSequence.map((entry, index) => {
            const scene = entry.scene;
            const sceneNumber = Number(scene?.sceneNumber) || index + 1;
            const sceneTitle = String(scene?.title || t.card_untitled_story || "").trim();
            const heading = `${formatBookDraftEntryLocation(entry)} – ${sceneTitle}`;
            const decisionNote = String(scene?.decisionNote || "").trim();
            const startsUnassigned =
              entry.isUnassigned &&
              (index === 0 || !bookDraftSequence[index - 1].isUnassigned);

            return (
              <React.Fragment key={getSceneId(scene) || `${sceneNumber}-${sceneTitle}`}>
                {startsUnassigned ? (
                  <h2 className="ns-full-draft-scene-title">
                    {String(lang || "en").toLowerCase().startsWith("fr")
                      ? "Scènes non affectées (hors chapitres)"
                      : "Unassigned Scenes (not placed in Chapters)"}
                  </h2>
                ) : null}
                <article className="ns-full-draft-scene">
                  <h3 className="ns-full-draft-scene-title">{heading}</h3>

                  {decisionNote ? (
                    <p className="ns-full-draft-decision">
                      <strong>{t.decisionNoteLabel} ({scene.decisionBy || t.writerRoleLabel}):</strong>{" "}
                      {decisionNote}
                    </p>
                  ) : null}

                  <NovelStorySceneReadOnly
                    scene={scene}
                    lang={lang}
                  />
                  <FullDraftSceneIllustration scene={scene} t={t} />
                </article>
              </React.Fragment>
            );
          })
        )}
      </div>
    </>
  )}
</ModalShell>
) : null}


{showCharactersPanel && activeStory ? (
  <ModalShell
    title={
      String(lang || "en").toLowerCase().startsWith("fr")
        ? "Personnages de l’histoire"
        : "Story Characters"
    }
    subtitle={
      activeStory?.title
        ? `${activeStory.title}`
        : String(lang || "en").toLowerCase().startsWith("fr")
        ? "Bible des personnages"
        : "Character Bible"
    }
    onClose={() => setShowCharactersPanel(false)}
    maxWidth={1100}
    closeLabel={t.close}
  >
    <StoryCharactersPanel
      story={activeStory}
      user={{ username, role }}
      lang={lang}
      canManageOverride={isWriter}
    />
  </ModalShell>
) : null}

{voteProposalScene ? (
  <ModalShell
    title={
      String(lang || "en")
        .toLowerCase()
        .startsWith("fr")
        ? "Voter sur la proposition"
        : "Vote on Proposal"
    }
    subtitle={
      voteProposalScene?.title ||
      t.untitledProposal
    }
    onClose={() => {
      if (!proposalVoteBusy) {
        setVoteProposalScene(null);
      }
    }}
    maxWidth={720}
    closeLabel={t.close}
  >
    <div
      data-novel-story-proposal-vote="true"
      className="ws-proposal-vote-panel"
    >
      <p className="ns-card-meta">
        {String(lang || "en")
          .toLowerCase()
          .startsWith("fr")
          ? "Choisissez une seule appréciation. Votre vote est enregistré une fois pour cette proposition."
          : "Choose one assessment. Your vote is recorded once for this proposal."}
      </p>

      <div className="ws-proposal-vote-preview">
        {String(
          voteProposalScene?.text ||
            ""
        ).length > 320
          ? String(
              voteProposalScene?.text ||
                ""
            ).slice(0, 320) + "…"
          : String(
              voteProposalScene?.text ||
                ""
            )}
      </div>

      <div className="ws-proposal-vote-actions">
        <button
          type="button"
          className="ns-button ns-button-primary"
          disabled={proposalVoteBusy}
          onClick={() =>
            handleVote(
              voteProposalScene,
              1
            )
          }
        >
          👍 {t.good} (+1)
        </button>

        <button
          type="button"
          className="ns-button ns-button-outline"
          disabled={proposalVoteBusy}
          onClick={() =>
            handleVote(
              voteProposalScene,
              0
            )
          }
        >
          ◻︎ {t.neutral} (0)
        </button>

        <button
          type="button"
          className="ns-button ns-button-outline"
          disabled={proposalVoteBusy}
          onClick={() =>
            handleVote(
              voteProposalScene,
              -1
            )
          }
        >
          👎 {t.incompatible} (−1)
        </button>
      </div>
    </div>
  </ModalShell>
) : null}

{fullProposalScene ? (
  <ModalShell
    title={fullProposalScene.title || t.fullProposal}
    subtitle={`${t.by} ${
      fullProposalScene.authorName ||
      fullProposalScene.authorUsername ||
      fullProposalScene.author ||
      t.unknown
    } · ${formatDateTime(fullProposalScene.createdAt)}`}
    onClose={() => setFullProposalScene(null)}
    maxWidth={1080}
    closeLabel={t.close}
  >
    <div
      data-novel-story-proposal-review-reader="true"
      style={{
        maxHeight: "68vh",
        overflowY: "auto",
        padding: "4px 2px 12px",
      }}
    >
      <NovelStorySceneReadOnly
        scene={fullProposalScene}
        lang={lang}
      />
      <SceneIllustrationPreview
        scene={fullProposalScene}
        t={t}
      />
      {renderIllustrationReviewControls(
        fullProposalScene
      )}
    </div>

    {isWriter &&
    isPendingProposal(fullProposalScene) ? (
      <div
        data-novel-story-owner-proposal-review-actions="true"
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop:
            "1px solid rgba(148,163,184,0.22)",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="ns-button ns-button-primary"
          onClick={() => {
            const proposal =
              fullProposalScene;
            setFullProposalScene(null);
            requestApproveProposal(
              proposal
            );
          }}
        >
          {String(lang || "en")
            .toLowerCase()
            .startsWith("fr")
            ? "Sélectionner pour adoption"
            : "Select for Adoption"}
        </button>

        <button
          type="button"
          className="ns-button ns-button-outline"
          onClick={() => {
            const sceneId =
              getSceneId(
                fullProposalScene
              );
            setFullProposalScene(null);
            if (sceneId) {
              handleRejectProposal(
                sceneId
              );
            }
          }}
        >
          {String(lang || "en")
            .toLowerCase()
            .startsWith("fr")
            ? "Rejeter"
            : "Reject"}
        </button>
      </div>
    ) : null}
  </ModalShell>
) : null}
    </div>
  );
}
